import { Link, useNavigate } from '@tanstack/react-router';
import {
    AlertTriangle,
    ArrowLeft,
    ArrowUpDown,
    Bell,
    Check,
    ChevronDown,
    ExternalLink,
    FileText,
    ListChecks,
    Loader2,
    MessageSquareText,
    MoreHorizontal,
    Pause,
    Pencil,
    Phone,
    Play,
    Plus,
    Trash2,
    X,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { type KeyedMutator } from 'swr';

import { BlockedPhonePopover } from '@apps/web/components/caregivers/blocked_phone_popover';
import { CaregiverComplianceBadge } from '@apps/web/components/caregivers/caregiver_compliance_badge';
import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { getEhrCaregiverFieldVisibility } from '@apps/web/components/caregivers/ehr_caregiver_field_visibility';
import { EhrCaregiverStatusBadge } from '@apps/web/components/caregivers/ehr_caregiver_status_badge';
import { ClientDetailModal } from '@apps/web/components/clients/client_detail_modal';
import { EntityEventsTimeline } from '@apps/web/components/events/entity_events_timeline';
import { OutreachSubscribersPanel } from '@apps/web/components/outreach/outreach_subscribers_panel';
import { AddCaregiversModal } from '@apps/web/components/outreaches/add_caregivers_modal';
import { AssignAndFillConfirmationModal } from '@apps/web/components/outreaches/assign_and_fill_confirmation_modal';
import { CancelShiftButton } from '@apps/web/components/outreaches/cancel_shift_button';
import { CancelShiftDialog } from '@apps/web/components/outreaches/cancel_shift_dialog';
import { CarePlanModalButton } from '@apps/web/components/outreaches/care_plan_modal';
import { readCarePlanFromMetadata } from '@apps/web/components/outreaches/care_plan_summary_preview';
import { CaregiverOutreachDetailModal } from '@apps/web/components/outreaches/caregiver_outreach_detail_modal';
import {
    type CaregiverMatchSignal,
    CaregiverOutreachStatsBadges,
} from '@apps/web/components/outreaches/caregiver_outreach_stats_badges';
import { ClientNotificationDetailModal } from '@apps/web/components/outreaches/client_notification_detail_modal';
import { resolveClientNotificationTarget } from '@apps/web/components/outreaches/client_notification_target';
import { ContactAttemptStatusBadge } from '@apps/web/components/outreaches/contact_attempt_status_badge';
import { CreateOutreachModal } from '@apps/web/components/outreaches/create_outreach_modal';
import {
    AddShiftsDialog,
    isOutreachShiftAddBlocked,
} from '@apps/web/components/outreaches/edit_shifts_section';
import {
    GroupSelectionWarningDialog,
    useGroupSelectionWarning,
} from '@apps/web/components/outreaches/group_selection_warning';
import { InhouseRecommendationsTable } from '@apps/web/components/outreaches/inhouse_recommendations_table';
import {
    ManualContactConflictDialog,
    type ManualContactConflictOverride,
    parseManualContactConflict,
} from '@apps/web/components/outreaches/manual_contact_conflict_dialog';
import {
    compareMetricLowerIsBetter,
    getAvailabilitySortPriority,
    getMatchSignalForSort,
} from '@apps/web/components/outreaches/match_signal_sort_utils';
import { MissingCarePlanBanner } from '@apps/web/components/outreaches/missing_care_plan_banner';
import { NextAttemptCountdown } from '@apps/web/components/outreaches/next_attempt_countdown';
import { OutreachConflictAlert } from '@apps/web/components/outreaches/outreach_conflict_alert';
import {
    type OutreachDetailChrome,
    outreachDetailChrome,
    outreachDetailReferenceButtonClassName,
    outreachDetailReferenceButtonVariant,
    outreachDetailReferenceIconClassName,
    outreachDetailsHeaderClassName,
    outreachDetailsPanelClassName,
    outreachDetailsStatusRowClassName,
    outreachDetailsTableClassName,
    outreachShiftsTableClassName,
} from '@apps/web/components/outreaches/outreach_detail_chrome';
import { OutreachDetailsOverflowMenu } from '@apps/web/components/outreaches/outreach_details_overflow_menu';
import { isCoordinatorPickedOutreach } from '@apps/web/components/outreaches/outreach_drive_mode';
import {
    type OutreachScheduleConflict,
    OutreachScheduleConflictDialog,
    parseOutreachScheduleConflict,
} from '@apps/web/components/outreaches/outreach_schedule_conflict_dialog';
import {
    AutoAssignClientPolicyOverrides,
    AutoAssignDetailSection,
    AutoAssignGroupChangeDialog,
    AutoAssignShiftCell,
    AutoAssignShiftHeader,
    type ClientAutoAssignPolicyOverride,
    useOutreachAutoAssignEdits,
} from '@apps/web/components/outreaches/outreach_shifts_auto_assign';
import { useSkipListRetry } from '@apps/web/components/outreaches/outreach_skip_list_dialog';
import { OutreachStatusBadge } from '@apps/web/components/outreaches/outreach_status_badge';
import { OutreachStepsModal } from '@apps/web/components/outreaches/outreach_steps_modal';
import { OutreachTypeBadge } from '@apps/web/components/outreaches/outreach_type_badge';
import { QuietHoursBanner } from '@apps/web/components/outreaches/quiet_hours_banner';
import { ResponsePicker } from '@apps/web/components/outreaches/response_picker';
import {
    groupShiftsByClient,
    type ShiftGroup as ShiftGroupValue,
} from '@apps/web/components/outreaches/shift_group_editor';
import { ShiftNotesModalButton } from '@apps/web/components/outreaches/shift_notes_modal';
import {
    getAssignAndFillCandidate,
    getAssignAndFillCandidates,
    getContactAttemptDetailsText,
    getDisplayOutreachStatus,
    getInterestedShiftIds,
    getOutstandingHHAExchangeOvertimeReviewFromContactAttempts,
    getOutstandingWellSkyOverrideReviewFromContactAttempts,
    getOverallCaregiverResponse,
    getResolvedShiftResponsesFromContactAttempts,
    isActiveOutreach,
} from '@apps/web/components/outreaches/utils';
import { TrainingModePageTourReplayButton } from '@apps/web/components/page_tour/training_mode_page_tour';
import { PendingMessagesDialog } from '@apps/web/components/shared/messages/messages_pending_dialog';
import { EditShiftModal } from '@apps/web/components/shifts/edit_shift_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card, CardContent } from '@apps/web/components/ui/card';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    DataTable,
    DataTableColumnHeader,
} from '@apps/web/components/ui/data_table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import {
    REFERENCE_PILL_CLASS,
    ReferencePillContent,
} from '@apps/web/components/ui/reference_pill';
import { Spinner } from '@apps/web/components/ui/spinner';
import { statusIntentBadgeClassName } from '@apps/web/components/ui/status_tokens';
import { Switch } from '@apps/web/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    getAvatarSolidToneClass,
    UserAvatar,
} from '@apps/web/components/ui/user_avatar';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import { useOutreachCaregiverSelection } from '@apps/web/context/outreach_caregiver_selection';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import {
    REFERENCE_TYPE_HOVER_CLASSES,
    REFERENCE_TYPE_ICONS,
    REFERENCE_TYPE_TINT_CLASSES,
} from '@apps/web/features/phoebe_chat_v3/ui/reference_icons';
import {
    useClientContactAttemptsRealtime,
    useContactAttemptsRealtime,
    useOrganizationPendingMessagesCountRealtime,
    useOrganizationPendingMessagesRealtime,
    useOutreachRelatedInboundCallsRealtime,
} from '@apps/web/hooks/realtime_hooks';
import { useCanPlaceCalls } from '@apps/web/hooks/use_can_place_calls';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    useDisplayNamesLastFirst,
    useFormatPersonName,
} from '@apps/web/hooks/use_person_name_format';
import {
    type ChatTypingUser,
    formatChatTypingLabel,
} from '@apps/web/hooks/use_phoebe_chat_typing';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { mergeGeneratedAndTemplateAgentInstructions } from '@apps/web/lib/agent_instructions_utils';
import { isStateChangingAgentStreamEvent } from '@apps/web/lib/agent_stream_events';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    type AutoAssignCaregiverPolicy,
    getAutoAssignTooltip,
    isAutoAssignAllowed,
    resolveOrganizationAutoAssignPolicy,
} from '@apps/web/lib/auto_assign';
import { formatDistance } from '@apps/web/lib/callout_utils';
import {
    compareCaregiverDisplayName,
    comparePersonNames,
    formatCaregiverName,
} from '@apps/web/lib/caregiver_name';
import { rawPersonName } from '@apps/web/lib/caregiver_name';
import {
    buildCaregiverShiftIdsFromSelections,
    buildCaregiverShiftIdsFromUnitSelections,
    type CaregiverShiftIds,
    normalizeSelectedCaregiverIds,
} from '@apps/web/lib/caregiver_shift_selections';
import {
    getDeliveryModeDisplayName,
    isS3RecordingUrl,
} from '@apps/web/lib/contact_utils';
import {
    generateExternalCaregiverUrl,
    getDataSourceDisplayName,
} from '@apps/web/lib/data_source_utils';
import {
    formatDateAndTime,
    formatDateAndTimeWithWeekday,
    formatTime,
    getDateStringInTimezone,
} from '@apps/web/lib/date_utils';
import { EVENTS } from '@apps/web/lib/events';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import {
    getApiErrorDetail,
    isTransparentRateLimitError,
    parseKnownApiError,
} from '@apps/web/lib/known_api_errors';
import { useMode } from '@apps/web/lib/mode_store';
import { formatNameList } from '@apps/web/lib/name_lists';
import {
    buildOutreachDisplayUnits,
    groupModeLabel,
    type OutreachDisplayUnit,
} from '@apps/web/lib/outreach_shift_ordering';
import {
    createStartOutreachTimeout,
    dismissStartReport,
    getOutreachSequence,
    getOutreachStartedReport,
    publishStartReport,
    type StartReport,
    type StartReportStatus,
    STILL_STARTING_REPORT_TEXT,
    useStartReport,
} from '@apps/web/lib/outreach_started_report';
import {
    getAutoAssignShiftIds,
    getEffectiveScheduledOutreachStartAt,
    getScheduledOutreachState,
    isUrgentOutreach,
    type OutreachConflictError,
    type OutreachStep,
    parseOutreachConflictError,
    supportsOutreachAutoAssignFirstResponder,
} from '@apps/web/lib/outreach_utils';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { type ColumnDef } from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';
import {
    type BlockedCaregiverInfo,
    useBlockedCaregivers,
    useOrganization,
    useOrganizationClients,
    useOrganizationDataSourceAccounts,
    useOrganizationTemplates,
    useOutreachDismissedCaregivers,
    useUser,
    useVoiceCallRecordingUrls,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    ApiError,
    type ApiResponseType,
    type CaregiverResponseType,
    type components,
    type ContactAttempt,
    type DisplayOutreachStatus,
    type JoinedOutreachRead,
    type OrganizationDataSourceAccount,
    type OrgCaregiverRecord,
    type OutreachStatus,
    type ShiftAssignmentType,
} from '@libraries/typescript/api_client';

type OutreachDetailProps = {
    readonly outreachAction?: 'assign_and_fill';
    readonly outreach: JoinedOutreachRead;
    readonly mutateOutreach: KeyedMutator<JoinedOutreachRead>;
    readonly onOpenOutreachChat?: () => void;
    readonly isOutreachChatAvailable?: boolean;
    readonly selectedContactAttemptId?: string | null;
    readonly chatTypingUsers?: ReadonlyArray<ChatTypingUser>;
};

type OrgClientRead = components['schemas']['OrgClientRead'];
type ClientContactAttemptRead =
    components['schemas']['ContactAttemptClientRead'];
type CaregiverNamesById = Readonly<Record<string, string>>;
// caregiver_name is the fallback label for a caregiver who was never a
// recipient, so the contact-attempt lookup can't name them.
type FilledShiftInfo = {
    readonly caregiver_id: string;
    readonly filled_at: string;
    readonly caregiver_name?: string;
    readonly assigned_without_response?: boolean;
    readonly override_reason?: string | null;
};

type OutreachShift = JoinedOutreachRead['shifts'][number];
type SuggestedCaregiver = NonNullable<
    JoinedOutreachRead['suggested_caregivers']
>[number];
type RecommendationAccordionTimeRow = {
    readonly label: string;
    readonly selectedCount: number;
    readonly shiftId: string;
};
const OPEN_MOBILE_OUTREACH_DETAILS_EVENT =
    'phoebe:open-mobile-outreach-details';
const MOBILE_MANUAL_FALLBACK_TABLE_SCROLL_CLASSNAME =
    'max-md:max-h-[calc(100dvh-14rem)] max-md:overflow-y-auto max-md:overscroll-contain max-md:scroll-pb-24 max-md:pb-24 max-md:[-webkit-overflow-scrolling:touch] md:contents';
const CANCELLABLE_SHIFT_OUTREACH_STATUSES: ReadonlySet<string> = new Set([
    'pending',
    'in_progress',
    'awaiting_confirmation',
    'paused',
]);

function buildSuggestedCaregiverIdsByShift({
    shifts,
    suggestions,
}: {
    readonly shifts: JoinedOutreachRead['shifts'];
    readonly suggestions: ReadonlyArray<SuggestedCaregiver>;
}): Record<string, Array<string>> {
    const selectedByShift: Record<string, Array<string>> = {};
    for (const shift of shifts) {
        const caregiverIds: Array<string> = [];
        const seen = new Set<string>();
        for (const suggestion of suggestions) {
            if (
                !suggestion.covered_shift_ids.includes(shift.id) ||
                seen.has(suggestion.caregiver_id)
            ) {
                continue;
            }
            seen.add(suggestion.caregiver_id);
            caregiverIds.push(suggestion.caregiver_id);
        }
        if (caregiverIds.length > 0) {
            selectedByShift[shift.id] =
                normalizeSelectedCaregiverIds(caregiverIds);
        }
    }
    return selectedByShift;
}

const CLIENT_NOTIFICATION_RETRYABLE_STATUSES = new Set<
    ClientContactAttemptRead['outreach_status']
>([
    'failed',
    'no_answer',
    'unreachable',
    'voicemail_left',
    'unable_to_leave_message',
    'call_disconnected',
]);

const GROUP_BADGE_COLORS = [
    'border-blue-200 bg-blue-50 text-blue-700',
    'border-purple-200 bg-purple-50 text-purple-700',
    'border-amber-200 bg-amber-50 text-amber-700',
    'border-emerald-200 bg-emerald-50 text-emerald-700',
    'border-rose-200 bg-rose-50 text-rose-700',
    'border-cyan-200 bg-cyan-50 text-cyan-700',
] as const;

// Base classes for the small response-count chips in the summary grid; hue
// comes from the status intent via STATUS_INTENT_TOKENS.
const RESPONSE_COUNT_CHIP_CLASS =
    'h-4 min-w-4 rounded-full px-1 py-0 text-[10px] leading-none md:h-auto md:rounded-md md:px-2.5 md:py-0.5 md:text-xs';

const CAREGIVER_RESPONSE_LABELS: Record<CaregiverResponseType, string> = {
    available_interested: 'Available',
    available_not_interested: 'Not a fit',
    not_a_fit: 'Not a fit',
    unavailable: 'Unavailable',
    pending: 'Pending response',
    no_response: 'No response',
    unknown: 'Needs review',
};

function getCaregiverResponseLabel(response: CaregiverResponseType | null) {
    return response ? CAREGIVER_RESPONSE_LABELS[response] : '—';
}

// Helper function to determine if an outreach has started.
function isOutreachStarted(status: OutreachStatus): boolean {
    return status !== 'pending';
}

function buildSkippedCaregiverLabel(
    droppedIds: ReadonlyArray<string>,
    namesById: Readonly<Record<string, string>>,
): string {
    const skippedNames = droppedIds
        .map((id) => namesById[id])
        .filter((name): name is string => Boolean(name));
    if (skippedNames.length === droppedIds.length && skippedNames.length > 0) {
        return formatNameList(skippedNames);
    }
    return `${droppedIds.length} caregiver${droppedIds.length === 1 ? '' : 's'}`;
}

type ScheduleOverlapRange = {
    readonly start_time: string;
    readonly end_time: string;
};

type ScheduleConflictingShift = {
    readonly id: string;
    readonly patient_name: string;
    readonly shift_start_time: string;
    readonly shift_end_time: string;
    readonly blocked_start_time: string;
    readonly blocked_end_time: string;
};

type AssignAndFillEntryPoint =
    | 'inline_button'
    | 'overflow_menu'
    | 'caregiver_detail_modal'
    | 'slack_mark_filled_deep_link';

type ContactAttemptColumnShift = {
    readonly id: string;
    readonly patient_name: string;
    readonly shift_start_time: string;
    readonly shift_end_time: string;
};

type ContactAttemptGroup = {
    readonly caregiver: OrgCaregiverRecord;
    readonly attempts: Array<ContactAttempt>;
    readonly timelineItems: Array<ContactAttempt>;
    readonly overallResponse: CaregiverResponseType | null;
    readonly recordedResponse: CaregiverResponseType | null;
    readonly wellskyOverrideReview?:
        | ContactAttempt['wellsky_override_review']
        | null;
    readonly hhaexchangeOvertimeReview?:
        | ContactAttempt['hhaexchange_overtime_review']
        | null;
    readonly hasOverride?: boolean;
    // Multi-shift outreach support
    readonly shiftResponses?: Record<string, CaregiverResponseType> | null;
    readonly interestedShiftCount?: number;
    readonly currentShiftEndTime?: string | null;
    readonly scheduleOverlapMinutes?: number | null;
    readonly scheduleOverlapRanges?: ReadonlyArray<ScheduleOverlapRange> | null;
    readonly scheduleConflictingShifts?: ReadonlyArray<ScheduleConflictingShift> | null;
    readonly matchSignalsByShift?: Array<CaregiverMatchSignal>;
    readonly distance?: number | null;
    readonly clientDistances?: ContactAttempt['client_distances'];
};

function ContactAttemptDistances({
    clientDistances,
    distance,
}: {
    readonly clientDistances?: ContactAttempt['client_distances'];
    readonly distance?: number | null;
}) {
    if (!clientDistances?.length) {
        return <span>{formatDistance(distance)}</span>;
    }

    return (
        <div className="flex flex-col gap-1">
            {clientDistances.map((clientDistance) => (
                <span key={clientDistance.client_id}>
                    {clientDistance.client_name}:{' '}
                    {formatDistance(clientDistance.distance)}
                </span>
            ))}
        </div>
    );
}

type RelatedInboundOutreachCall = ApiResponseType<
    '/organizations/{organization_id}/callouts/{callout_id}/related-inbound-calls',
    'get'
>[number];

const RELATED_INBOUND_CALL_SOURCE = 'related_inbound_call';
const OUTREACH_REFRESH_THROTTLE_DELAY_MS = 3000;

const VOICE_OUTREACH_STATUSES_EXPECTING_SUMMARY = new Set<
    ContactAttempt['outreach_status']
>(['completed', 'live_answer']);

export function isRelatedInboundTimelineItem(attempt: ContactAttempt): boolean {
    const details = attempt.communication_details;
    if (!details || typeof details !== 'object') {
        return false;
    }

    return details['source'] === RELATED_INBOUND_CALL_SOURCE;
}

function eventReferencesOutreach(
    event: {
        readonly resource: { readonly type: string; readonly id: string };
        readonly relationships: ReadonlyArray<{
            readonly type: string;
            readonly id: string;
        }>;
    },
    outreachId: string,
): boolean {
    if (event.resource.type === 'callout' && event.resource.id === outreachId) {
        return true;
    }

    return event.relationships.some(
        (relationship) =>
            relationship.type === 'callout' && relationship.id === outreachId,
    );
}

export function getContactAttemptDeliveryModeLabel(
    attempt: ContactAttempt,
): string {
    if (isRelatedInboundTimelineItem(attempt)) {
        return 'Inbound Call';
    }

    return getDeliveryModeDisplayName(attempt.delivery_mode);
}

function buildRelatedInboundTimelineItem({
    relatedInboundCall,
    caregiver,
    outreachId,
}: {
    readonly relatedInboundCall: RelatedInboundOutreachCall;
    readonly caregiver: OrgCaregiverRecord;
    readonly outreachId: string;
}): ContactAttempt {
    return {
        id: relatedInboundCall.voice_call_id,
        callout_id: outreachId,
        caregiver_id: relatedInboundCall.caregiver_id,
        org_caregiver_record: caregiver,
        delivery_mode: relatedInboundCall.delivery_mode,
        round: 0,
        outreach_status: relatedInboundCall.outreach_status,
        response_timestamp: relatedInboundCall.response_timestamp ?? null,
        transcript_text: relatedInboundCall.transcript_text ?? null,
        call_summary: relatedInboundCall.call_summary ?? null,
        sms_message_text: null,
        conversation_id: null,
        communication_details: {
            source: RELATED_INBOUND_CALL_SOURCE,
            related_contact_attempt_ids:
                relatedInboundCall.related_contact_attempt_ids,
            voice_call_id: relatedInboundCall.voice_call_id,
        },
        created_at: relatedInboundCall.created_at,
        updated_at: relatedInboundCall.updated_at,
        voice_call: relatedInboundCall.voice_call,
        shift_responses: relatedInboundCall.shift_responses,
        shift_response_recorded_at: {},
        current_shift_end: null,
        held_by_quiet_hours: false,
        schedule_overlap_minutes: 0,
        schedule_overlap_ranges: [],
        schedule_conflicting_shifts: [],
        distance: null,
        client_distances: [],
        match_signals: null,
    };
}

const shouldShowSummaryPending = (attempt: ContactAttempt): boolean => {
    if (isRelatedInboundTimelineItem(attempt)) {
        return false;
    }

    if (attempt.delivery_mode !== 'voice' || attempt.call_summary) {
        return false;
    }

    if (
        !VOICE_OUTREACH_STATUSES_EXPECTING_SUMMARY.has(attempt.outreach_status)
    ) {
        return false;
    }

    const callStatus = attempt.voice_call?.call_status;

    if (!callStatus) {
        return true;
    }

    return callStatus === 'ended';
};

const getAttemptTimestampMs = (
    attempt: ClientContactAttemptRead,
): number | null => {
    const parsedTimestamp = Date.parse(attempt.created_at);
    if (Number.isNaN(parsedTimestamp)) {
        return null;
    }

    return parsedTimestamp;
};

function isNewerClientAttempt(
    candidate: ClientContactAttemptRead,
    current: ClientContactAttemptRead,
): boolean {
    if (candidate.round !== current.round) {
        return candidate.round > current.round;
    }

    const candidateTimestampMs = getAttemptTimestampMs(candidate);
    const currentTimestampMs = getAttemptTimestampMs(current);
    if (
        candidateTimestampMs !== null &&
        currentTimestampMs !== null &&
        candidateTimestampMs !== currentTimestampMs
    ) {
        return candidateTimestampMs > currentTimestampMs;
    }

    return candidate.id > current.id;
}

function getTotalShiftDurationMinutes(
    shifts: ReadonlyArray<ContactAttemptColumnShift> | undefined,
): number | null {
    if (!shifts || shifts.length === 0) {
        return null;
    }

    const totalDurationMs = shifts.reduce((durationMs, shift) => {
        const shiftDurationMs =
            new Date(shift.shift_end_time).getTime() -
            new Date(shift.shift_start_time).getTime();
        return shiftDurationMs > 0 ? durationMs + shiftDurationMs : durationMs;
    }, 0);
    if (totalDurationMs <= 0) {
        return null;
    }

    return Math.floor(totalDurationMs / 60000);
}

function ContactAttemptScheduleConflictBadge({
    scheduleOverlapMinutes,
    conflictingShifts,
    shifts,
    timezone,
}: {
    readonly scheduleOverlapMinutes?: number | null;
    readonly conflictingShifts?: ReadonlyArray<ScheduleConflictingShift> | null;
    readonly shifts?: ReadonlyArray<ContactAttemptColumnShift>;
    readonly timezone: string;
}) {
    const { formatName } = useFormatPersonName();
    const overlapMinutes = scheduleOverlapMinutes ?? 0;
    const conflicting = conflictingShifts ?? [];
    if (overlapMinutes <= 0 && conflicting.length === 0) {
        return null;
    }

    const totalShiftDurationMinutes = getTotalShiftDurationMinutes(shifts);
    const isPartial =
        totalShiftDurationMinutes !== null &&
        totalShiftDurationMinutes > overlapMinutes;

    const sorted = [...conflicting].sort((a, b) => {
        const cmp =
            new Date(a.shift_start_time).getTime() -
            new Date(b.shift_start_time).getTime();
        return cmp !== 0 ? cmp : a.id.localeCompare(b.id);
    });

    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <AlertTriangle
                    className={cn(
                        'h-4 w-4 shrink-0',
                        isPartial ? 'text-amber-600' : 'text-red-600',
                    )}
                />
            </TooltipTrigger>
            <TooltipContent
                side="top"
                align="center"
                hideArrow={true}
                className="border-none bg-transparent p-0 shadow-none"
            >
                <Card className="bg-background gap-2 rounded-md py-3 shadow-md">
                    <CardContent className="flex flex-col gap-2 px-3 py-0">
                        <span className="text-xs font-medium">
                            Assigned to conflicting shift:
                        </span>
                        {sorted.map((s) => (
                            <Link
                                key={s.id}
                                to="/shifts"
                                search={{
                                    date: getDateStringInTimezone(
                                        new Date(s.shift_start_time),
                                        timezone,
                                    ),
                                    shiftId: s.id,
                                }}
                                className="hover:bg-muted bg-background inline-flex rounded-md border px-2 py-1 text-xs underline-offset-2 hover:underline"
                            >
                                {formatName(s.patient_name)} ·{' '}
                                {formatTime(s.shift_start_time, timezone)}-
                                {formatTime(s.shift_end_time, timezone)}
                            </Link>
                        ))}
                    </CardContent>
                </Card>
            </TooltipContent>
        </Tooltip>
    );
}

// The agent-active guard (start-outreach / confirm-suggestions) returns a 409
// with a plain string `detail`. parseOutreachConflictError treats any string
// detail as a shift conflict (shifts: []) and would surface the destructive
// conflict banner, so detect this shape explicitly and toast it instead.
function isAgentActive409(error: unknown): boolean {
    return (
        error instanceof ApiError &&
        error.status === 409 &&
        typeof (error.body as { detail?: unknown } | undefined)?.detail ===
            'string'
    );
}

const createContactAttemptsColumns = (
    timezone: string,
    onCallAgain: (caregiverId: string) => void,
    isCallingCaregiver: string | null,
    onAssignAndFill: (
        caregiver: OrgCaregiverRecord,
        interestedShiftIds: Array<string>,
        wellskyOverrideReview: ContactAttempt['wellsky_override_review'] | null,
        hhaexchangeOvertimeReview:
            | ContactAttempt['hhaexchange_overtime_review']
            | null,
        scheduleOverlapMinutes?: number | null,
        scheduleOverlapRanges?: ReadonlyArray<ScheduleOverlapRange> | null,
        scheduleConflictingShifts?: ReadonlyArray<ScheduleConflictingShift> | null,
        entryPoint?: AssignAndFillEntryPoint,
    ) => void,
    hasPendingMessage?: (caregiverId: string) => boolean,
    formatCaregiverNameFn: (name: string) => string = (name) => name,
    options?: {
        isDevelopment: boolean;
        onDeleteContactAttempt: (contactAttemptId: string) => void;
        deletingContactAttemptId: string | null;
        outreachSteps?: ReadonlyArray<OutreachStep>;
        pausedTimeSeconds?: number;
        isPaused?: boolean;
        blockedCaregiverMap?: Map<string, Array<BlockedCaregiverInfo>>;
        onCaregiverUnblocked?: () => void;
        onCaregiverClick?: (caregiverId: string) => void;
        recordingUrls?: Record<string, string>;
        isMultiShiftOutreach?: boolean;
        isMultiClientOutreach?: boolean;
        sortByLastName?: boolean;
        shifts?: Array<ContactAttemptColumnShift>;
        timezone?: string;
        onShiftResponseChange?: (args: {
            caregiverId: string;
            contactAttemptId: string;
            shiftId: string;
            response: CaregiverResponseType | null;
        }) => void;
        skipBusyCaregivers?: boolean;
        quietHoursResumesAt?: string | null;
        filledShiftIds?: Set<string>;
        cancelledShiftIds?: Set<string>;
        dataSourceAccounts?: Array<OrganizationDataSourceAccount>;
        matchSignalSortKey?: string | null;
        onMatchSignalSortKeyChange?: (signalKey: string) => void;
        matchSignalSortOptions?: Array<{ key: string; label: string }>;
        isAgentAddingPeople?: boolean;
        showEhrCaregiverStatus?: boolean;
        showHHAExchangeCompliance?: boolean;
    },
): Array<ColumnDef<ContactAttemptGroup>> => {
    const isDevelopment = options?.isDevelopment ?? false;
    const onDeleteContactAttempt = options?.onDeleteContactAttempt;
    const deletingContactAttemptId = options?.deletingContactAttemptId ?? null;
    const outreachSteps = options?.outreachSteps;
    const pausedTimeSeconds = options?.pausedTimeSeconds ?? 0;
    const isPaused = options?.isPaused ?? false;
    const blockedCaregiverMap = options?.blockedCaregiverMap;
    const onCaregiverUnblocked = options?.onCaregiverUnblocked;
    const recordingUrls = options?.recordingUrls;
    const isMultiShiftOutreach = options?.isMultiShiftOutreach ?? false;
    const shifts = options?.shifts;
    // When the caller scopes `shifts` to a selected shift, hide the other
    // shifts' match-signal badges too.
    const scopedShiftIds = shifts
        ? new Set(shifts.map((shift) => shift.id))
        : null;
    const columnTimezone = options?.timezone;
    const onShiftResponseChange = options?.onShiftResponseChange;
    const skipBusyCaregivers = options?.skipBusyCaregivers ?? false;
    const quietHoursResumesAt = options?.quietHoursResumesAt ?? null;
    const filledShiftIds = options?.filledShiftIds;
    const cancelledShiftIds = options?.cancelledShiftIds;
    const resolvedShiftIds = new Set<string>([
        ...(filledShiftIds ?? []),
        ...(cancelledShiftIds ?? []),
    ]);
    const dataSourceAccounts = options?.dataSourceAccounts;
    const matchSignalSortKey = options?.matchSignalSortKey ?? null;
    const onMatchSignalSortKeyChange = options?.onMatchSignalSortKeyChange;
    const matchSignalSortOptions = options?.matchSignalSortOptions;
    const fallbackMatchSignalSortKey = matchSignalSortOptions?.[0]?.key ?? null;
    const conflictTimezone = columnTimezone ?? timezone;
    const isAgentAddingPeople = options?.isAgentAddingPeople ?? false;
    const showEhrCaregiverStatus = options?.showEhrCaregiverStatus ?? false;
    const showHHAExchangeCompliance =
        options?.showHHAExchangeCompliance ?? false;

    return [
        {
            id: 'assignAndFill',
            header: '',
            meta: { cellClassName: 'p-0' },
            cell: ({ row }) => {
                const assignAndFillCandidate = getAssignAndFillCandidate(
                    row.original,
                    resolvedShiftIds,
                );
                if (!assignAndFillCandidate) {
                    return null;
                }
                return (
                    <Button
                        size="sm"
                        className="bg-[rgb(49,124,255)] text-white hover:bg-[rgb(29,104,235)]"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAssignAndFill(
                                row.original.caregiver,
                                assignAndFillCandidate.interestedShiftIds,
                                row.original.wellskyOverrideReview ?? null,
                                row.original.hhaexchangeOvertimeReview ?? null,
                                row.original.scheduleOverlapMinutes,
                                row.original.scheduleOverlapRanges,
                                row.original.scheduleConflictingShifts,
                                'inline_button',
                            );
                        }}
                    >
                        {row.original.wellskyOverrideReview ||
                        row.original.hhaexchangeOvertimeReview
                            ? 'Review override'
                            : 'Assign & Fill'}
                    </Button>
                );
            },
        },
        {
            id: 'pendingMessage',
            header: '',
            cell: ({ row }) => {
                const hasPending = hasPendingMessage?.(
                    row.original.caregiver.id,
                );
                if (!hasPending) {
                    return null;
                }

                return (
                    <div className="flex items-center justify-center">
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <div className="relative cursor-pointer">
                                    <MessageSquareText className="h-4 w-4 text-amber-600" />
                                    <div className="absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full bg-amber-600" />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>New message awaiting approval</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                );
            },
            size: 40,
        },
        {
            id: 'externalLink',
            header: '',
            cell: ({ row }) => {
                const sourceSystem = row.original.caregiver.source_system;
                const externalId =
                    row.original.caregiver.external_url_id ??
                    row.original.caregiver.external_id;
                if (!sourceSystem || !externalId) {
                    return null;
                }

                const dataSourceAccount = dataSourceAccounts?.find(
                    (account) => account.type === sourceSystem,
                );
                if (!dataSourceAccount) {
                    return null;
                }

                const externalUrl = generateExternalCaregiverUrl(
                    sourceSystem,
                    externalId,
                    dataSourceAccount.base_url,
                );
                if (!externalUrl) {
                    return null;
                }

                const systemName = getDataSourceDisplayName(sourceSystem);

                return (
                    <Button
                        variant="ghost"
                        size="icon"
                        asChild={true}
                        className="h-8 w-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <a
                            href={externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`View in ${systemName}`}
                        >
                            <ExternalLink className="h-4 w-4" />
                            <span className="sr-only">
                                View in {systemName}
                            </span>
                        </a>
                    </Button>
                );
            },
            size: 40,
        },
        {
            accessorKey: 'caregiver.name',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Caregiver" />
            ),
            sortFn: (rowA, rowB, columnId) => {
                const nameA = rowA.getValue<string>(columnId) ?? '';
                const nameB = rowB.getValue<string>(columnId) ?? '';
                return compareCaregiverDisplayName(
                    nameA,
                    nameB,
                    formatCaregiverNameFn,
                    options?.sortByLastName ?? false,
                );
            },
            enableSorting: true,
            cell: ({ row }) => {
                const blockedInfos = blockedCaregiverMap?.get(
                    row.original.caregiver.id,
                );
                const currentShiftEndTime = row.original.currentShiftEndTime;
                return (
                    <div>
                        <div className="flex items-center gap-2 font-medium">
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    options?.onCaregiverClick?.(
                                        row.original.caregiver.id,
                                    );
                                }}
                                className="cursor-pointer text-left font-medium underline"
                            >
                                {formatCaregiverNameFn(
                                    row.original.caregiver.name,
                                )}
                            </button>
                            {blockedInfos &&
                                blockedInfos.length > 0 &&
                                onCaregiverUnblocked && (
                                    <BlockedPhonePopover
                                        blockedInfos={blockedInfos}
                                        onUnblocked={onCaregiverUnblocked}
                                    />
                                )}
                            {skipBusyCaregivers && currentShiftEndTime && (
                                <Tooltip>
                                    <TooltipTrigger asChild={true}>
                                        <Badge
                                            variant="secondary"
                                            className="cursor-help text-xs"
                                        >
                                            On Shift
                                        </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Shift ends at{' '}
                                        {formatTime(
                                            currentShiftEndTime,
                                            columnTimezone ?? '',
                                        )}
                                        . Call will be sent 10 minutes after if
                                        they’re not on another shift.
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                        <div className="text-muted-foreground text-sm">
                            {row.original.attempts.length} attempt
                            {row.original.attempts.length === 1 ? '' : 's'}
                        </div>
                        {row.original.wellskyOverrideReview && (
                            <Badge className="mt-2 border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50">
                                Override approval required
                            </Badge>
                        )}
                    </div>
                );
            },
        },
        ...(showEhrCaregiverStatus
            ? [
                  {
                      accessorKey: 'caregiver.ehr_caregiver_status',
                      header: ({ column }) => (
                          <DataTableColumnHeader
                              column={column}
                              title="Caregiver status"
                          />
                      ),
                      cell: ({ row }) =>
                          row.original.caregiver.ehr_caregiver_status ? (
                              <EhrCaregiverStatusBadge
                                  status={
                                      row.original.caregiver
                                          .ehr_caregiver_status
                                  }
                              />
                          ) : (
                              <span className="text-muted-foreground">—</span>
                          ),
                  } satisfies ColumnDef<ContactAttemptGroup>,
              ]
            : []),
        ...(showHHAExchangeCompliance
            ? [
                  {
                      id: 'caregiver.compliance',
                      accessorFn: (row) =>
                          row.caregiver.caregiver_compliance?.state ?? '',
                      header: ({ column }) => (
                          <DataTableColumnHeader
                              column={column}
                              title="Compliance"
                          />
                      ),
                      enableSorting: true,
                      cell: ({ row }) =>
                          row.original.caregiver.caregiver_compliance ? (
                              <CaregiverComplianceBadge
                                  compliance={
                                      row.original.caregiver
                                          .caregiver_compliance
                                  }
                              />
                          ) : (
                              <span className="text-muted-foreground">—</span>
                          ),
                  } satisfies ColumnDef<ContactAttemptGroup>,
              ]
            : []),
        {
            accessorKey: 'caregiver.city',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="City" />
            ),
            enableSorting: true,
            cell: ({ row }) => (
                <span className="text-sm">
                    {row.original.caregiver.city || '—'}
                </span>
            ),
        },
        {
            id: 'overallResponse',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Availability" />
            ),
            accessorFn: (row) => row.overallResponse,
            cell: ({ row }) => {
                const {
                    caregiver,
                    overallResponse,
                    recordedResponse,
                    shiftResponses,
                    scheduleOverlapMinutes,
                    scheduleConflictingShifts,
                } = row.original;
                const latestAttemptId = row.original.attempts[0]?.id;

                if (isMultiShiftOutreach && shifts) {
                    const contactedShifts = shifts.filter(
                        (shift) => shiftResponses?.[shift.id] !== undefined,
                    );

                    return (
                        <div className="flex items-center gap-2">
                            <div className="flex flex-wrap gap-1">
                                {contactedShifts.map((shift) => {
                                    const shiftId = shift.id;
                                    const response = shiftResponses?.[shiftId];

                                    return (
                                        <ResponsePicker
                                            key={shiftId}
                                            value={
                                                (response as CaregiverResponseType | null) ??
                                                null
                                            }
                                            defaultDisplayValue="pending"
                                            ariaLabel={
                                                shift.patient_name
                                                    ? `Change response for ${formatCaregiverNameFn(shift.patient_name ?? '')}`
                                                    : 'Change shift response'
                                            }
                                            onSelect={(newResponse) => {
                                                if (latestAttemptId) {
                                                    onShiftResponseChange?.({
                                                        caregiverId:
                                                            caregiver.id,
                                                        contactAttemptId:
                                                            latestAttemptId,
                                                        shiftId,
                                                        response: newResponse,
                                                    });
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </div>
                            <ContactAttemptScheduleConflictBadge
                                scheduleOverlapMinutes={scheduleOverlapMinutes}
                                conflictingShifts={scheduleConflictingShifts}
                                shifts={shifts}
                                timezone={conflictTimezone}
                            />
                        </div>
                    );
                }

                // Single-shift outreach: show overall status picker
                const singleShiftId = shifts?.[0]?.id;
                return (
                    <div className="flex items-center gap-2">
                        <ResponsePicker
                            value={overallResponse ?? null}
                            recordedValue={recordedResponse ?? null}
                            ariaLabel={
                                formatCaregiverNameFn(caregiver.name)
                                    ? `Change availability for ${formatCaregiverNameFn(caregiver.name)}`
                                    : 'Change availability'
                            }
                            onSelect={(next) => {
                                if (latestAttemptId && singleShiftId) {
                                    onShiftResponseChange?.({
                                        caregiverId: caregiver.id,
                                        contactAttemptId: latestAttemptId,
                                        shiftId: singleShiftId,
                                        response: next,
                                    });
                                }
                            }}
                            onReset={() => {
                                if (latestAttemptId && singleShiftId) {
                                    onShiftResponseChange?.({
                                        caregiverId: caregiver.id,
                                        contactAttemptId: latestAttemptId,
                                        shiftId: singleShiftId,
                                        response: recordedResponse ?? null,
                                    });
                                }
                            }}
                            onClear={() => {
                                if (latestAttemptId && singleShiftId) {
                                    onShiftResponseChange?.({
                                        caregiverId: caregiver.id,
                                        contactAttemptId: latestAttemptId,
                                        shiftId: singleShiftId,
                                        response: null,
                                    });
                                }
                            }}
                        />
                        <ContactAttemptScheduleConflictBadge
                            scheduleOverlapMinutes={scheduleOverlapMinutes}
                            conflictingShifts={scheduleConflictingShifts}
                            shifts={shifts}
                            timezone={conflictTimezone}
                        />
                    </div>
                );
            },
            sortFn: (rowA, rowB) => {
                const responseOrder: Record<
                    CaregiverResponseType | 'null',
                    number
                > = {
                    available_interested: 0,
                    available_not_interested: 1,
                    not_a_fit: 1,
                    unknown: 2,
                    pending: 3,
                    no_response: 4,
                    unavailable: 5,
                    null: 6,
                };

                const orderA =
                    responseOrder[rowA.original.overallResponse ?? 'null'];
                const orderB =
                    responseOrder[rowB.original.overallResponse ?? 'null'];

                return orderA - orderB;
            },
        },
        {
            id: 'distance',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Distance" />
            ),
            accessorFn: (row) => row.distance ?? Number.MAX_VALUE,
            enableSorting: !options?.isMultiClientOutreach,
            cell: ({ row }) => (
                <ContactAttemptDistances
                    clientDistances={row.original.clientDistances}
                    distance={row.original.distance}
                />
            ),
        },
        {
            id: 'matchSignals',
            accessorFn: (row) =>
                row.matchSignalsByShift?.[0]?.sameDayShiftCount ?? null,
            header: ({ column }) =>
                matchSignalSortOptions && matchSignalSortOptions.length > 0 ? (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild={true}>
                            <Button
                                variant="ghost"
                                className="flex h-10 w-full items-center justify-start px-0 text-left focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                            >
                                Match Signals
                                <span className="ml-2 flex items-center">
                                    <ArrowUpDown className="h-4 w-4" />
                                </span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            {matchSignalSortOptions.map((option) => {
                                const isSelected =
                                    option.key === matchSignalSortKey;

                                return (
                                    <DropdownMenuItem
                                        key={option.key}
                                        onSelect={(event) => {
                                            event.preventDefault();
                                            if (isSelected) {
                                                return;
                                            }
                                            onMatchSignalSortKeyChange?.(
                                                option.key,
                                            );
                                            column.toggleSorting(false);
                                        }}
                                        className={cn(
                                            'max-w-[340px] gap-2',
                                            isSelected &&
                                                'bg-muted font-medium',
                                        )}
                                    >
                                        <Check
                                            className={cn(
                                                'h-4 w-4',
                                                isSelected
                                                    ? 'opacity-100'
                                                    : 'opacity-0',
                                            )}
                                        />
                                        <span className="truncate">
                                            {option.label}
                                        </span>
                                    </DropdownMenuItem>
                                );
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                ) : (
                    <DataTableColumnHeader
                        column={column}
                        title="Match Signals"
                    />
                ),
            cell: ({ row }) => (
                <CaregiverOutreachStatsBadges
                    selectedSignal={getMatchSignalForSort(
                        row.original,
                        matchSignalSortKey ?? fallbackMatchSignalSortKey,
                    )}
                    signalsByShift={
                        scopedShiftIds
                            ? row.original.matchSignalsByShift?.filter(
                                  (signal) =>
                                      scopedShiftIds.has(signal.shiftId),
                              )
                            : row.original.matchSignalsByShift
                    }
                    forceExpandedShiftId={matchSignalSortKey}
                    alwaysShowShiftLabel={(shifts?.length ?? 0) > 1}
                />
            ),
            sortFn: (rowA, rowB) => {
                if (
                    isMultiShiftOutreach &&
                    (!matchSignalSortKey || matchSignalSortKey.length === 0)
                ) {
                    return 0;
                }

                const selectedShiftId =
                    matchSignalSortKey ?? fallbackMatchSignalSortKey;
                const selectedShiftResponseA = selectedShiftId
                    ? rowA.original.shiftResponses?.[selectedShiftId]
                    : null;
                const selectedShiftResponseB = selectedShiftId
                    ? rowB.original.shiftResponses?.[selectedShiftId]
                    : null;
                const availabilityA = getAvailabilitySortPriority(
                    (selectedShiftResponseA as CaregiverResponseType | null) ??
                        rowA.original.overallResponse,
                );
                const availabilityB = getAvailabilitySortPriority(
                    (selectedShiftResponseB as CaregiverResponseType | null) ??
                        rowB.original.overallResponse,
                );
                if (availabilityA !== availabilityB) {
                    return availabilityA - availabilityB;
                }

                const signalA = getMatchSignalForSort(
                    rowA.original,
                    selectedShiftId,
                );
                const signalB = getMatchSignalForSort(
                    rowB.original,
                    selectedShiftId,
                );

                const sameDaySort = compareMetricLowerIsBetter(
                    signalA.sameDayShiftCount,
                    signalB.sameDayShiftCount,
                );
                if (sameDaySort !== 0) {
                    return sameDaySort;
                }

                const declineSort = compareMetricLowerIsBetter(
                    signalA.declineCount,
                    signalB.declineCount,
                );
                if (declineSort !== 0) {
                    return declineSort;
                }

                const overtimeSort = compareMetricLowerIsBetter(
                    signalA.weeklyHours,
                    signalB.weeklyHours,
                );
                if (overtimeSort !== 0) {
                    return overtimeSort;
                }

                const caregiverNameSort = compareCaregiverDisplayName(
                    rowA.original.caregiver.name,
                    rowB.original.caregiver.name,
                    formatCaregiverNameFn,
                    options?.sortByLastName ?? false,
                );
                if (caregiverNameSort !== 0) {
                    return caregiverNameSort;
                }

                return rowA.original.caregiver.id.localeCompare(
                    rowB.original.caregiver.id,
                );
            },
            enableSorting: true,
        },
        {
            id: 'contactAttempts',
            header: 'Contact Attempts',
            cell: ({ row }) => (
                <Table className="table-fixed">
                    <TableHeader>
                        <TableRow className="border-0">
                            <TableHead className="w-28 px-2">
                                Delivery Mode
                            </TableHead>
                            <TableHead className="w-32 px-2">
                                Outreach Status
                            </TableHead>
                            <TableHead className="w-40 px-2">Created</TableHead>
                            {outreachSteps && (
                                <TableHead className="w-32 px-2">
                                    Next Attempt
                                </TableHead>
                            )}
                            <TableHead className="w-48 px-2">
                                Summary / Transcript
                            </TableHead>
                            <TableHead className="w-48 px-2">
                                Recording
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {row.original.timelineItems.map((attempt) => (
                            <TableRow key={attempt.id}>
                                <TableCell className="w-28 px-2">
                                    <Badge
                                        variant="outline"
                                        className="text-xs"
                                    >
                                        {getContactAttemptDeliveryModeLabel(
                                            attempt,
                                        )}
                                    </Badge>
                                </TableCell>
                                <TableCell className="w-32 px-2">
                                    <ContactAttemptStatusBadge
                                        className="whitespace-normal"
                                        status={attempt.outreach_status}
                                        deliveryMode={attempt.delivery_mode}
                                        isInboundCall={isRelatedInboundTimelineItem(
                                            attempt,
                                        )}
                                        communicationDetails={
                                            attempt.communication_details
                                        }
                                        currentShiftEndTime={
                                            attempt.current_shift_end
                                        }
                                        showBusyQueuedState={skipBusyCaregivers}
                                        voiceCallStartedAt={
                                            attempt.voice_call?.started_at
                                        }
                                        timezone={columnTimezone}
                                        heldByQuietHours={
                                            attempt.held_by_quiet_hours
                                        }
                                        quietHoursResumesAt={
                                            quietHoursResumesAt
                                        }
                                    />
                                </TableCell>
                                <TableCell className="text-muted-foreground w-40 px-2 text-xs">
                                    {formatDateAndTime(
                                        attempt.created_at,
                                        timezone,
                                    )}
                                </TableCell>
                                {outreachSteps && (
                                    <TableCell className="w-32 px-2">
                                        <NextAttemptCountdown
                                            attempt={attempt}
                                            outreachSteps={outreachSteps}
                                            pausedTimeSeconds={
                                                pausedTimeSeconds
                                            }
                                            isPaused={isPaused}
                                            currentShiftEndTime={
                                                row.original.currentShiftEndTime
                                            }
                                            skipBusyCaregivers={
                                                skipBusyCaregivers
                                            }
                                            timezone={columnTimezone}
                                        />
                                    </TableCell>
                                )}
                                <TableCell className="w-48 overflow-hidden px-2">
                                    {shouldShowSummaryPending(attempt) ? (
                                        <div className="text-muted-foreground flex items-center gap-2 text-xs">
                                            <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
                                            <span className="truncate">
                                                Generating summary…
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="text-muted-foreground block truncate text-xs">
                                            {getContactAttemptDetailsText(
                                                attempt,
                                            )}
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="w-48 overflow-hidden px-2">
                                    {attempt.voice_call ? (
                                        <VoiceCallAudioPlayer
                                            voiceCall={attempt.voice_call}
                                            prefetchedRecordingUrl={
                                                recordingUrls?.[
                                                    attempt.voice_call.id
                                                ]
                                            }
                                        />
                                    ) : (
                                        <span className="text-muted-foreground text-xs">
                                            —
                                        </span>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ),
        },
        {
            id: 'actions',
            header: '',
            cell: ({ row }) => {
                const hasPhoneAttempt = row.original.attempts.some(
                    (attempt) => attempt.delivery_mode === 'voice',
                );

                const voiceAttempts = row.original.attempts.filter(
                    (attempt) => attempt.delivery_mode === 'voice',
                );

                const deletableAttempts = row.original.attempts.filter(
                    (attempt) =>
                        attempt.delivery_mode === 'voice' ||
                        attempt.delivery_mode === 'sms',
                );

                const latestDeletableAttempt = deletableAttempts[0];

                const hasPendingPhoneCall = voiceAttempts.some(
                    (attempt) =>
                        attempt.outreach_status === 'queued' ||
                        attempt.outreach_status === 'initiated' ||
                        attempt.outreach_status === 'sent',
                );

                const showDeleteAttempt = Boolean(
                    isDevelopment &&
                    latestDeletableAttempt &&
                    onDeleteContactAttempt,
                );
                const isDeletingAttempt = Boolean(
                    latestDeletableAttempt &&
                    deletingContactAttemptId === latestDeletableAttempt.id,
                );

                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild={true}>
                            <Button
                                variant="ghost"
                                className="h-8 w-8 cursor-pointer p-0"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAssignAndFill(
                                        row.original.caregiver,
                                        getInterestedShiftIds(
                                            row.original.shiftResponses,
                                            resolvedShiftIds,
                                        ),
                                        row.original.wellskyOverrideReview ??
                                            null,
                                        row.original
                                            .hhaexchangeOvertimeReview ?? null,
                                        row.original.scheduleOverlapMinutes,
                                        row.original.scheduleOverlapRanges,
                                        row.original.scheduleConflictingShifts,
                                        'overflow_menu',
                                    );
                                }}
                            >
                                {row.original.wellskyOverrideReview ||
                                row.original.hhaexchangeOvertimeReview
                                    ? 'Review override'
                                    : 'Assign & Fill'}
                            </DropdownMenuItem>
                            {(showDeleteAttempt || hasPhoneAttempt) && (
                                <DropdownMenuSeparator />
                            )}
                            {showDeleteAttempt && latestDeletableAttempt && (
                                <React.Fragment>
                                    <DropdownMenuItem
                                        className="text-red-600"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteContactAttempt?.(
                                                latestDeletableAttempt.id,
                                            );
                                        }}
                                        disabled={isDeletingAttempt}
                                    >
                                        {isDeletingAttempt
                                            ? 'Deleting...'
                                            : latestDeletableAttempt.delivery_mode ===
                                                'voice'
                                              ? 'Delete Voice Attempt (Dev)'
                                              : 'Delete SMS Attempt (Dev)'}
                                    </DropdownMenuItem>
                                    {hasPhoneAttempt && (
                                        <DropdownMenuSeparator />
                                    )}
                                </React.Fragment>
                            )}
                            {hasPhoneAttempt && (
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCallAgain(row.original.caregiver.id);
                                    }}
                                    disabled={
                                        isCallingCaregiver ===
                                            row.original.caregiver.id ||
                                        hasPendingPhoneCall ||
                                        isAgentAddingPeople
                                    }
                                >
                                    {isCallingCaregiver ===
                                    row.original.caregiver.id
                                        ? 'Calling...'
                                        : hasPendingPhoneCall
                                          ? 'Call Pending'
                                          : isAgentAddingPeople
                                            ? 'Phoebe is adding people…'
                                            : 'Call Again'}
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
        },
    ];
};

type OutreachDetailsProps = {
    readonly outreach: JoinedOutreachRead;
    readonly onEditOutreachClick?: () => void;
    readonly timezone: string;
    readonly actionButtons?: React.ReactNode;
    readonly mobileShiftsSection?: React.ReactNode;
    readonly displayStatus?: DisplayOutreachStatus;
    readonly organizationId: string | null;
    readonly autoAssignPolicy: AutoAssignCaregiverPolicy | undefined;
    readonly mutateOutreach: KeyedMutator<JoinedOutreachRead>;
    readonly clientNotificationContent?: React.ReactNode;
    readonly onClientNotificationClick?: () => void;
    readonly chrome: OutreachDetailChrome;
    readonly showAutoAssignFirstResponderToggle: boolean;
    readonly onAutoAssignSave: (
        autoAssignShifts: ReadonlyArray<string>,
    ) => void;
    readonly isUpdatingAutoAssign: boolean;
    readonly autoAssignClientPolicyOverrides: ReadonlyArray<ClientAutoAssignPolicyOverride>;
    readonly showAddShiftsDialog: boolean;
    readonly onAddShiftsDialogOpenChange: (open: boolean) => void;
};

type MobileDetailCardProps = {
    readonly label: React.ReactNode;
    readonly children: React.ReactNode;
    readonly action?: React.ReactNode;
    readonly compact?: boolean;
    readonly onClick?: () => void;
    readonly testId?: string;
};

function OutreachCreatedByChip({
    outreach,
}: {
    readonly outreach: JoinedOutreachRead;
}) {
    const displayName = outreach.auto_drafted
        ? 'Phoebe'
        : (outreach.created_by_user_name ?? 'Unknown');
    const colorKey = outreach.auto_drafted
        ? 'Phoebe'
        : (outreach.created_by_user_id ?? displayName);
    const title =
        outreach.auto_drafted && outreach.started_by_user_name
            ? `Created by Phoebe, started by ${outreach.started_by_user_name}`
            : `Created by ${displayName}`;

    return (
        <div
            className="flex shrink-0 items-center gap-1.5"
            title={title}
            aria-label={title}
        >
            <UserAvatar
                displayName={displayName}
                colorKey={colorKey}
                toneClassName={getAvatarSolidToneClass(colorKey)}
            />
            <span className="text-sm">{displayName}</span>
        </div>
    );
}

function OutreachRecommendationEmptyState({
    title,
    description,
    actionLabel,
    onAction,
    headerAction,
}: {
    readonly title: string;
    readonly description?: string;
    readonly actionLabel: string;
    readonly onAction: () => void;
    readonly headerAction?: React.ReactNode;
}) {
    return (
        <div className="relative flex flex-col items-center justify-center px-6 py-12 text-center">
            {headerAction ? (
                <div className="absolute top-2 right-2">{headerAction}</div>
            ) : null}
            <p className="font-medium">{title}</p>
            {description ? (
                <p className="text-muted-foreground mt-1 max-w-sm text-sm">
                    {description}
                </p>
            ) : null}
            <Button className="mt-4" variant="outline" onClick={onAction}>
                {actionLabel}
            </Button>
        </div>
    );
}

function MobileDetailCard({
    label,
    children,
    action,
    compact = false,
    onClick,
    testId,
}: MobileDetailCardProps) {
    return (
        <div
            data-testid={testId}
            className={cn(
                compact ? 'rounded-md border p-2.5' : 'rounded-lg border p-4',
                onClick && 'hover:bg-muted/40 cursor-pointer transition-colors',
            )}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={
                onClick
                    ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
        >
            <div
                className={cn(
                    'flex items-center justify-between',
                    compact ? 'mb-1.5 gap-2' : 'mb-2 gap-3',
                )}
            >
                <div className="text-muted-foreground text-xs leading-4 font-medium">
                    {label}
                </div>
                {action}
            </div>
            <div className="min-w-0">{children}</div>
        </div>
    );
}

function OutreachDetails({
    outreach,
    onEditOutreachClick,
    timezone,
    actionButtons,
    mobileShiftsSection,
    displayStatus,
    organizationId,
    autoAssignPolicy,
    mutateOutreach,
    clientNotificationContent,
    onClientNotificationClick,
    chrome,
    showAutoAssignFirstResponderToggle,
    onAutoAssignSave,
    isUpdatingAutoAssign,
    autoAssignClientPolicyOverrides,
    showAddShiftsDialog,
    onAddShiftsDialogOpenChange,
}: OutreachDetailsProps) {
    const [isEditingCancellationReason, setIsEditingCancellationReason] =
        useState(false);
    const [cancellationReasonDraft, setCancellationReasonDraft] = useState(
        outreach.cancellation_reason ?? '',
    );
    const [isSavingCancellationReason, setIsSavingCancellationReason] =
        useState(false);

    const handleSaveCancellationReason = async () => {
        if (!organizationId) {
            return;
        }
        setIsSavingCancellationReason(true);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/callouts/{callout_id}/cancellation-reason',
                pathParams: {
                    organization_id: organizationId,
                    callout_id: outreach.id,
                },
                body: {
                    cancellation_reason: cancellationReasonDraft.trim() || null,
                },
            });
            await mutateOutreach();
            setIsEditingCancellationReason(false);
            toast.success('Cancellation reason updated', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
        } catch (error) {
            console.error('Failed to update cancellation reason:', error);
            if (!isTransparentRateLimitError(error)) {
                toast.error('Failed to update cancellation reason', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            }
        } finally {
            setIsSavingCancellationReason(false);
        }
    };

    const handleCancelEdit = () => {
        setCancellationReasonDraft(outreach.cancellation_reason ?? '');
        setIsEditingCancellationReason(false);
    };

    const autoAssignSavedShiftIdsKey = useMemo(
        () => [...getAutoAssignShiftIds(outreach)].sort().join(','),
        [outreach],
    );
    const isMobile = useIsMobile();
    const [isMobileDetailsOpen, setIsMobileDetailsOpen] = useState(false);
    useEffect(() => {
        if (!isMobile) {
            return undefined;
        }
        const handleOpenMobileDetails = (event: Event) => {
            const detail = (event as CustomEvent<{ outreachId?: string }>)
                .detail;
            if (detail?.outreachId && detail.outreachId !== outreach.id) {
                return;
            }
            setIsMobileDetailsOpen(true);
        };
        window.addEventListener(
            OPEN_MOBILE_OUTREACH_DETAILS_EVENT,
            handleOpenMobileDetails,
        );
        return () => {
            window.removeEventListener(
                OPEN_MOBILE_OUTREACH_DETAILS_EVENT,
                handleOpenMobileDetails,
            );
        };
    }, [isMobile, outreach.id]);
    const createdByLabel = outreach.auto_drafted
        ? `Phoebe${
              outreach.started_by_user_name
                  ? `, started by ${outreach.started_by_user_name}`
                  : ''
          }`
        : (outreach.created_by_user_name ?? 'Unknown');
    const scheduledStartAt =
        outreach.status === 'pending'
            ? getEffectiveScheduledOutreachStartAt(outreach.outreach_metadata)
            : null;
    const scheduledStartIsPaused =
        getScheduledOutreachState(outreach.outreach_metadata) === 'paused';
    const mobileOverflowContent = (
        <div className="space-y-3">
            <MobileDetailCard label="Status" compact={true}>
                <div className="flex flex-wrap items-center gap-2">
                    <OutreachStatusBadge
                        status={displayStatus ?? outreach.status}
                        autoStartManualReviewReason={
                            outreach.auto_start_manual_review_reason
                        }
                        acceptedCaregiverCount={
                            outreach.response_counts?.accepted_count
                        }
                        openAcceptedCaregiverCount={
                            outreach.response_counts?.open_accepted_count
                        }
                        hasAssignedCaregiver={outreach.shifts.some((shift) =>
                            Boolean(shift.assigned_caregiver_id),
                        )}
                        outreachMetadata={outreach.outreach_metadata}
                    />
                    <OutreachTypeBadge
                        outreachType={outreach.outreach_type}
                        isUrgent={isUrgentOutreach(outreach.outreach_metadata)}
                    />
                </div>
                {scheduledStartAt ? (
                    <div className="text-muted-foreground mt-1 text-xs">
                        {scheduledStartIsPaused
                            ? 'Paused — scheduled for'
                            : 'Scheduled to start'}{' '}
                        {formatDateAndTime(scheduledStartAt, timezone)}
                    </div>
                ) : null}
            </MobileDetailCard>
            <MobileDetailCard label="Reported" compact={true}>
                <div className="space-y-1 text-sm">
                    <div>
                        By <span className="font-medium">{createdByLabel}</span>
                    </div>
                    <div className="text-muted-foreground">
                        {formatDateAndTime(outreach.created_at, timezone)}
                    </div>
                </div>
            </MobileDetailCard>
            {(onEditOutreachClick || actionButtons) && (
                <MobileDetailCard label="Actions" compact={true}>
                    <div
                        className="grid gap-2 [&_button]:w-full [&_button]:justify-start"
                        onClickCapture={(event) => {
                            if (
                                event.target instanceof HTMLElement &&
                                event.target.closest('button,a')
                            ) {
                                setIsMobileDetailsOpen(false);
                            }
                        }}
                    >
                        {onEditOutreachClick ? (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onEditOutreachClick}
                            >
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit Outreach
                            </Button>
                        ) : null}
                        {actionButtons}
                    </div>
                </MobileDetailCard>
            )}
            {outreach.status === 'cancelled' && (
                <MobileDetailCard
                    label="Cancellation Reason"
                    compact={true}
                    action={
                        !isEditingCancellationReason ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                    setCancellationReasonDraft(
                                        outreach.cancellation_reason ?? '',
                                    );
                                    setIsEditingCancellationReason(true);
                                }}
                            >
                                <Pencil className="h-3 w-3" />
                            </Button>
                        ) : null
                    }
                >
                    {isEditingCancellationReason ? (
                        <div className="space-y-2">
                            <Textarea
                                value={cancellationReasonDraft}
                                onChange={(e) =>
                                    setCancellationReasonDraft(e.target.value)
                                }
                                placeholder="Enter a reason for cancellation..."
                                rows={3}
                            />
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    size="sm"
                                    onClick={handleSaveCancellationReason}
                                    disabled={isSavingCancellationReason}
                                >
                                    {isSavingCancellationReason ? (
                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                    ) : (
                                        <Check className="mr-1 h-3 w-3" />
                                    )}
                                    <span>Save</span>
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleCancelEdit}
                                    disabled={isSavingCancellationReason}
                                >
                                    <X className="mr-1 h-3 w-3" />
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-sm break-words whitespace-normal">
                            {outreach.cancellation_reason ? (
                                outreach.cancellation_reason
                            ) : (
                                <span className="text-muted-foreground italic">
                                    No reason provided
                                </span>
                            )}
                        </div>
                    )}
                </MobileDetailCard>
            )}
            {!!outreach.outreach_metadata['callout_reason'] && (
                <MobileDetailCard label="Open Shift Reason" compact={true}>
                    <div className="text-sm break-words whitespace-normal">
                        {outreach.outreach_metadata['callout_reason'] as string}
                    </div>
                </MobileDetailCard>
            )}
            {showAutoAssignFirstResponderToggle && (
                <MobileDetailCard
                    label={
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <span className="cursor-default">
                                    Auto-Assign First Responder
                                </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                                {getAutoAssignTooltip(autoAssignPolicy)}
                            </TooltipContent>
                        </Tooltip>
                    }
                    compact={true}
                >
                    <AutoAssignDetailSection
                        key={autoAssignSavedShiftIdsKey}
                        outreach={outreach}
                        timezone={timezone}
                        isUpdating={isUpdatingAutoAssign}
                        onSave={onAutoAssignSave}
                        clientPolicyOverrides={autoAssignClientPolicyOverrides}
                    />
                </MobileDetailCard>
            )}
            {mobileShiftsSection}
            {clientNotificationContent && (
                <MobileDetailCard
                    label="Client Notification"
                    compact={true}
                    onClick={onClientNotificationClick}
                >
                    {clientNotificationContent}
                </MobileDetailCard>
            )}
            {organizationId ? (
                <MobileDetailCard label="Activity" compact={true}>
                    <EntityEventsTimeline
                        organizationId={organizationId}
                        entityType="outreach"
                        entityId={outreach.id}
                    />
                </MobileDetailCard>
            ) : null}
        </div>
    );

    if (isMobile) {
        return (
            <React.Fragment>
                <Dialog
                    open={isMobileDetailsOpen}
                    onOpenChange={setIsMobileDetailsOpen}
                >
                    <DialogContent className="max-sm:[&_[data-slot=dialog-close]]:bg-background max-w-sm overflow-y-auto p-4 pt-12 max-sm:top-36 max-sm:right-3 max-sm:bottom-4 max-sm:left-3 max-sm:max-h-none max-sm:w-auto max-sm:max-w-none max-sm:rounded-2xl max-sm:[&_[data-slot=dialog-close]]:top-6 max-sm:[&_[data-slot=dialog-close]]:right-5 max-sm:[&_[data-slot=dialog-close]]:flex max-sm:[&_[data-slot=dialog-close]]:h-9 max-sm:[&_[data-slot=dialog-close]]:w-9 max-sm:[&_[data-slot=dialog-close]]:items-center max-sm:[&_[data-slot=dialog-close]]:justify-center max-sm:[&_[data-slot=dialog-close]]:rounded-full max-sm:[&_[data-slot=dialog-close]]:border max-sm:[&_[data-slot=dialog-close]]:opacity-100">
                        <DialogHeader className="sr-only">
                            <DialogTitle>Outreach details</DialogTitle>
                        </DialogHeader>
                        {mobileOverflowContent}
                    </DialogContent>
                </Dialog>
                <AddShiftsDialog
                    open={showAddShiftsDialog}
                    onOpenChange={onAddShiftsDialogOpenChange}
                    outreach={outreach}
                    organizationId={organizationId ?? ''}
                    onSuccess={() => {
                        mutateOutreach();
                    }}
                />
            </React.Fragment>
        );
    }

    return (
        <div
            className={outreachDetailsPanelClassName(chrome)}
            data-testid="outreach-details-panel"
        >
            <div className={outreachDetailsHeaderClassName(chrome)}>
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                    <h2 className="shrink-0 text-lg font-medium">
                        Outreach Details
                    </h2>
                    <div className={outreachDetailsStatusRowClassName(chrome)}>
                        <OutreachStatusBadge
                            status={displayStatus ?? outreach.status}
                            autoStartManualReviewReason={
                                outreach.auto_start_manual_review_reason
                            }
                            acceptedCaregiverCount={
                                outreach.response_counts?.accepted_count
                            }
                            openAcceptedCaregiverCount={
                                outreach.response_counts?.open_accepted_count
                            }
                            hasAssignedCaregiver={outreach.shifts.some(
                                (shift) => Boolean(shift.assigned_caregiver_id),
                            )}
                            outreachMetadata={outreach.outreach_metadata}
                        />
                        {scheduledStartAt ? (
                            <span className="text-muted-foreground text-xs">
                                {scheduledStartIsPaused
                                    ? 'Paused — scheduled for'
                                    : 'Scheduled to start'}{' '}
                                {formatDateAndTime(scheduledStartAt, timezone)}
                            </span>
                        ) : null}
                    </div>
                    <OutreachTypeBadge
                        outreachType={outreach.outreach_type}
                        isUrgent={isUrgentOutreach(outreach.outreach_metadata)}
                    />
                    <OutreachCreatedByChip outreach={outreach} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {actionButtons}
                    {onEditOutreachClick && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-foreground h-5 w-5 p-0"
                            onClick={onEditOutreachClick}
                            aria-label="Edit outreach"
                        >
                            <Pencil className="size-2.5" />
                        </Button>
                    )}
                </div>
            </div>
            {(outreach.status === 'cancelled' ||
                !!outreach.outreach_metadata['callout_reason'] ||
                showAutoAssignFirstResponderToggle ||
                Boolean(clientNotificationContent)) && (
                <Table className={outreachDetailsTableClassName(chrome)}>
                    <TableBody>
                        {outreach.status === 'cancelled' && (
                            <TableRow className="border-0">
                                <TableCell className="text-muted-foreground w-[140px] pl-0 align-top font-medium">
                                    <div className="flex items-center">
                                        Cancellation Reason
                                        {!isEditingCancellationReason && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={() => {
                                                    setCancellationReasonDraft(
                                                        outreach.cancellation_reason ??
                                                            '',
                                                    );
                                                    setIsEditingCancellationReason(
                                                        true,
                                                    );
                                                }}
                                            >
                                                <Pencil className="h-3 w-3" />
                                            </Button>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className="border-0">
                                    {isEditingCancellationReason ? (
                                        <div className="space-y-2">
                                            <Textarea
                                                value={cancellationReasonDraft}
                                                onChange={(e) =>
                                                    setCancellationReasonDraft(
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Enter a reason for cancellation..."
                                                rows={3}
                                                className="max-w-md"
                                            />
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={
                                                        handleSaveCancellationReason
                                                    }
                                                    disabled={
                                                        isSavingCancellationReason
                                                    }
                                                >
                                                    {isSavingCancellationReason ? (
                                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <Check className="mr-1 h-3 w-3" />
                                                    )}
                                                    <span>Save</span>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={handleCancelEdit}
                                                    disabled={
                                                        isSavingCancellationReason
                                                    }
                                                >
                                                    <X className="mr-1 h-3 w-3" />
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-sm break-words whitespace-normal">
                                            {outreach.cancellation_reason ? (
                                                outreach.cancellation_reason
                                            ) : (
                                                <span className="text-muted-foreground italic">
                                                    No reason provided
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                        {!!outreach.outreach_metadata['callout_reason'] && (
                            <TableRow className="border-0">
                                <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                    Open Shift Reason
                                </TableCell>
                                <TableCell className="border-0" colSpan={2}>
                                    <div className="break-words whitespace-normal">
                                        {
                                            outreach.outreach_metadata[
                                                'callout_reason'
                                            ] as string
                                        }
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {showAutoAssignFirstResponderToggle && (
                            <TableRow className="border-0">
                                <TableCell className="text-muted-foreground w-[140px] pl-0 align-top font-medium">
                                    <Tooltip>
                                        <TooltipTrigger asChild={true}>
                                            <span className="cursor-default">
                                                Auto-Assign First Responder
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                            {getAutoAssignTooltip(
                                                autoAssignPolicy,
                                            )}
                                        </TooltipContent>
                                    </Tooltip>
                                </TableCell>
                                <TableCell className="border-0">
                                    <AutoAssignDetailSection
                                        key={autoAssignSavedShiftIdsKey}
                                        outreach={outreach}
                                        timezone={timezone}
                                        isUpdating={isUpdatingAutoAssign}
                                        onSave={onAutoAssignSave}
                                        clientPolicyOverrides={
                                            autoAssignClientPolicyOverrides
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                        {clientNotificationContent && (
                            <TableRow
                                className={cn(
                                    'border-0',
                                    onClientNotificationClick &&
                                        'hover:bg-muted/40 cursor-pointer transition-colors',
                                )}
                                onClick={onClientNotificationClick}
                                role={
                                    onClientNotificationClick
                                        ? 'button'
                                        : undefined
                                }
                                tabIndex={
                                    onClientNotificationClick ? 0 : undefined
                                }
                                onKeyDown={
                                    onClientNotificationClick
                                        ? (event) => {
                                              if (
                                                  event.key === 'Enter' ||
                                                  event.key === ' '
                                              ) {
                                                  event.preventDefault();
                                                  onClientNotificationClick();
                                              }
                                          }
                                        : undefined
                                }
                            >
                                <TableCell className="text-muted-foreground w-[140px] pl-0 align-top font-medium">
                                    Client Notification
                                </TableCell>
                                <TableCell className="border-0">
                                    {clientNotificationContent}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            )}
        </div>
    );
}

async function fetchCaregiverIdsForShift(
    organizationId: string,
    shiftId: string,
): Promise<Array<string>> {
    const recs = await getApiClient().get({
        url: '/organizations/{organization_id}/caregiver-recommendations/{shift_id}',
        pathParams: {
            organization_id: organizationId,
            shift_id: shiftId,
        },
    });
    return (
        recs?.recommendations?.map(
            (r: { readonly caregiver_id: string }) => r.caregiver_id,
        ) ?? []
    );
}

// Shared stable fallbacks so a unit with no agent suggestions still yields a
// constant identity, keeping InhouseRecommendationsTable's memoized columns
// (and the Radix Checkbox select header) from remounting on every render.
const EMPTY_UNIT_CAREGIVER_IDS: Array<string> = [];
const EMPTY_UNIT_REASONING_MAP: ReadonlyMap<string, string> = new Map();

// Keep the bib fixed above the table scrollers. From `md` up the detail view
// can sit beside a docked chat, so anchor the bib to the app content's left
// edge and size it from this page's query container (`@container` on the
// OutreachDetail root) — centering it over the table rather than the viewport.
// Below `md` there is no chat beside the page but the route workspace does add
// its own padding, so plain viewport centering is the accurate one.
const OUTREACH_SELECTION_BAR_WRAPPER_CLASS =
    'pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4 md:right-auto md:left-[var(--app-content-left-offset,0px)] md:w-[100cqw]';

const OUTREACH_SELECTION_BAR_SHELL_CLASS =
    'border-border bg-background text-foreground pointer-events-auto flex w-fit max-w-[calc(100%-2rem)] items-center gap-2 rounded-full border px-3 py-1.5 shadow-md transition-colors';

// Split flap + full-width reveal: selection rolls up, status rolls down; spinner
// stays left while report copy wipes across to the X. Under reduced motion the
// transforms are dropped, so the layers rely on opacity instead of stacking.
const OUTREACH_BAR_FLAP_CLASS =
    'duration-[450ms] ease-[cubic-bezier(0.4,0,0.2,1)] transition-transform motion-reduce:transition-none motion-reduce:transform-none';
// `0fr` is the one fraction value that animates as a true zero, so the browser
// eases the trail's own intrinsic width — no measuring, and nothing pinned in
// pixels that a narrow viewport could then clamp below the text it must fit.
const OUTREACH_BAR_TRAIL_CLASS =
    'duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] transition-[grid-template-columns,opacity] motion-reduce:transition-none';
// Wipe trails the flap so the roll reads first, then the copy sweeps out to the X.
// Collapsing a `0fr` track rather than clipping in place: the text's own
// layout width animates, so the pill eases open around it exactly as it did
// when an off-screen probe measured the sentence and pinned a pixel width.
const OUTREACH_BAR_TEXT_REVEAL_CLASS =
    'duration-[550ms] delay-[120ms] ease-[cubic-bezier(0.22,1,0.36,1)] transition-[grid-template-columns] motion-reduce:transition-none';
// Trailing chrome stays mounted until the collapse transition finishes, so the
// animation is not cut short; a shade past OUTREACH_BAR_TRAIL_CLASS's 500ms.
const OUTREACH_BAR_TRAIL_UNMOUNT_MS = 520;

// A teammate typing in this outreach's chat, surfaced on the detail page
// itself so presence is visible while the chat panel is closed. Shares the
// selection bar's fixed anchoring; lifted above it when both are on screen
// so the two pills never overlap.
function OutreachChatTypingPill({
    typingUsers,
    liftedAboveSelectionBar,
}: {
    readonly typingUsers: ReadonlyArray<ChatTypingUser>;
    readonly liftedAboveSelectionBar: boolean;
}) {
    if (typingUsers.length === 0) {
        return null;
    }
    return (
        <div
            className={cn(
                OUTREACH_SELECTION_BAR_WRAPPER_CLASS,
                liftedAboveSelectionBar && 'bottom-16',
            )}
            data-testid="outreach-chat-typing-pill-wrapper"
        >
            <div
                className={cn(
                    OUTREACH_SELECTION_BAR_SHELL_CLASS,
                    'text-muted-foreground text-xs',
                )}
                data-testid="outreach-chat-typing-pill"
            >
                <span>{formatChatTypingLabel(typingUsers)}</span>
            </div>
        </div>
    );
}

type OutreachSelectionFloatingBarProps = {
    readonly startReport: StartReport | null;
    readonly selectedCaregiverCount: number;
    readonly selectedCaregiverShiftCount: number;
    readonly isOverCaregiverLimit: boolean;
    readonly maxOutreachCaregivers: number;
    readonly shiftReadinessLabel: string | undefined;
    readonly isAgentAddingPeople: boolean;
    readonly isStartingSelectedCaregivers: boolean;
    readonly clearSelectedCaregivers: () => void;
    readonly renderStartButton: (className?: string) => React.ReactNode;
};

function OutreachSelectionFloatingBar({
    startReport,
    selectedCaregiverCount,
    selectedCaregiverShiftCount,
    isOverCaregiverLimit,
    maxOutreachCaregivers,
    shiftReadinessLabel,
    isAgentAddingPeople,
    isStartingSelectedCaregivers,
    clearSelectedCaregivers,
    renderStartButton,
}: OutreachSelectionFloatingBarProps) {
    const barRef = useRef<HTMLDivElement>(null);
    const reportStatus = startReport?.status ?? null;
    // A failure keeps the trailing chrome: the bar is telling the coordinator
    // to try again, so the button that does it has to still be there. Only a
    // send in progress or a confirmed start collapses it.
    const collapsesTrail =
        reportStatus === 'sending' || reportStatus === 'started';
    const [isFlapActive, setIsFlapActive] = useState(false);
    const [isTrailMounted, setIsTrailMounted] = useState(true);

    useEffect(() => {
        if (reportStatus === null) {
            setIsFlapActive(false);
            setIsTrailMounted(true);
            return;
        }

        // A timer, not `requestAnimationFrame`: browsers pause rAF in a hidden
        // tab, so a coordinator who clicks Start and switches away would come
        // back to a spinner with the sentence still collapsed to nothing. One
        // macrotask is enough for the `0fr` state to commit first, which is all
        // the transition needs to animate rather than jump.
        const revealFlap = window.setTimeout(() => {
            setIsFlapActive(true);
        }, 0);
        if (!collapsesTrail) {
            setIsTrailMounted(true);
            return () => window.clearTimeout(revealFlap);
        }

        const unmountTrail = window.setTimeout(() => {
            setIsTrailMounted(false);
        }, OUTREACH_BAR_TRAIL_UNMOUNT_MS);

        return () => {
            window.clearTimeout(revealFlap);
            window.clearTimeout(unmountTrail);
        };
    }, [reportStatus, collapsesTrail]);

    return (
        <React.Fragment>
            <div
                className={OUTREACH_SELECTION_BAR_WRAPPER_CLASS}
                data-testid="outreach-selection-bar-wrapper"
            >
                <div
                    ref={barRef}
                    className={cn(
                        OUTREACH_SELECTION_BAR_SHELL_CLASS,
                        'overflow-hidden',
                    )}
                    data-testid="outreach-selection-bar"
                >
                    {/* Both layers share one grid cell, so the track sizes to
                        whichever is wider and the pill grows to fit the report
                        sentence on its own. Stacking the status layer with
                        `absolute` instead sized the slot from the selection
                        copy alone and clipped the longer text — which is what
                        the off-screen measuring probe used to paper over. */}
                    <div className="grid h-8 min-w-0 flex-auto grid-cols-1 overflow-hidden">
                        <div
                            className={cn(
                                'flex h-full items-center gap-2 whitespace-nowrap [grid-area:1/1]',
                                OUTREACH_BAR_FLAP_CLASS,
                                isFlapActive
                                    ? 'pointer-events-none -translate-y-full motion-reduce:opacity-0'
                                    : 'translate-y-0',
                            )}
                            aria-hidden={isFlapActive}
                        >
                            <span className="text-sm font-medium">
                                {selectedCaregiverCount} selected
                            </span>
                            {selectedCaregiverShiftCount > 1 && (
                                <span className="text-muted-foreground text-sm">
                                    across {selectedCaregiverShiftCount} shifts
                                </span>
                            )}
                        </div>
                        {/* The live region stays mounted and keeps a stable
                            role; only its content swaps. A region inserted
                            together with its text is not reliably announced,
                            and neither is one whose role changes — either would
                            lose the messages the toasts used to speak. It is
                            hidden by the flap transform rather than
                            `visibility`, which would take it back out of the
                            accessibility tree and defeat the whole point. */}
                        <div
                            className={cn(
                                'flex h-full items-center gap-2 whitespace-nowrap [grid-area:1/1]',
                                OUTREACH_BAR_FLAP_CLASS,
                                isFlapActive
                                    ? 'translate-y-0'
                                    : 'translate-y-full motion-reduce:opacity-0',
                            )}
                            role="status"
                        >
                            {startReport ? (
                                <React.Fragment>
                                    <div className="shrink-0">
                                        {startReport.status === 'sending' ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <span
                                                className={cn(
                                                    'zoom-in-50 animate-in grid size-4 place-items-center rounded-full text-[10px] font-bold duration-200 motion-reduce:animate-none',
                                                    startReport.status ===
                                                        'started'
                                                        ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                                                        : 'bg-destructive/15 text-destructive',
                                                )}
                                                aria-hidden={true}
                                            >
                                                {startReport.status ===
                                                'started'
                                                    ? '✓'
                                                    : '!'}
                                            </span>
                                        )}
                                    </div>
                                    <span
                                        className={cn(
                                            'grid',
                                            OUTREACH_BAR_TEXT_REVEAL_CLASS,
                                            isFlapActive
                                                ? 'grid-cols-[1fr]'
                                                : 'grid-cols-[0fr]',
                                        )}
                                    >
                                        <span
                                            className="block min-w-0 overflow-hidden text-sm whitespace-nowrap"
                                            data-testid="outreach-start-report"
                                        >
                                            {startReport.text}
                                        </span>
                                    </span>
                                </React.Fragment>
                            ) : null}
                        </div>
                    </div>
                    {/* While a report is up the selection is no longer on
                        screen, so clearing it would be an invisible edit with
                        the visible message left behind. Dismiss the report
                        instead — the obvious reading of an X on a status bar —
                        and leave the selection intact to retry with. */}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={
                            startReport
                                ? dismissStartReport
                                : clearSelectedCaregivers
                        }
                        disabled={
                            isAgentAddingPeople || isStartingSelectedCaregivers
                        }
                        className="text-foreground hover:bg-foreground/10 h-5 w-5 shrink-0"
                        aria-label={startReport ? 'Dismiss' : 'Clear selection'}
                    >
                        <X className="h-3 w-3" />
                    </Button>
                    {isTrailMounted || !collapsesTrail ? (
                        // Deliberately not `inert`: it evicts focus from its
                        // subtree, which threw the coordinator to <body> on
                        // every keyboard submit and took three attempts to put
                        // back. Nothing here needs it — Start is `disabled` for
                        // the whole request, so it is unfocusable anyway, and
                        // the trail unmounts once the collapse finishes.
                        <div
                            className={cn(
                                'grid',
                                OUTREACH_BAR_TRAIL_CLASS,
                                isFlapActive && collapsesTrail
                                    ? 'pointer-events-none grid-cols-[0fr] opacity-0'
                                    : 'grid-cols-[1fr] opacity-100',
                            )}
                            aria-hidden={collapsesTrail}
                        >
                            <div className="flex min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap">
                                <div className="bg-foreground/20 h-4 w-px shrink-0" />
                                {isOverCaregiverLimit && (
                                    <span className="text-destructive shrink-0 text-[11px]">
                                        Maximum {maxOutreachCaregivers}{' '}
                                        caregivers allowed
                                    </span>
                                )}
                                {shiftReadinessLabel && (
                                    <span className="text-muted-foreground shrink-0 text-[11px]">
                                        {shiftReadinessLabel}
                                    </span>
                                )}
                                <div className="shrink-0">
                                    {renderStartButton('text-xs')}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </React.Fragment>
    );
}

export function OutreachDetail({
    outreachAction,
    outreach,
    mutateOutreach,
    onOpenOutreachChat,
    isOutreachChatAvailable = false,
    selectedContactAttemptId: selectedContactAttemptIdProp,
    chatTypingUsers,
}: OutreachDetailProps) {
    const { setSelection: setOutreachCaregiverSelection } =
        useOutreachCaregiverSelection();
    const displayCaregiverNamesLastFirst = useDisplayNamesLastFirst();
    const formatName = useCallback(
        (fullName: string | null | undefined) =>
            formatCaregiverName(fullName ?? '', displayCaregiverNamesLastFirst),
        [displayCaregiverNamesLastFirst],
    );
    const navigate = useNavigate();
    const posthog = usePostHog();
    const selectedContactAttemptId = selectedContactAttemptIdProp ?? null;
    const [selectedCaregiverDetailId, setSelectedCaregiverDetailId] = useState<
        string | null
    >(null);
    const assignAndFillDeepLinkHandledKeyRef = useRef<string | null>(null);
    const organizationId = useOrganizationId();
    const { timezone } = useOrganizationTimezone();
    const isMobile = useIsMobile();
    const { data: user } = useUser({ reason: 'callout-detail' });
    const isDevelopment = getRunEnvironment() === 'development';

    const { data: organization } = useOrganization(organizationId, {
        reason: 'callout-detail',
    });
    const uiV3 = useFeatureFlag('ui_v3_components', organization?.features);
    const sortByLastName =
        organization?.features?.last_name_sorting_enabled === true;
    const {
        data: agentInstructionsTemplates,
        isLoading: isLoadingAgentInstructionsTemplate,
    } = useOrganizationTemplates(organizationId, { reason: 'callout-detail' }, [
        'agent_instructions',
    ]);
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        {
            reason: 'callout-detail',
        },
    );
    const isAxisCare =
        dataSourceAccounts?.some((account) => account.type === 'axiscare') ??
        false;
    const {
        showCaregiverStatus: showEhrCaregiverStatus,
        showCompliance: showHHAExchangeCompliance,
    } = getEhrCaregiverFieldVisibility(dataSourceAccounts);
    const outreachClientIds = useMemo(
        () =>
            [
                ...new Set(
                    outreach.shifts.flatMap((shift) =>
                        shift.client_id ? [shift.client_id] : [],
                    ),
                ),
            ].sort(),
        [outreach.shifts],
    );
    const { data: clients, mutate: mutateClients } = useOrganizationClients(
        organizationId,
        {
            reason: 'callout-detail',
        },
        { includeInactive: true, clientIds: outreachClientIds },
    );

    const clientById = useMemo(() => {
        const map = new Map<string, OrgClientRead>();
        for (const client of clients ?? []) {
            map.set(client.id, client);
        }
        return map;
    }, [clients]);

    const autoAssignAllowed = isAutoAssignAllowed(
        organization?.callout_settings,
    );
    const supportsAutoAssignFirstResponder =
        autoAssignAllowed &&
        supportsOutreachAutoAssignFirstResponder(outreach.outreach_type);
    const autoAssignClientPolicyOverrides = useMemo(() => {
        const organizationPolicy = resolveOrganizationAutoAssignPolicy(
            organization?.callout_settings,
        );
        const seen = new Set<string>();
        const rows: Array<ClientAutoAssignPolicyOverride> = [];
        for (const shift of outreach.shifts) {
            const clientId = shift.client_id;
            if (!clientId || seen.has(clientId)) {
                continue;
            }
            seen.add(clientId);
            const policy = clientById.get(clientId)?.auto_assign_policy;
            if (policy && policy !== organizationPolicy) {
                rows.push({
                    clientId,
                    clientName:
                        formatName(shift.patient_name) || 'Unknown client',
                    policy,
                });
            }
        }
        return rows;
    }, [
        clientById,
        organization?.callout_settings,
        outreach.shifts,
        formatName,
    ]);
    const chrome = outreachDetailChrome(outreach);
    const isCompactChrome = chrome === 'compact';
    const showAutoAssignColumn =
        supportsAutoAssignFirstResponder && isCompactChrome;
    const {
        data: contactAttempts,
        isLoading: isLoadingContactAttempts,
        error: contactAttemptsError,
        mutate: mutateContactAttempts,
    } = useContactAttemptsRealtime(outreach.id, 'callout-detail');
    const { data: relatedInboundCalls } =
        useOutreachRelatedInboundCallsRealtime(outreach.id, 'callout-detail');
    const { data: clientContactAttempts, mutate: mutateClientContactAttempts } =
        useClientContactAttemptsRealtime(outreach.id, 'callout-detail');
    const isMultiShiftOutreach = true;
    const isMultiClientOutreach =
        new Set(
            outreach.shifts.map(
                (shift) => shift.client_id ?? shift.patient_name,
            ),
        ).size > 1;
    const [matchSignalSortKey, setMatchSignalSortKey] = useState<string | null>(
        null,
    );
    const [outreachConflictError, setOutreachConflictError] =
        useState<OutreachConflictError | null>(null);
    const [manualContactConflictOverride, setManualContactConflictOverride] =
        useState<ManualContactConflictOverride | null>(null);
    const [scheduleConflict, setScheduleConflict] =
        useState<OutreachScheduleConflict | null>(null);
    const [conflictingStart, setConflictingStart] = useState<{
        readonly kind: 'start' | 'confirm_suggestions';
        readonly caregiverShiftIds: CaregiverShiftIds;
        readonly caregiverNamesById?: Readonly<Record<string, string>>;
    } | null>(null);
    const [isConfirmingScheduleConflict, setIsConfirmingScheduleConflict] =
        useState(false);
    const [retryingClientContactAttemptId, setRetryingClientContactAttemptId] =
        useState<string | null>(null);
    const [markingClientContactAttemptId, setMarkingClientContactAttemptId] =
        useState<string | null>(null);
    const [
        isUpdatingAutoAssignFirstResponder,
        setIsUpdatingAutoAssignFirstResponder,
    ] = useState(false);
    const initialShiftGroups = useMemo<ReadonlyArray<ShiftGroupValue>>(() => {
        if (outreach.shift_groups && outreach.shift_groups.length > 0) {
            return outreach.shift_groups
                .filter((g) => g.shift_ids.length > 0)
                .map((g) => ({
                    id: g.id,
                    shiftIds: g.shift_ids,
                    groupMode: g.group_mode as 'all' | 'any',
                }));
        }
        // Legacy format: metadata['shift_groups'] is a list of
        // client keys with all-or-nothing grouping
        const legacyKeys = outreach.outreach_metadata?.['shift_groups'];
        if (
            !Array.isArray(legacyKeys) ||
            legacyKeys.length === 0 ||
            !outreach.shifts
        ) {
            return [];
        }
        const keySet = new Set<string>(legacyKeys as Array<string>);
        // Only the grouped shifts are read here; the names are not shown.
        const clientGroups = groupShiftsByClient(
            outreach.shifts,
            rawPersonName,
        );
        return [...clientGroups.entries()]
            .filter(([key, { shifts }]) => keySet.has(key) && shifts.length > 1)
            .map(([, { shifts }]) => ({
                id: crypto.randomUUID(),
                shiftIds: shifts.map((s) => s.id),
                groupMode: 'all' as const,
            }));
    }, [outreach.shift_groups, outreach.outreach_metadata, outreach.shifts]);
    const matchSignalSortOptions = useMemo(() => {
        if (!isMultiShiftOutreach || !outreach.shifts) {
            return [] as Array<{ key: string; label: string }>;
        }

        return outreach.shifts.map((shift) => {
            return {
                key: shift.id,
                label: `${formatDateAndTimeWithWeekday(shift.shift_start_time, timezone)} - ${formatName(shift.patient_name)}`,
            };
        });
    }, [isMultiShiftOutreach, outreach.shifts, timezone, formatName]);

    useEffect(() => {
        if (matchSignalSortOptions.length === 0) {
            if (matchSignalSortKey !== null) {
                setMatchSignalSortKey(null);
            }
            return;
        }

        if (!matchSignalSortKey) {
            return;
        }

        if (
            matchSignalSortOptions.some(
                (option) => option.key === matchSignalSortKey,
            )
        ) {
            return;
        }

        setMatchSignalSortKey(null);
    }, [matchSignalSortKey, matchSignalSortOptions]);

    const shiftLabelById = useMemo(() => {
        const labels = new Map<string, string>();
        for (const shift of outreach.shifts ?? []) {
            labels.set(
                shift.id,
                `${formatDateAndTimeWithWeekday(shift.shift_start_time, timezone)} - ${formatName(shift.patient_name)}`,
            );
        }
        return labels;
    }, [outreach.shifts, timezone, formatName]);
    const handleMatchSignalSortKeyChange = useCallback((signalKey: string) => {
        setMatchSignalSortKey(signalKey);
    }, []);

    const shiftHoursById = useMemo(() => {
        const hoursById = new Map<string, number>();
        for (const shift of outreach.shifts ?? []) {
            const start = new Date(shift.shift_start_time);
            const end = new Date(shift.shift_end_time);
            const durationHours = (end.getTime() - start.getTime()) / 3600000;
            if (Number.isFinite(durationHours) && durationHours > 0) {
                hoursById.set(shift.id, durationHours);
            }
        }
        return hoursById;
    }, [outreach.shifts]);

    const filledShiftIds = useMemo(() => {
        const metadata = outreach?.outreach_metadata as {
            filled_shifts?: Record<
                string,
                { caregiver_id: string; filled_at: string }
            >;
        } | null;
        return new Set(Object.keys(metadata?.filled_shifts ?? {}));
    }, [outreach?.outreach_metadata]);

    const cancelledShiftIds = useMemo(() => {
        const metadata = outreach?.outreach_metadata as {
            cancelled_shifts?: Array<string>;
        } | null;
        return new Set(metadata?.cancelled_shifts ?? []);
    }, [outreach?.outreach_metadata]);

    // Shifts the page shows as cancelled: taken off this outreach, or
    // cancelled in the EHR. Rows and group display share this one definition.
    const displayCancelledShiftIds = useMemo(
        () =>
            new Set([
                ...cancelledShiftIds,
                ...outreach.shifts
                    .filter((shift) => shift.status === 'cancelled')
                    .map((shift) => shift.id),
            ]),
        [cancelledShiftIds, outreach.shifts],
    );

    // Cancelling a shift off a started outreach leaves it in the stored
    // group, so group display uses only the members each group still holds.
    const activeShiftGroups = useMemo(
        () =>
            (outreach.shift_groups ?? []).map((group) => ({
                ...group,
                shift_ids: group.shift_ids.filter(
                    (shiftId) => !displayCancelledShiftIds.has(shiftId),
                ),
            })),
        [outreach.shift_groups, displayCancelledShiftIds],
    );

    const shiftCarePlans = useMemo(
        () => readCarePlanFromMetadata(outreach).perShiftCarePlans,
        [outreach],
    );
    const [selectedShiftIdsToArchive, setSelectedShiftIdsToArchive] = useState<
        Set<string>
    >(new Set());
    const archivableShiftIds = useMemo(
        () =>
            outreach.shifts
                .filter(
                    (shift) =>
                        CANCELLABLE_SHIFT_OUTREACH_STATUSES.has(
                            outreach.status,
                        ) &&
                        !filledShiftIds.has(shift.id) &&
                        !cancelledShiftIds.has(shift.id) &&
                        shift.status !== 'cancelled',
                )
                .map((shift) => shift.id),
        [cancelledShiftIds, filledShiftIds, outreach.shifts, outreach.status],
    );
    const archivableShiftIdSet = useMemo(
        () => new Set(archivableShiftIds),
        [archivableShiftIds],
    );
    const selectedArchivableShiftIds = useMemo(
        () =>
            Array.from(selectedShiftIdsToArchive).filter((shiftId) =>
                archivableShiftIdSet.has(shiftId),
            ),
        [archivableShiftIdSet, selectedShiftIdsToArchive],
    );
    const allArchivableShiftsSelected =
        archivableShiftIds.length > 0 &&
        selectedArchivableShiftIds.length === archivableShiftIds.length;
    const showShiftArchiveSelection = archivableShiftIds.length > 0;
    const selectedShiftArchivePatientName = useMemo(() => {
        if (selectedArchivableShiftIds.length !== 1) {
            return `${selectedArchivableShiftIds.length} shifts`;
        }
        return (
            outreach.shifts.find(
                (shift) => shift.id === selectedArchivableShiftIds[0],
            )?.patient_name ?? 'selected shift'
        );
    }, [outreach.shifts, selectedArchivableShiftIds]);

    useEffect(() => {
        setSelectedShiftIdsToArchive((previousIds) => {
            const nextIds = new Set(
                Array.from(previousIds).filter((shiftId) =>
                    archivableShiftIdSet.has(shiftId),
                ),
            );
            if (nextIds.size === previousIds.size) {
                return previousIds;
            }
            return nextIds;
        });
    }, [archivableShiftIdSet]);

    const assignedShiftIds = useMemo(() => {
        return new Set(
            outreach.shifts
                .filter((shift) => shift.assigned_caregiver_id)
                .map((shift) => shift.id),
        );
    }, [outreach.shifts]);

    const relatedInboundCallsByCaregiver = useMemo(() => {
        const groupedCalls = new Map<
            string,
            Array<RelatedInboundOutreachCall>
        >();
        for (const inboundCall of relatedInboundCalls ?? []) {
            const existingCalls =
                groupedCalls.get(inboundCall.caregiver_id) ?? [];
            existingCalls.push(inboundCall);
            groupedCalls.set(inboundCall.caregiver_id, existingCalls);
        }

        for (const calls of groupedCalls.values()) {
            calls.sort(
                (a, b) =>
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime(),
            );
        }

        return groupedCalls;
    }, [relatedInboundCalls]);

    const contactAttemptGroups = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return [] as Array<ContactAttemptGroup>;
        }

        const groupsByCaregiver = new Map<
            string,
            { caregiver: OrgCaregiverRecord; attempts: Array<ContactAttempt> }
        >();

        for (const attempt of contactAttempts ?? []) {
            const caregiverId = attempt.caregiver_id;
            const existingGroup = groupsByCaregiver.get(caregiverId);

            if (existingGroup) {
                existingGroup.attempts.push(attempt);
            } else {
                groupsByCaregiver.set(caregiverId, {
                    caregiver: attempt.org_caregiver_record,
                    attempts: [attempt],
                });
            }
        }

        return Array.from(groupsByCaregiver.values()).map((group) => {
            const overallResponse = getOverallCaregiverResponse(group.attempts);
            const shiftResponses = getResolvedShiftResponsesFromContactAttempts(
                group.attempts,
            );
            const wellskyOverrideReview =
                getOutstandingWellSkyOverrideReviewFromContactAttempts(
                    group.attempts,
                    {
                        filledShiftIds,
                        cancelledShiftIds,
                        assignedShiftIds,
                    },
                );
            const hhaexchangeOvertimeReview =
                getOutstandingHHAExchangeOvertimeReviewFromContactAttempts(
                    group.attempts,
                    {
                        filledShiftIds,
                        cancelledShiftIds,
                        assignedShiftIds,
                    },
                );
            const relatedInboundTimelineItems = (
                relatedInboundCallsByCaregiver.get(group.caregiver.id) ?? []
            ).map((relatedInboundCall) =>
                buildRelatedInboundTimelineItem({
                    relatedInboundCall,
                    caregiver: group.caregiver,
                    outreachId: outreach.id,
                }),
            );
            const timelineItems = [
                ...group.attempts,
                ...relatedInboundTimelineItems,
            ].sort(
                (a, b) =>
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime(),
            );

            // Use latest attempt for metadata (distance/match signal source)
            const latestAttempt = group.attempts[0];
            if (!latestAttempt) {
                return {
                    caregiver: group.caregiver,
                    attempts: group.attempts,
                    timelineItems,
                    overallResponse,
                    recordedResponse: overallResponse,
                    wellskyOverrideReview: null,
                    hhaexchangeOvertimeReview: null,
                    hasOverride: false,
                    shiftResponses: null,
                    interestedShiftCount: undefined,
                    currentShiftEndTime: null,
                    scheduleOverlapMinutes: null,
                    scheduleOverlapRanges: null,
                    scheduleConflictingShifts: null,
                    matchSignalsByShift: [],
                };
            }
            const matchSignalsByShift =
                latestAttempt.match_signals
                    ?.filter(
                        (signal) =>
                            shiftResponses?.[signal.shift_id] !== undefined,
                    )
                    .map((signal) => ({
                        shiftId: signal.shift_id,
                        label:
                            shiftLabelById.get(signal.shift_id) ??
                            signal.shift_label,
                        weeklyHours: signal.weekly_hours ?? null,
                        previousVisits: signal.previous_visits ?? null,
                        shiftHours: shiftHoursById.get(signal.shift_id) ?? null,
                        sameDayShiftCount: signal.same_day_shift_count ?? null,
                        declineCount: signal.decline_count ?? null,
                        isAvailable:
                            shiftResponses?.[signal.shift_id] ===
                            'available_interested',
                        isPreferred: signal.is_preferred ?? false,
                    })) ?? [];

            const interestedShiftCount = shiftResponses
                ? Object.values(shiftResponses).filter(
                      (response) => response === 'available_interested',
                  ).length
                : undefined;

            return {
                caregiver: group.caregiver,
                attempts: group.attempts,
                timelineItems,
                overallResponse,
                recordedResponse: overallResponse,
                wellskyOverrideReview,
                hhaexchangeOvertimeReview,
                hasOverride: false,
                shiftResponses,
                interestedShiftCount,
                currentShiftEndTime: latestAttempt?.current_shift_end ?? null,
                scheduleOverlapMinutes:
                    latestAttempt?.schedule_overlap_minutes ?? null,
                scheduleOverlapRanges:
                    latestAttempt?.schedule_overlap_ranges ?? null,
                scheduleConflictingShifts:
                    latestAttempt?.schedule_conflicting_shifts ?? null,
                matchSignalsByShift,
                // Older API responses only identify the first client's miles.
                distance: isMultiClientOutreach
                    ? null
                    : (latestAttempt?.distance ?? null),
                clientDistances: latestAttempt?.client_distances,
            };
        });
    }, [
        contactAttempts,
        isMultiClientOutreach,
        outreach.id,
        shiftHoursById,
        shiftLabelById,
        relatedInboundCallsByCaregiver,
        filledShiftIds,
        cancelledShiftIds,
        assignedShiftIds,
    ]);

    const contactAttemptsById = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return new Map<string, ContactAttempt>();
        }

        return new Map(
            contactAttempts.map((attempt) => [attempt.id, attempt] as const),
        );
    }, [contactAttempts]);

    // Extract voice call IDs that have S3 recordings for batch fetching.
    // Include both caregiver and client notification attempts.
    const voiceCallIdsForRecordings = useMemo(() => {
        if (
            (!contactAttempts || contactAttempts.length === 0) &&
            (!relatedInboundCalls || relatedInboundCalls.length === 0) &&
            (!clientContactAttempts || clientContactAttempts.length === 0)
        ) {
            return null;
        }

        const ids = new Set<string>();
        const allAttempts = [
            ...(contactAttempts ?? []),
            ...(relatedInboundCalls ?? []),
            ...(clientContactAttempts ?? []),
        ];

        for (const attempt of allAttempts) {
            const voiceCall = attempt.voice_call;
            if (!voiceCall?.id || !voiceCall.recording_url) {
                continue;
            }
            if (isS3RecordingUrl(voiceCall.recording_url)) {
                ids.add(voiceCall.id);
            }
        }

        return ids.size > 0 ? Array.from(ids) : null;
    }, [contactAttempts, relatedInboundCalls, clientContactAttempts]);

    const displayStatus = useMemo(
        () => getDisplayOutreachStatus(outreach.status, contactAttempts),
        [outreach.status, contactAttempts],
    );
    const showContactAttemptHistory = isOutreachStarted(outreach.status);

    const { data: recordingUrls } = useVoiceCallRecordingUrls(
        organizationId,
        voiceCallIdsForRecordings,
    );

    const handleShiftResponseChange = useCallback(
        async ({
            contactAttemptId,
            shiftId,
            response,
        }: {
            caregiverId: string;
            contactAttemptId: string;
            shiftId: string;
            response: CaregiverResponseType | null;
            previousResponse?: CaregiverResponseType | null;
        }) => {
            if (!organizationId || !contactAttempts) {
                toast.error('Unable to update shift response.', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                return;
            }

            // Optimistically update the contact attempts cache
            const optimisticData = contactAttempts.map((attempt) => {
                if (attempt.id !== contactAttemptId) {
                    return attempt;
                }
                // Build new shift_responses, removing the key if response is null
                const newShiftResponses = { ...attempt.shift_responses };
                if (response === null) {
                    delete newShiftResponses[shiftId];
                } else {
                    newShiftResponses[shiftId] = response;
                }
                return {
                    ...attempt,
                    shift_responses: newShiftResponses,
                };
            });

            // Optimistically update
            await mutateContactAttempts(optimisticData, false);

            try {
                const result = await getApiClient().patch({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/contact-attempts/{contact_attempt_id}/shift-response',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                        contact_attempt_id: contactAttemptId,
                    },
                    body: {
                        updates: [
                            {
                                shift_id: shiftId,
                                response,
                            },
                        ],
                    },
                });

                // Build shift_responses map from API response
                const serverShiftResponses: Record<
                    string,
                    CaregiverResponseType
                > = {};
                for (const sr of result.shift_responses) {
                    serverShiftResponses[sr.shift_id] = sr.response;
                }

                // Update cache with server response
                const updatedData = contactAttempts.map((attempt) =>
                    attempt.id === contactAttemptId
                        ? { ...attempt, shift_responses: serverShiftResponses }
                        : attempt,
                );
                await mutateContactAttempts(updatedData, false);
            } catch (error) {
                console.error('Failed to update shift response', error);
                if (!isTransparentRateLimitError(error)) {
                    toast.error('Failed to update shift response.', {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                }
                // Rollback on error
                await mutateContactAttempts();
            }
        },
        [outreach.id, mutateContactAttempts, organizationId, contactAttempts],
    );

    const selectedCaregiverGroup = useMemo(() => {
        if (!selectedContactAttemptId) {
            return null;
        }

        const selectedAttempt = contactAttemptsById.get(
            selectedContactAttemptId,
        );

        if (!selectedAttempt) {
            return null;
        }

        return (
            contactAttemptGroups.find(
                (group) => group.caregiver.id === selectedAttempt.caregiver_id,
            ) ?? null
        );
    }, [contactAttemptGroups, contactAttemptsById, selectedContactAttemptId]);

    const openContactAttemptModal = useCallback(
        (contactAttemptId: string) => {
            navigate({
                to: '/outreaches/$callout-id',
                params: { 'callout-id': outreach.id },
                search: (previousSearch) => ({
                    organizationId: previousSearch.organizationId,
                    mode:
                        previousSearch.mode === 'live' ||
                        previousSearch.mode === 'sandbox'
                            ? previousSearch.mode
                            : undefined,
                    contactAttemptId,
                }),
            });
        },
        [outreach.id, navigate],
    );

    const closeContactAttemptModal = useCallback(() => {
        navigate({
            to: '/outreaches/$callout-id',
            params: { 'callout-id': outreach.id },
            search: (previousSearch) => ({
                organizationId: previousSearch.organizationId,
                mode:
                    previousSearch.mode === 'live' ||
                    previousSearch.mode === 'sandbox'
                        ? previousSearch.mode
                        : undefined,
            }),
        });
    }, [outreach.id, navigate]);

    useEffect(() => {
        if (
            selectedContactAttemptId &&
            !isLoadingContactAttempts &&
            contactAttempts &&
            !contactAttemptsById.has(selectedContactAttemptId)
        ) {
            closeContactAttemptModal();
        }
    }, [
        closeContactAttemptModal,
        contactAttempts,
        contactAttemptsById,
        isLoadingContactAttempts,
        selectedContactAttemptId,
    ]);

    const [isCallingCaregiver, setIsCallingCaregiver] = useState<string | null>(
        null,
    );
    const [isStartingSelectedCaregivers, setIsStartingSelectedCaregivers] =
        useState(false);
    const [deletingContactAttemptId, setDeletingContactAttemptId] = useState<
        string | null
    >(null);
    const [showAddCaregiversModal, setShowAddCaregiversModal] = useState(false);
    const [showClientNotificationModal, setShowClientNotificationModal] =
        useState(false);
    const [clientDetailClientId, setClientDetailClientId] = useState<
        string | null
    >(null);
    const [showPendingMessagesDialog, setShowPendingMessagesDialog] =
        useState(false);
    const [assignAndFillModalState, setAssignAndFillModalState] = useState<{
        isOpen: boolean;
        caregiver: OrgCaregiverRecord | null;
        assignmentType: ShiftAssignmentType;
        rateName: string | null;
        entryPoint: AssignAndFillEntryPoint | null;
        interestedShiftIds: Array<string>;
        wellskyOverrideReview: ContactAttempt['wellsky_override_review'] | null;
        hhaexchangeOvertimeReview:
            | ContactAttempt['hhaexchange_overtime_review']
            | null;
        scheduleOverlapMinutes: number | null;
        scheduleOverlapRanges: ReadonlyArray<ScheduleOverlapRange> | null;
        scheduleConflictingShifts: ReadonlyArray<ScheduleConflictingShift> | null;
    }>({
        isOpen: false,
        caregiver: null,
        assignmentType: 'single',
        rateName: null,
        entryPoint: null,
        interestedShiftIds: [],
        wellskyOverrideReview: null,
        hhaexchangeOvertimeReview: null,
        scheduleOverlapMinutes: null,
        scheduleOverlapRanges: null,
        scheduleConflictingShifts: null,
    });
    const [showEditShiftModal, setShowEditShiftModal] = useState(false);
    const [showEditOutreachModal, setShowEditOutreachModal] = useState(false);
    const [mobileShiftDetailsId, setMobileShiftDetailsId] = useState<
        string | null
    >(null);
    const [showAddShiftsDialog, setShowAddShiftsDialog] = useState(false);
    const [showStepsPreviewModal, setShowStepsPreviewModal] = useState(false);
    const [showSubscribersModal, setShowSubscribersModal] = useState(false);
    const [showInstructionsModal, setShowInstructionsModal] = useState(false);
    const [isEditingInstructions, setIsEditingInstructions] = useState(false);
    const [editedInstructions, setEditedInstructions] = useState('');
    const [isSavingInstructions, setIsSavingInstructions] = useState(false);
    const [isGeneratingInstructions, setIsGeneratingInstructions] =
        useState(false);
    const instructionsLoadingStatusRef = useRef<HTMLDivElement>(null);
    const [isTogglingOutreach, setIsTogglingOutreach] = useState(false);
    const [shiftToCancel, setShiftToCancel] = useState<{
        ids: Array<string>;
        targetLabel: string;
    } | null>(null);
    const isAgentBackedOutreach =
        outreach.agent_status !== null && outreach.agent_status !== undefined;
    const coordinatorPicksCaregivers = isCoordinatorPickedOutreach(outreach);
    const isChatBackedOutreach =
        isAgentBackedOutreach || outreach.agent_generation === 'v3';
    const isAgentPickingCaregivers =
        isAgentBackedOutreach && !coordinatorPicksCaregivers;
    const suggestedCaregivers: NonNullable<
        JoinedOutreachRead['suggested_caregivers']
    > = useMemo(
        () => outreach.suggested_caregivers ?? [],
        [outreach.suggested_caregivers],
    );
    const isOutreachChatLoading = outreach.agent_status === 'analyzing';
    // True only while a primary agent run is actively executing — not merely
    // "analyzing" (which is also reported for an agent outreach with no run
    // yet). Gate manual people-adding while this is true so the user and the
    // agent don't race; the backend also rejects these writes with a 409.
    const isAgentAddingPeople = outreach.agent_is_actively_running === true;
    const displayUnits = useMemo<Array<OutreachDisplayUnit<OutreachShift>>>(
        () => buildOutreachDisplayUnits(outreach.shifts, activeShiftGroups),
        [outreach.shifts, activeShiftGroups],
    );
    // Derive each unit's suggested caregiver IDs and reasoning map once, keyed
    // by unit id, from the memoized `displayUnits`/`suggestedCaregivers`. This
    // gives InhouseRecommendationsTable stable `preSelectedCaregiverIds` and
    // `agentReasoningMap` identities instead of the fresh arrays/maps a render
    // loop would otherwise build every render (WEB-CS).
    const unitAgentRecommendationsById = useMemo(() => {
        const byUnitId = new Map<
            string,
            {
                caregiverIds: Array<string>;
                reasoningMap: ReadonlyMap<string, string>;
            }
        >();
        for (const unit of displayUnits) {
            const unitShiftIdSet = new Set(unit.shiftIds);
            const unitSuggestions = suggestedCaregivers.filter((suggestion) =>
                suggestion.covered_shift_ids.some((shiftId) =>
                    unitShiftIdSet.has(shiftId),
                ),
            );
            byUnitId.set(unit.id, {
                caregiverIds: Array.from(
                    new Set(unitSuggestions.map((s) => s.caregiver_id)),
                ),
                reasoningMap: new Map(
                    unitSuggestions.map((s) => [s.caregiver_id, s.reasoning]),
                ),
            });
        }
        return byUnitId;
    }, [displayUnits, suggestedCaregivers]);
    const outreachDismissedCaregiversQuery = useOutreachDismissedCaregivers(
        organizationId,
        isAgentBackedOutreach ? outreach.id : null,
        { reason: 'callout-detail' },
    );
    const hasLoadedOutreachDismissedCaregivers =
        outreachDismissedCaregiversQuery.data !== undefined ||
        outreachDismissedCaregiversQuery.error !== undefined;
    // Unchecking strips a suggestion; its dismissal keeps the unit's table.
    const unitKeysWithDismissals = useMemo(
        () =>
            new Set(
                (outreachDismissedCaregiversQuery.data?.units ?? [])
                    .filter((unit) => unit.dismissed_caregivers.length > 0)
                    .map((unit) => `${unit.unit_type}:${unit.unit_id}`),
            ),
        [outreachDismissedCaregiversQuery.data],
    );
    const initialRecommendationUnitId = useMemo(() => {
        const suggestions = outreach.suggested_caregivers ?? [];
        if (suggestions.length > 0) {
            const coveredShiftIds = new Set<string>(
                suggestions.flatMap(
                    (suggestion) => suggestion.covered_shift_ids,
                ),
            );
            const unitWithSuggestions = displayUnits.find((unit) =>
                unit.shiftIds.some((shiftId) => coveredShiftIds.has(shiftId)),
            );
            if (unitWithSuggestions) {
                return unitWithSuggestions.id;
            }
        }
        return displayUnits[0]?.id;
    }, [displayUnits, outreach.suggested_caregivers]);
    const [selectedRecommendationUnitId, setSelectedRecommendationUnitId] =
        useState<string | undefined>(undefined);
    const [openRecommendationUnitIds, setOpenRecommendationUnitIds] = useState<
        Set<string>
    >(() => new Set());
    const autoOpenedUnitIdRef = useRef<string | undefined>(undefined);
    const recommendationsSectionRef = useRef<HTMLDivElement | null>(null);
    const autoScrolledOutreachIdRef = useRef<string | undefined>(undefined);
    const activeRecommendationUnitId =
        selectedRecommendationUnitId ?? initialRecommendationUnitId;
    const recommendationAccordionId = useId();
    useEffect(() => {
        // The recommendations live below the metadata fold and user tests
        // showed coordinators never found them unaided, so land the page on
        // them. A single scroll on mount clamps against the not-yet-grown
        // page (the tables stream in after mount), so keep re-anchoring
        // while the section grows and stop on user scroll or after a beat.
        // Anchor once per outreach so later revalidations don't yank the
        // viewport back down after the user scrolls away.
        if (outreach.status !== 'pending' || outreach.shifts.length === 0) {
            return;
        }
        if (autoScrolledOutreachIdRef.current === outreach.id) {
            return;
        }
        const section = recommendationsSectionRef.current;
        if (!section) {
            return;
        }
        autoScrolledOutreachIdRef.current = outreach.id;
        let cancelled = false;
        const anchor = () => {
            if (!cancelled) {
                section.scrollIntoView({ block: 'start' });
            }
        };
        const resizeObserver = new ResizeObserver(anchor);
        const stop = () => {
            cancelled = true;
            resizeObserver.disconnect();
            window.clearTimeout(stopTimer);
            window.removeEventListener('wheel', stop);
            window.removeEventListener('touchmove', stop);
        };
        const ANCHOR_WINDOW_MS = 4000;
        const stopTimer = window.setTimeout(stop, ANCHOR_WINDOW_MS);
        anchor();
        resizeObserver.observe(section);
        window.addEventListener('wheel', stop, { passive: true });
        window.addEventListener('touchmove', stop, { passive: true });
        return stop;
    }, [outreach.status, outreach.shifts.length, outreach.id]);
    useEffect(() => {
        if (
            selectedRecommendationUnitId &&
            !displayUnits.some(
                (unit) => unit.id === selectedRecommendationUnitId,
            )
        ) {
            setSelectedRecommendationUnitId(undefined);
        }
    }, [displayUnits, selectedRecommendationUnitId]);
    useEffect(() => {
        // The initial unit (the agent-suggested one, else the first) starts
        // expanded, but only once per unit so a user collapse survives
        // revalidations that rebuild displayUnits.
        const autoOpenUnitId = initialRecommendationUnitId;
        const shouldAutoOpenUnit =
            autoOpenUnitId !== undefined &&
            autoOpenedUnitIdRef.current !== autoOpenUnitId;
        if (shouldAutoOpenUnit) {
            autoOpenedUnitIdRef.current = autoOpenUnitId;
        }
        setOpenRecommendationUnitIds((currentOpenUnitIds) => {
            const availableUnitIds = new Set(
                displayUnits.map((unit) => unit.id),
            );
            const nextOpenUnitIds = new Set(
                [...currentOpenUnitIds].filter((unitId) =>
                    availableUnitIds.has(unitId),
                ),
            );
            if (shouldAutoOpenUnit) {
                nextOpenUnitIds.add(autoOpenUnitId);
            }
            if (
                nextOpenUnitIds.size === currentOpenUnitIds.size &&
                [...nextOpenUnitIds].every((unitId) =>
                    currentOpenUnitIds.has(unitId),
                )
            ) {
                return currentOpenUnitIds;
            }
            return nextOpenUnitIds;
        });
    }, [displayUnits, initialRecommendationUnitId]);
    const handleRecommendationUnitToggle = useCallback((unitId: string) => {
        setSelectedRecommendationUnitId(unitId);
        setOpenRecommendationUnitIds((currentOpenUnitIds) => {
            const nextOpenUnitIds = new Set(currentOpenUnitIds);
            if (nextOpenUnitIds.has(unitId)) {
                nextOpenUnitIds.delete(unitId);
            } else {
                nextOpenUnitIds.add(unitId);
            }
            return nextOpenUnitIds;
        });
    }, []);
    const [manualFallbackShiftIds, setManualFallbackShiftIds] = useState<
        Set<string>
    >(new Set());
    const initialSuggestedCaregiverIdsByShift =
        buildSuggestedCaregiverIdsByShift({
            shifts: outreach.shifts,
            suggestions: suggestedCaregivers,
        });
    const seenSuggestedCaregiverIdsByShiftRef = useRef(
        initialSuggestedCaregiverIdsByShift,
    );
    const [selectedCaregiverIdsByShift, setSelectedCaregiverIdsByShift] =
        useState<Record<string, Array<string>>>(
            initialSuggestedCaregiverIdsByShift,
        );
    const { handleSkipListRefusal, skipListDialog } = useSkipListRetry<{
        readonly kind: 'start' | 'confirm_suggestions';
        readonly caregiverNamesById?: Readonly<Record<string, string>>;
        readonly acknowledgedConflicts: Record<string, Array<string>>;
    }>({
        setSelectedCaregiverIdsByShift,
        resubmit: (caregiverShiftIds, { kind, ...retry }): Promise<boolean> =>
            (kind === 'start'
                ? submitStartOutreach
                : submitConfirmSuggestionsAsOutreach)(
                caregiverShiftIds,
                retry.caregiverNamesById,
                retry.acknowledgedConflicts,
            ),
    });
    const [selectedCaregiverNameById, setSelectedCaregiverNameById] = useState<
        Record<string, string>
    >(() =>
        Object.fromEntries(
            suggestedCaregivers.map((suggestion) => [
                suggestion.caregiver_id,
                formatName(suggestion.caregiver_name),
            ]),
        ),
    );
    const activeShiftGroupsForStart = initialShiftGroups;
    useEffect(() => {
        const nextSuggestedCaregiverIdsByShift =
            buildSuggestedCaregiverIdsByShift({
                shifts: outreach.shifts,
                suggestions: suggestedCaregivers,
            });
        const seenSuggestedCaregiverIdsByShift =
            seenSuggestedCaregiverIdsByShiftRef.current;

        setSelectedCaregiverIdsByShift((previousSelected) => {
            let changed = false;
            const nextSelected = { ...previousSelected };

            for (const shift of outreach.shifts) {
                const shiftId = shift.id;
                const seenSuggestedIds = new Set(
                    seenSuggestedCaregiverIdsByShift[shiftId] ?? [],
                );
                const selectedIds = new Set(nextSelected[shiftId] ?? []);
                const newlySuggestedIds = (
                    nextSuggestedCaregiverIdsByShift[shiftId] ?? []
                ).filter(
                    (caregiverId) =>
                        !seenSuggestedIds.has(caregiverId) &&
                        !selectedIds.has(caregiverId),
                );
                if (newlySuggestedIds.length === 0) {
                    continue;
                }
                nextSelected[shiftId] = normalizeSelectedCaregiverIds([
                    ...(nextSelected[shiftId] ?? []),
                    ...newlySuggestedIds,
                ]);
                changed = true;
            }

            return changed ? nextSelected : previousSelected;
        });
        const nextSeenSuggestedCaregiverIdsByShift = {
            ...seenSuggestedCaregiverIdsByShift,
        };
        for (const shift of outreach.shifts) {
            const shiftId = shift.id;
            nextSeenSuggestedCaregiverIdsByShift[shiftId] =
                normalizeSelectedCaregiverIds([
                    ...(nextSeenSuggestedCaregiverIdsByShift[shiftId] ?? []),
                    ...(nextSuggestedCaregiverIdsByShift[shiftId] ?? []),
                ]);
        }
        seenSuggestedCaregiverIdsByShiftRef.current =
            nextSeenSuggestedCaregiverIdsByShift;
    }, [suggestedCaregivers, outreach.shifts]);

    const { checkForWarnings, warningDialogProps } = useGroupSelectionWarning({
        shifts: outreach.shifts,
        shiftGroups: activeShiftGroupsForStart,
        shiftLabelById,
        sortByLastName,
    });
    const [useSameCaregiversAcrossShifts, setUseSameCaregiversAcrossShifts] =
        useState(false);
    // Latched on the first sync-toggle check and never reset: once parent
    // state has been the sync source of truth, the tables must keep
    // receiving it. Dropping the prop back to undefined would make the
    // tables' external-selection delta effect remove every previously
    // synced id. Reload clears the latch, preserving localStorage restore.
    const [hasEverSyncedAcrossShifts, setHasEverSyncedAcrossShifts] =
        useState(false);
    const sameCaregiversSourceIdsRef = useRef<Array<string>>([]);
    // Toggling "same caregivers for all shifts" on: take the active unit's
    // selection as the source of truth, then fetch each other shift's
    // eligible caregivers and union in any that match the source set.
    const handleSameCaregiversAcrossShiftsToggle = useCallback(
        (
            isChecked: boolean,
            activeDisplayUnit: OutreachDisplayUnit<OutreachShift>,
        ) => {
            setUseSameCaregiversAcrossShifts(isChecked);
            if (isChecked) {
                setHasEverSyncedAcrossShifts(true);
            }
            if (!isChecked || !organizationId) {
                return;
            }
            const sourceIds = normalizeSelectedCaregiverIds(
                activeDisplayUnit.shiftIds.flatMap(
                    (shiftId) => selectedCaregiverIdsByShift[shiftId] ?? [],
                ),
            );
            sameCaregiversSourceIdsRef.current = sourceIds;
            if (sourceIds.length === 0) {
                // Keep the parent payload aligned with the empty selection
                // displayed by every controlled table.
                setSelectedCaregiverIdsByShift(
                    Object.fromEntries(
                        outreach.shifts.map((shift) => [shift.id, []]),
                    ),
                );
                return;
            }
            const sourceSet = new Set(sourceIds);
            void (async () => {
                const next: Record<string, Array<string>> = {
                    ...selectedCaregiverIdsByShift,
                };
                await Promise.all(
                    outreach.shifts.map(async (shift) => {
                        if (
                            activeDisplayUnit.kind === 'shift' &&
                            activeDisplayUnit.shiftIds.includes(shift.id)
                        ) {
                            return;
                        }
                        try {
                            const ids = await fetchCaregiverIdsForShift(
                                organizationId,
                                shift.id,
                            );
                            const matched = ids.filter((id) =>
                                sourceSet.has(id),
                            );
                            const existing = next[shift.id] ?? [];
                            next[shift.id] = normalizeSelectedCaregiverIds([
                                ...existing,
                                ...matched,
                            ]);
                        } catch {
                            // Keep existing selections on error
                        }
                    }),
                );
                setSelectedCaregiverIdsByShift(next);
            })();
        },
        [organizationId, outreach.shifts, selectedCaregiverIdsByShift],
    );
    const [notifyClientByShift, setNotifyClientByShift] = useState<
        Record<string, boolean>
    >(() => {
        const metadata = outreach.outreach_metadata as {
            notify_client_on_fill?: Record<string, boolean>;
        } | null;
        return metadata?.notify_client_on_fill ?? {};
    });
    const handleToggleNotifyClient = useCallback(
        async (shiftId: string, checked: boolean) => {
            if (!organizationId) {
                return;
            }
            const updated = { ...notifyClientByShift, [shiftId]: checked };
            setNotifyClientByShift(updated);
            try {
                await getApiClient().patch({
                    url: '/organizations/{organization_id}/callouts/{callout_id}',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                    },
                    body: { notify_client_on_fill: updated },
                });
            } catch (error) {
                setNotifyClientByShift((prev) => ({
                    ...prev,
                    [shiftId]: !checked,
                }));
                if (!isTransparentRateLimitError(error)) {
                    toast.error(
                        'Failed to update client notification setting',
                        {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                }
            }
        },
        [organizationId, outreach.id, notifyClientByShift],
    );
    const handleAutoAssignSave = useCallback(
        async (autoAssignShifts: ReadonlyArray<string>) => {
            if (!organizationId) {
                return;
            }

            setIsUpdatingAutoAssignFirstResponder(true);
            try {
                await getApiClient().patch({
                    url: '/organizations/{organization_id}/callouts/{callout_id}',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                    },
                    body: {
                        auto_assign_shifts: [...autoAssignShifts],
                    },
                });
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    toast.error('Failed to save auto-assign settings', {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                }
                console.error('Error saving auto-assign settings:', error);
                throw error;
            } finally {
                setIsUpdatingAutoAssignFirstResponder(false);
            }
            try {
                await mutateOutreach();
            } catch (error) {
                console.error(
                    'Error refreshing outreach after auto-assign save:',
                    error,
                );
            }
        },
        [organizationId, outreach.id, mutateOutreach],
    );
    const autoAssignEdits = useOutreachAutoAssignEdits({
        outreach,
        isUpdating: isUpdatingAutoAssignFirstResponder,
        onSave: handleAutoAssignSave,
    });
    const autoGeneratedInstructionsKeyRef = useRef<string | null>(null);
    const [isGeneratingCarePlan, setIsGeneratingCarePlan] = useState(false);
    const autoGeneratedCarePlanKeyRef = useRef<string | null>(null);
    const throttledRefreshOutreach = useThrottledCallback(() => {
        void mutateOutreach();
    }, OUTREACH_REFRESH_THROTTLE_DELAY_MS);

    useListenForOrganizationEvent('outreach_suggestion_added', (event) => {
        if (
            event.resource.type === 'outreach' &&
            event.resource.id === outreach.id
        ) {
            void mutateOutreach();
        }
    });
    useListenForOrganizationEvent('outreach_suggestion_removed', (event) => {
        if (
            event.resource.type === 'outreach' &&
            event.resource.id === outreach.id
        ) {
            void mutateOutreach();
        }
    });
    useListenForOrganizationEvent('agent_conversation_updated', (event) => {
        if (
            event.relationships.some(
                (relationship) =>
                    relationship.type === 'outreach' &&
                    relationship.id === outreach.id,
            )
        ) {
            throttledRefreshOutreach();
        }
    });
    useListenForOrganizationEvent('agent_run_stream_event', (event) => {
        if (
            isStateChangingAgentStreamEvent(event) &&
            event.relationships.some(
                (relationship) =>
                    relationship.type === 'outreach' &&
                    relationship.id === outreach.id,
            )
        ) {
            throttledRefreshOutreach();
        }
    });
    useListenForOrganizationEvent('contact_attempt_updated', (event) => {
        if (!eventReferencesOutreach(event, outreach.id)) {
            return;
        }
        throttledRefreshOutreach();
    });

    // Get the pending-message count for this outreach.
    const {
        data: pendingMessagesCount,
        isLoading: isLoadingPendingMessagesCount,
    } = useOrganizationPendingMessagesCountRealtime('callout-detail', {
        organizationId,
        domain: 'callout',
        domainId: outreach.id,
    });

    const responseSummary = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return null;
        }

        // Group contact attempts by caregiver
        const caregiverResponseMap = new Map<
            string,
            CaregiverResponseType | null
        >();

        contactAttempts.forEach((attempt) => {
            const caregiverId = attempt.caregiver_id;
            const currentResponse = caregiverResponseMap.get(caregiverId);

            // If we don't have a response for this caregiver yet, or if the new response has higher priority
            if (
                !currentResponse ||
                getOverallCaregiverResponse([attempt]) !== null
            ) {
                // Get all attempts for this caregiver
                const caregiverAttempts = contactAttempts.filter(
                    (a) => a.caregiver_id === caregiverId,
                );
                const overallResponse =
                    getOverallCaregiverResponse(caregiverAttempts);
                caregiverResponseMap.set(caregiverId, overallResponse);
            }
        });

        // Count responses by type
        const counts = {
            total: caregiverResponseMap.size,
            available_interested: 0,
            available_not_interested: 0,
            unavailable: 0,
            pending: 0,
            no_response: 0,
        };

        caregiverResponseMap.forEach((response) => {
            if (response === 'available_interested') {
                counts.available_interested++;
            } else if (
                response === 'available_not_interested' ||
                response === 'not_a_fit'
            ) {
                counts.available_not_interested++;
            } else if (response === 'unavailable') {
                counts.unavailable++;
            } else if (response === 'pending') {
                counts.pending++;
            } else if (response === 'no_response') {
                counts.no_response++;
            }
        });

        return counts;
    }, [contactAttempts]);

    // Extract list of caregiver IDs that have already been contacted
    const contactedCaregiverIds = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return [];
        }

        // Get unique caregiver IDs from contact attempts
        const uniqueCaregiverIds = new Set<string>();
        contactAttempts.forEach((attempt) => {
            uniqueCaregiverIds.add(attempt.caregiver_id);
        });

        return Array.from(uniqueCaregiverIds);
    }, [contactAttempts]);

    // Map shift IDs to a color index for group badges
    const shiftGroupByShiftId = useMemo(() => {
        const result = new Map<string, number>();

        // New format: shift_groups from API (shift_groups table)
        if (activeShiftGroups.length > 0) {
            let colorIndex = 0;
            for (const group of activeShiftGroups) {
                if (group.shift_ids.length <= 1) {
                    continue;
                }
                for (const id of group.shift_ids) {
                    result.set(id, colorIndex);
                }
                colorIndex++;
            }
            return result;
        }

        // Old format: metadata['shift_groups'] is a list of client
        // keys where all-or-nothing is enabled
        const legacyKeys = outreach?.outreach_metadata?.['shift_groups'] as
            | Array<string>
            | undefined;
        if (!legacyKeys || legacyKeys.length === 0 || !outreach?.shifts) {
            return result;
        }
        const keySet = new Set(legacyKeys);
        const grouped = new Map<string, Array<string>>();
        for (const shift of outreach.shifts) {
            const key =
                shift.client_id ||
                (shift.patient_name || '').trim() ||
                'Unknown';
            const existing = grouped.get(key);
            if (existing) {
                existing.push(shift.id);
            } else {
                grouped.set(key, [shift.id]);
            }
        }
        let colorIndex = 0;
        for (const [key, shiftIds] of grouped) {
            if (!keySet.has(key) || shiftIds.length <= 1) {
                continue;
            }
            for (const id of shiftIds) {
                result.set(id, colorIndex);
            }
            colorIndex++;
        }
        return result;
    }, [activeShiftGroups, outreach?.outreach_metadata, outreach?.shifts]);
    const latestClientContactAttempts = useMemo(() => {
        if (!clientContactAttempts || clientContactAttempts.length === 0) {
            return [] as Array<ClientContactAttemptRead>;
        }

        const attemptsByClientId = new Map<string, ClientContactAttemptRead>();
        for (const attempt of clientContactAttempts) {
            const existingAttempt = attemptsByClientId.get(attempt.client_id);
            if (!existingAttempt) {
                attemptsByClientId.set(attempt.client_id, attempt);
                continue;
            }

            if (isNewerClientAttempt(attempt, existingAttempt)) {
                attemptsByClientId.set(attempt.client_id, attempt);
            }
        }

        return Array.from(attemptsByClientId.values()).sort((a, b) =>
            comparePersonNames(a.client_name, b.client_name, sortByLastName),
        );
    }, [clientContactAttempts, sortByLastName]);

    // Only worth asking when the playbook actually escalates to voice — a
    // text-only outreach reads the same either way, so a pure-SMS agency never
    // pays for the lookup.
    const hasVoiceStep = (outreach.steps ?? []).some(
        (step) => step.step_type === 'voice',
    );
    const canPlaceCalls = useCanPlaceCalls(
        hasVoiceStep ? organizationId : null,
    );
    const outreachSequence = useMemo(
        () => getOutreachSequence(outreach.steps ?? [], { canPlaceCalls }),
        [outreach.steps, canPlaceCalls],
    );
    // The selection bar becomes the report rather than a toast beside it: one
    // element the coordinator is already looking at, so nothing overlaps the
    // button they just pressed and no extra click is added to the product's
    // busiest path. The report lives outside React so it outlives anything
    // that remounts this view mid-send.
    const startReport = useStartReport(outreach.id);
    const reportStart = useCallback(
        (next: { readonly status: StartReportStatus; readonly text: string }) =>
            publishStartReport({ ...next, outreachId: outreach.id }),
        [outreach.id],
    );

    const submitStartOutreach = useCallback(
        async (
            caregiverShiftIds: CaregiverShiftIds,
            caregiverNamesById?: Readonly<Record<string, string>>,
            acknowledgedConflicts: Record<string, Array<string>> = {},
        ): Promise<boolean> => {
            if (!outreach || !organizationId) {
                return false;
            }

            if (Object.keys(caregiverShiftIds).length === 0) {
                toast.error('Select at least one caregiver', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                return false;
            }

            setOutreachConflictError(null);
            reportStart({
                status: 'sending',
                text: getOutreachStartedReport({
                    caregiverCount: Object.keys(caregiverShiftIds).length,
                    sequence: outreachSequence,
                }),
            });
            const startTimeout = createStartOutreachTimeout();
            try {
                const response = await getApiClient().post({
                    url: '/organizations/{organization_id}/outreaches/{outreach_id}/start-outreach',
                    pathParams: {
                        organization_id: organizationId,
                        outreach_id: outreach.id,
                    },
                    body: {
                        caregiver_shift_ids: caregiverShiftIds,
                        skip_conflict_check: false,
                        acknowledged_conflicts: acknowledgedConflicts,
                        suppress_agent_notification: false,
                    },
                    reason: 'callout-details-view',
                    signal: startTimeout.signal,
                });

                const droppedInactiveIds =
                    response?.dropped_inactive_caregiver_ids ?? [];
                const droppedNoValidShiftsIds =
                    response?.dropped_no_valid_shifts_caregiver_ids ?? [];
                const contactedCount =
                    Object.keys(caregiverShiftIds).length -
                    droppedInactiveIds.length -
                    droppedNoValidShiftsIds.length;
                const mergedNames: Readonly<Record<string, string>> = {
                    ...selectedCaregiverNameById,
                    ...(caregiverNamesById ?? {}),
                };
                if (
                    droppedInactiveIds.length > 0 ||
                    droppedNoValidShiftsIds.length > 0
                ) {
                    // Only the first emitted toast carries the
                    // "Outreach started;" preamble; the second starts
                    // with "Skipped" so the user doesn't read the
                    // same status twice in two consecutive toasts.
                    if (droppedInactiveIds.length > 0) {
                        toast.success(
                            `Skipped ${buildSkippedCaregiverLabel(
                                droppedInactiveIds,
                                mergedNames,
                            )} because they are inactive.`,
                            {
                                persistNotification: true,
                                notificationType: 'outreach_action',
                            },
                        );
                    }
                    if (droppedNoValidShiftsIds.length > 0) {
                        toast.warning(
                            `Skipped ${buildSkippedCaregiverLabel(
                                droppedNoValidShiftsIds,
                                mergedNames,
                            )}, whose shifts aren’t on this outreach.`,
                            {
                                persistNotification: true,
                                notificationType: 'outreach_action',
                            },
                        );
                    }
                }
                if (contactedCount > 0) {
                    reportStart({
                        status: 'started',
                        text: getOutreachStartedReport({
                            caregiverCount: contactedCount,
                            sequence: outreachSequence,
                        }),
                    });
                } else {
                    dismissStartReport();
                }
                await mutateOutreach();
                await mutateContactAttempts();
                setScheduleConflict(null);
                setConflictingStart(null);
                return true;
            } catch (error) {
                await mutateOutreach();
                if (error instanceof ApiError && error.isAbortError) {
                    reportStart({
                        status: 'failed',
                        text: STILL_STARTING_REPORT_TEXT,
                    });
                    return false;
                }
                if (isTransparentRateLimitError(error)) {
                    console.error('Error starting outreach:', error);
                    dismissStartReport();
                    throw error;
                }
                const scheduleConflict = parseOutreachScheduleConflict(error);
                if (scheduleConflict) {
                    dismissStartReport();
                    setScheduleConflict(scheduleConflict);
                    setConflictingStart({
                        kind: 'start',
                        caregiverShiftIds,
                        caregiverNamesById,
                    });
                    return false;
                }
                if (
                    handleSkipListRefusal(error, caregiverShiftIds, {
                        kind: 'start',
                        caregiverNamesById,
                        acknowledgedConflicts,
                    })
                ) {
                    dismissStartReport();
                    return false;
                }
                const knownError = parseKnownApiError(error);
                if (knownError?.kind === 'all_caregivers_inactive') {
                    dismissStartReport();
                    toast.error(
                        'Everyone selected is inactive. Pick at least one active caregiver.',
                        {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                    return false;
                }
                if (isAgentActive409(error)) {
                    // Agent started adding people mid-request; surface the
                    // server's reason instead of the conflict banner.
                    dismissStartReport();
                    toast.error((error as ApiError).message, {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                    return false;
                }
                console.error('Error starting outreach:', error);
                const conflict = parseOutreachConflictError(error);
                if (conflict) {
                    // The conflict banner is the report for this one.
                    dismissStartReport();
                    setOutreachConflictError(conflict);
                } else {
                    reportStart({
                        status: 'failed',
                        text: 'We couldn’t start the outreach. Try again.',
                    });
                }
                throw error;
            } finally {
                startTimeout.clear();
            }
        },
        [
            outreachSequence,
            reportStart,
            organizationId,
            outreach,
            mutateOutreach,
            mutateContactAttempts,
            selectedCaregiverNameById,
            handleSkipListRefusal,
        ],
    );
    const submitConfirmSuggestionsAsOutreach = useCallback(
        async (
            caregiverShiftIds: CaregiverShiftIds,
            caregiverNamesById?: Readonly<Record<string, string>>,
            acknowledgedConflicts: Record<string, Array<string>> = {},
        ): Promise<boolean> => {
            if (!organizationId) {
                return false;
            }

            if (Object.keys(caregiverShiftIds).length === 0) {
                toast.error('Select at least one caregiver', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                return false;
            }

            setOutreachConflictError(null);
            reportStart({
                status: 'sending',
                text: getOutreachStartedReport({
                    caregiverCount: Object.keys(caregiverShiftIds).length,
                    sequence: outreachSequence,
                }),
            });
            const startTimeout = createStartOutreachTimeout();
            try {
                const response = await getApiClient().post({
                    url: '/organizations/{organization_id}/outreaches/{outreach_id}/confirm-suggestions-v2',
                    pathParams: {
                        organization_id: organizationId,
                        outreach_id: outreach.id,
                    },
                    body: {
                        caregiver_shift_ids: caregiverShiftIds,
                        skip_conflict_check: false,
                        acknowledged_conflicts: acknowledgedConflicts,
                        // Page-level start: the outreach agent should still
                        // wake for coordinator starts made outside its chat.
                        suppress_agent_notification: false,
                    },
                    reason: 'callout-details-view',
                    signal: startTimeout.signal,
                });

                const droppedInactiveIds =
                    response?.dropped_inactive_caregiver_ids ?? [];
                const droppedNoValidShiftsIds =
                    response?.dropped_no_valid_shifts_caregiver_ids ?? [];
                const contactedCount =
                    Object.keys(caregiverShiftIds).length -
                    droppedInactiveIds.length -
                    droppedNoValidShiftsIds.length;
                const mergedNames: Readonly<Record<string, string>> = {
                    ...selectedCaregiverNameById,
                    ...(caregiverNamesById ?? {}),
                };
                if (
                    droppedInactiveIds.length > 0 ||
                    droppedNoValidShiftsIds.length > 0
                ) {
                    // Only the first emitted toast carries the
                    // "Outreach started;" preamble; the second starts
                    // with "Skipped" so the user doesn't read the
                    // same status twice in two consecutive toasts.
                    if (droppedInactiveIds.length > 0) {
                        toast.success(
                            `Skipped ${buildSkippedCaregiverLabel(
                                droppedInactiveIds,
                                mergedNames,
                            )} because they are inactive.`,
                            {
                                persistNotification: true,
                                notificationType: 'outreach_action',
                            },
                        );
                    }
                    if (droppedNoValidShiftsIds.length > 0) {
                        toast.warning(
                            `Skipped ${buildSkippedCaregiverLabel(
                                droppedNoValidShiftsIds,
                                mergedNames,
                            )}, whose shifts aren’t on this outreach.`,
                            {
                                persistNotification: true,
                                notificationType: 'outreach_action',
                            },
                        );
                    }
                }
                if (contactedCount > 0) {
                    reportStart({
                        status: 'started',
                        text: getOutreachStartedReport({
                            caregiverCount: contactedCount,
                            sequence: outreachSequence,
                        }),
                    });
                } else {
                    dismissStartReport();
                }
                await mutateOutreach();
                await mutateContactAttempts();
                setScheduleConflict(null);
                setConflictingStart(null);
                return true;
            } catch (error) {
                await mutateOutreach();
                if (error instanceof ApiError && error.isAbortError) {
                    reportStart({
                        status: 'failed',
                        text: STILL_STARTING_REPORT_TEXT,
                    });
                    return false;
                }
                if (isTransparentRateLimitError(error)) {
                    console.error(
                        'Error confirming outreach suggestions:',
                        error,
                    );
                    dismissStartReport();
                    throw error;
                }
                const scheduleConflict = parseOutreachScheduleConflict(error);
                if (scheduleConflict) {
                    dismissStartReport();
                    setScheduleConflict(scheduleConflict);
                    setConflictingStart({
                        kind: 'confirm_suggestions',
                        caregiverShiftIds,
                        caregiverNamesById,
                    });
                    return false;
                }
                if (
                    handleSkipListRefusal(error, caregiverShiftIds, {
                        kind: 'confirm_suggestions',
                        caregiverNamesById,
                        acknowledgedConflicts,
                    })
                ) {
                    dismissStartReport();
                    return false;
                }
                const knownError = parseKnownApiError(error);
                if (knownError?.kind === 'all_caregivers_inactive') {
                    dismissStartReport();
                    toast.error(
                        'Everyone selected is inactive. Pick at least one active caregiver.',
                        {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        },
                    );
                    return false;
                }
                if (isAgentActive409(error)) {
                    // Agent started adding people mid-request; surface the
                    // server's reason instead of the conflict banner.
                    dismissStartReport();
                    toast.error((error as ApiError).message, {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                    return false;
                }
                console.error('Error confirming outreach suggestions:', error);
                const conflict = parseOutreachConflictError(error);
                if (conflict) {
                    // The conflict banner is the report for this one.
                    dismissStartReport();
                    setOutreachConflictError(conflict);
                } else {
                    reportStart({
                        status: 'failed',
                        text: 'We couldn’t start the outreach. Try again.',
                    });
                }
                throw error;
            } finally {
                startTimeout.clear();
            }
        },
        [
            outreachSequence,
            reportStart,
            organizationId,
            outreach.id,
            mutateOutreach,
            mutateContactAttempts,
            selectedCaregiverNameById,
            handleSkipListRefusal,
        ],
    );
    const handleStartOutreach = useCallback(
        async (
            caregiverShiftIds?: CaregiverShiftIds,
            caregiverNameById?: CaregiverNamesById,
        ): Promise<boolean> => {
            const mergedNamesById = {
                ...selectedCaregiverNameById,
                ...(caregiverNameById ?? {}),
            };

            if (
                caregiverShiftIds &&
                checkForWarnings({
                    caregiverShiftIds,
                    caregiverNamesById: mergedNamesById,
                    onConfirm: async (confirmedIds) => {
                        await submitStartOutreach(
                            confirmedIds,
                            mergedNamesById,
                        );
                    },
                })
            ) {
                return false;
            }

            if (!caregiverShiftIds) {
                return false;
            }

            return submitStartOutreach(caregiverShiftIds, mergedNamesById);
        },
        [checkForWarnings, selectedCaregiverNameById, submitStartOutreach],
    );
    const handleConfirmSuggestionsAsOutreach = useCallback(
        async (
            caregiverShiftIds?: CaregiverShiftIds,
            caregiverNameById?: CaregiverNamesById,
        ): Promise<boolean> => {
            const mergedNamesById = {
                ...selectedCaregiverNameById,
                ...(caregiverNameById ?? {}),
            };

            if (
                caregiverShiftIds &&
                checkForWarnings({
                    caregiverShiftIds,
                    caregiverNamesById: mergedNamesById,
                    onConfirm: async (confirmedIds) => {
                        await submitConfirmSuggestionsAsOutreach(
                            confirmedIds,
                            mergedNamesById,
                        );
                    },
                })
            ) {
                return false;
            }

            if (!caregiverShiftIds) {
                return false;
            }

            return submitConfirmSuggestionsAsOutreach(
                caregiverShiftIds,
                mergedNamesById,
            );
        },
        [
            checkForWarnings,
            selectedCaregiverNameById,
            submitConfirmSuggestionsAsOutreach,
        ],
    );
    const handleCallAgain = useCallback(
        async (
            caregiverId: string,
            acknowledgedConflicts: Record<string, Array<string>> = {},
        ) => {
            if (!organizationId) {
                return;
            }

            setIsCallingCaregiver(caregiverId);
            try {
                await getApiClient().post({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/contact-attempts',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                    },
                    body: {
                        caregiver_id: caregiverId,
                        delivery_mode: 'voice',
                        acknowledged_conflicts: acknowledgedConflicts,
                    },
                });
                // Trigger a re-fetch of the contact attempts data
                mutateContactAttempts();
                setManualContactConflictOverride(null);
                toast.success('Voice call initiated successfully', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            } catch (error) {
                if (isTransparentRateLimitError(error)) {
                    console.error('Error creating contact attempt:', error);
                    return;
                }
                const conflict = parseManualContactConflict(error, caregiverId);
                if (conflict) {
                    setManualContactConflictOverride(conflict);
                    return;
                }
                const message =
                    error instanceof ApiError && error.status === 409
                        ? error.message
                        : 'Failed to initiate voice call';
                toast.error(message, {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                console.error('Error creating contact attempt:', error);
            } finally {
                setIsCallingCaregiver(null);
            }
        },
        [organizationId, outreach.id, mutateContactAttempts],
    );

    const handleRetryClientContactAttempt = useCallback(
        async (contactAttemptId: string) => {
            if (!organizationId) {
                return;
            }

            setRetryingClientContactAttemptId(contactAttemptId);
            try {
                await getApiClient().post({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/client-contact-attempts/{contact_attempt_id}/retry',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                        contact_attempt_id: contactAttemptId,
                    },
                });
                toast.success('Client notification retry queued', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                await mutateClientContactAttempts();
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    toast.error('Failed to retry client notification', {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                }
                console.error(
                    'Error retrying client notification contact attempt:',
                    error,
                );
            } finally {
                setRetryingClientContactAttemptId(null);
            }
        },
        [organizationId, outreach.id, mutateClientContactAttempts],
    );

    const handleMarkClientContactAttemptNotified = useCallback(
        async (contactAttemptId: string) => {
            if (!organizationId) {
                return;
            }

            setMarkingClientContactAttemptId(contactAttemptId);
            try {
                await getApiClient().post({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/client-contact-attempts/{contact_attempt_id}/mark-notified',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                        contact_attempt_id: contactAttemptId,
                    },
                });
                toast.success('Marked client as notified', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                await mutateClientContactAttempts();
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    toast.error('Failed to mark client as notified', {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                }
                console.error(
                    'Error marking client notification attempt as notified:',
                    error,
                );
            } finally {
                setMarkingClientContactAttemptId(null);
            }
        },
        [organizationId, outreach.id, mutateClientContactAttempts],
    );

    const clientNotificationEnabled = Boolean(
        organization?.features?.client_notification_enabled,
    );
    const isNotifyClientEnabledForShift = (shiftId: string): boolean => {
        if (notifyClientByShift[shiftId] !== undefined) {
            return notifyClientByShift[shiftId];
        }
        const shift = outreach.shifts.find((s) => s.id === shiftId);
        const client = shift?.client_id
            ? clientById.get(shift.client_id)
            : undefined;
        if (
            client?.default_notify_on_fill !== null &&
            client?.default_notify_on_fill !== undefined
        ) {
            return client.default_notify_on_fill;
        }
        return clientNotificationEnabled;
    };
    // Predicts the worker's decision for a delayed client notification that
    // has no contact attempt yet: it is dropped if the shift was unassigned
    // (e.g. in the EHR) or cancelled before the delay elapsed.
    const willQueueClientNotification = ({
        shiftId,
        assignedCaregiverId,
        filledInfo,
        isCancelled,
    }: {
        readonly shiftId: string;
        readonly assignedCaregiverId: string | null | undefined;
        readonly filledInfo: FilledShiftInfo | undefined;
        readonly isCancelled: boolean;
    }): boolean =>
        filledInfo !== undefined &&
        assignedCaregiverId === filledInfo.caregiver_id &&
        !isCancelled &&
        isNotifyClientEnabledForShift(shiftId);
    const showNotifyClientColumn = clientNotificationEnabled;
    const showClientNotificationStatusColumn =
        clientNotificationEnabled && isMultiShiftOutreach;
    const showEditShiftAction =
        !!organization?.features?.['edit_shift_enabled'] &&
        outreach.shifts.length === 1;

    const clientAttemptByClientId = useMemo(() => {
        const map = new Map<string, ClientContactAttemptRead>();
        for (const attempt of latestClientContactAttempts) {
            map.set(attempt.client_id, attempt);
        }
        return map;
    }, [latestClientContactAttempts]);

    const clientNotificationContent = useMemo(() => {
        const clientNotificationEnabled = Boolean(
            organization?.features?.client_notification_enabled,
        );

        if (
            !clientNotificationEnabled &&
            latestClientContactAttempts.length === 0
        ) {
            return undefined;
        }

        if (latestClientContactAttempts.length === 0) {
            return (
                <span className="text-muted-foreground text-sm">
                    Not started
                </span>
            );
        }

        return (
            <div className="space-y-2">
                {latestClientContactAttempts.map((attempt) => {
                    const maxClientContactRounds = 2;
                    const canRetry =
                        CLIENT_NOTIFICATION_RETRYABLE_STATUSES.has(
                            attempt.outreach_status,
                        ) && attempt.round < maxClientContactRounds - 1;
                    const isRetrying =
                        retryingClientContactAttemptId === attempt.id;
                    return (
                        <div
                            key={attempt.id}
                            className="flex flex-wrap items-center gap-2"
                        >
                            <div className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium">
                                    {formatName(attempt.client_name)}
                                </span>
                                {attempt.recipient_name &&
                                    attempt.recipient_name !==
                                        attempt.client_name && (
                                        <span className="text-muted-foreground text-xs">
                                            Contact: {attempt.recipient_name}
                                        </span>
                                    )}
                            </div>
                            <ContactAttemptStatusBadge
                                status={attempt.outreach_status}
                                deliveryMode={attempt.delivery_mode}
                                recipientType="client"
                                communicationDetails={
                                    attempt.communication_details
                                }
                            />
                            {canRetry && (
                                <button
                                    type="button"
                                    className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs transition-colors"
                                    disabled={isRetrying}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRetryClientContactAttempt(
                                            attempt.id,
                                        );
                                    }}
                                >
                                    {isRetrying ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <Phone className="h-3 w-3" />
                                    )}
                                    <span>Try again</span>
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }, [
        latestClientContactAttempts,
        organization?.features?.client_notification_enabled,
        retryingClientContactAttemptId,
        handleRetryClientContactAttempt,
        formatName,
    ]);

    const handleDeleteContactAttempt = useCallback(
        async (contactAttemptId: string) => {
            if (!organizationId || !isDevelopment) {
                return;
            }

            setDeletingContactAttemptId(contactAttemptId);
            try {
                await getApiClient().delete({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/contact-attempts/{contact_attempt_id}',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                        contact_attempt_id: contactAttemptId,
                    },
                });
                toast.success('Contact attempt deleted', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                await mutateContactAttempts();
            } catch (error) {
                if (!isTransparentRateLimitError(error)) {
                    toast.error('Failed to delete contact attempt', {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    });
                }
                console.error('Error deleting contact attempt:', error);
            } finally {
                setDeletingContactAttemptId(null);
            }
        },
        [organizationId, outreach.id, isDevelopment, mutateContactAttempts],
    );
    const handleOpenAssignAndFillModal = useCallback(
        (
            caregiver: OrgCaregiverRecord,
            interestedShiftIds: Array<string> = [],
            wellskyOverrideReview:
                | ContactAttempt['wellsky_override_review']
                | null = null,
            hhaexchangeOvertimeReview:
                | ContactAttempt['hhaexchange_overtime_review']
                | null = null,
            scheduleOverlapMinutes: number | null = null,
            scheduleOverlapRanges: ReadonlyArray<ScheduleOverlapRange> | null = null,
            scheduleConflictingShifts: ReadonlyArray<ScheduleConflictingShift> | null = null,
            entryPoint: AssignAndFillEntryPoint = 'caregiver_detail_modal',
        ) => {
            posthog?.capture(EVENTS.OUTREACH_ASSIGN_AND_FILL_OPENED, {
                caregiver_id: caregiver.id,
                entry_point: entryPoint,
                has_hhaexchange_overtime_review: Boolean(
                    hhaexchangeOvertimeReview,
                ),
                has_wellsky_override_review: Boolean(wellskyOverrideReview),
                interested_shift_count: interestedShiftIds.length,
                outreach_id: outreach.id,
                surface: 'callout_detail',
            });
            setAssignAndFillModalState({
                isOpen: true,
                caregiver,
                assignmentType: 'single',
                rateName: null,
                entryPoint,
                interestedShiftIds,
                wellskyOverrideReview,
                hhaexchangeOvertimeReview,
                scheduleOverlapMinutes,
                scheduleOverlapRanges,
                scheduleConflictingShifts,
            });
        },
        [outreach.id, posthog],
    );
    const handleReviewOverrideFromDetailModal = useCallback(() => {
        if (!selectedCaregiverGroup) {
            return;
        }

        closeContactAttemptModal();
        const resolvedShiftIds = new Set<string>([
            ...filledShiftIds,
            ...cancelledShiftIds,
        ]);
        handleOpenAssignAndFillModal(
            selectedCaregiverGroup.caregiver,
            getInterestedShiftIds(
                selectedCaregiverGroup.shiftResponses,
                resolvedShiftIds,
            ),
            selectedCaregiverGroup.wellskyOverrideReview ?? null,
            selectedCaregiverGroup.hhaexchangeOvertimeReview ?? null,
            selectedCaregiverGroup.scheduleOverlapMinutes ?? null,
            selectedCaregiverGroup.scheduleOverlapRanges ?? null,
            selectedCaregiverGroup.scheduleConflictingShifts ?? null,
            'caregiver_detail_modal',
        );
    }, [
        closeContactAttemptModal,
        handleOpenAssignAndFillModal,
        selectedCaregiverGroup,
        filledShiftIds,
        cancelledShiftIds,
    ]);

    const assignAndFillDeepLinkKey =
        outreachAction === 'assign_and_fill' ? outreach.id : null;
    useEffect(() => {
        if (!assignAndFillDeepLinkKey) {
            assignAndFillDeepLinkHandledKeyRef.current = null;
            return;
        }
        if (
            assignAndFillDeepLinkHandledKeyRef.current ===
                assignAndFillDeepLinkKey ||
            isLoadingContactAttempts ||
            !contactAttempts
        ) {
            return;
        }

        const resolvedShiftIds = new Set<string>([
            ...filledShiftIds,
            ...cancelledShiftIds,
        ]);
        const candidates = getAssignAndFillCandidates(
            contactAttemptGroups,
            resolvedShiftIds,
        );

        if (candidates.length !== 1) {
            if (candidates.length > 1) {
                assignAndFillDeepLinkHandledKeyRef.current =
                    assignAndFillDeepLinkKey;
            }
            return;
        }

        assignAndFillDeepLinkHandledKeyRef.current = assignAndFillDeepLinkKey;
        const candidate = candidates[0]!;
        handleOpenAssignAndFillModal(
            candidate.source.caregiver,
            candidate.interestedShiftIds,
            candidate.source.wellskyOverrideReview ?? null,
            candidate.source.hhaexchangeOvertimeReview ?? null,
            candidate.source.scheduleOverlapMinutes,
            candidate.source.scheduleOverlapRanges,
            candidate.source.scheduleConflictingShifts,
            'slack_mark_filled_deep_link',
        );
    }, [
        assignAndFillDeepLinkKey,
        cancelledShiftIds,
        contactAttemptGroups,
        contactAttempts,
        filledShiftIds,
        handleOpenAssignAndFillModal,
        isLoadingContactAttempts,
    ]);

    const agentInstructionsTemplate = agentInstructionsTemplates?.find(
        (template) => template.type === 'agent_instructions',
    )?.template;
    const instructions = (
        (outreach?.outreach_metadata['instructions'] as string) ?? ''
    ).trim();
    const hasAgentInstructions = instructions.length > 0;
    const shiftIdsForInstructions = useMemo(
        () => outreach.shifts.map((shift) => shift.id),
        [outreach.shifts],
    );
    const instructionsGenerationKey = `${outreach.id}:${shiftIdsForInstructions.join(',')}`;
    const isAgentInstructionsLoading =
        !hasAgentInstructions &&
        (isGeneratingInstructions || isLoadingAgentInstructionsTemplate);

    const mode = useMode();
    const outreachSendingEnabled =
        mode === 'sandbox' ||
        Boolean(organization?.features?.['outreach_sending_enabled']);
    const outreachButtonText = !outreachSendingEnabled
        ? 'Sending Not Enabled'
        : 'Start Outreach';
    // Shifts that can still be contacted. Resolved shifts (filled via
    // assign-and-fill, archived into cancelled_shifts metadata, or
    // cancelled in the EHR) are excluded: the backend never contacts
    // them, so requiring a caregiver selection on them would dead-end
    // the Start Outreach button (PHO-11044). Unlike archivableShiftIds,
    // this deliberately ignores outreach status: it describes the
    // shifts themselves, not whether the archive action is available.
    const unresolvedShifts = useMemo(
        () =>
            outreach.shifts.filter(
                (shift) =>
                    !filledShiftIds.has(shift.id) &&
                    !cancelledShiftIds.has(shift.id) &&
                    shift.status !== 'cancelled',
            ),
        [outreach.shifts, filledShiftIds, cancelledShiftIds],
    );
    const allShiftsResolved =
        isMultiShiftOutreach && unresolvedShifts.length === 0;
    const startOutreachDisabledTooltip = !outreachSendingEnabled
        ? 'Outreach sending is not yet enabled for this organization. Contact your Phoebe representative to enable it.'
        : allShiftsResolved
          ? 'Every shift here is already filled or archived.'
          : undefined;

    const isStartOutreachDisabled =
        !outreachSendingEnabled || !hasAgentInstructions || allShiftsResolved;
    const shiftsAwaitingSelection = isMultiShiftOutreach
        ? unresolvedShifts
        : [];
    const shiftsWithSelections = shiftsAwaitingSelection.filter(
        (s) => (selectedCaregiverIdsByShift[s.id]?.length ?? 0) > 0,
    ).length;
    const allShiftsHaveSelections =
        shiftsWithSelections === shiftsAwaitingSelection.length;
    const shiftReadinessLabel =
        isMultiShiftOutreach && !allShiftsHaveSelections
            ? `${shiftsWithSelections}/${shiftsAwaitingSelection.length} shifts ready · ${
                  shiftsAwaitingSelection.length - shiftsWithSelections
              } still ${
                  shiftsAwaitingSelection.length - shiftsWithSelections === 1
                      ? 'needs'
                      : 'need'
              } caregivers`
            : undefined;
    const selectedCaregiverShiftIds = useMemo<CaregiverShiftIds>(() => {
        const [firstShift] = unresolvedShifts;
        if (!firstShift) {
            return {};
        }
        return buildCaregiverShiftIdsFromSelections({
            selectedCaregiverIdsByShift,
            shifts: unresolvedShifts,
            currentShiftId: firstShift.id,
            currentCaregiverIds:
                selectedCaregiverIdsByShift[firstShift.id] ?? [],
        });
    }, [selectedCaregiverIdsByShift, unresolvedShifts]);
    useEffect(() => {
        // Publish the empty mapping too: a cleared table selection must
        // block starting from the chat approval, not fall back to the
        // agent's full proposal. "No selection published" (null) is
        // reserved for pages without this outreach's table.
        setOutreachCaregiverSelection({
            outreachId: outreach.id,
            caregiverShiftIds: selectedCaregiverShiftIds,
        });
    }, [outreach.id, selectedCaregiverShiftIds, setOutreachCaregiverSelection]);
    useEffect(
        () => () => {
            setOutreachCaregiverSelection(null);
        },
        [setOutreachCaregiverSelection],
    );
    const selectedCaregiverCount = Object.keys(
        selectedCaregiverShiftIds,
    ).length;
    const selectedCaregiverShiftCount = new Set(
        Object.values(selectedCaregiverShiftIds).flat(),
    ).size;
    // Org-configurable cap, enforced server-side in
    // libraries/python/outreach_operations/start_outreach.py. 300 mirrors
    // DEFAULT_MAX_CAREGIVERS_PER_OUTREACH there.
    const maxOutreachCaregivers =
        organization?.callout_settings?.max_caregivers_per_outreach ?? 300;
    const isOverCaregiverLimit = selectedCaregiverCount > maxOutreachCaregivers;
    const selectedCaregiverActionTooltip =
        startOutreachDisabledTooltip ?? shiftReadinessLabel;
    const isSelectedCaregiverActionDisabled =
        isAgentAddingPeople ||
        isStartingSelectedCaregivers ||
        selectedCaregiverCount === 0 ||
        isOverCaregiverLimit ||
        isStartOutreachDisabled ||
        !allShiftsHaveSelections;
    const clearSelectedCaregivers = useCallback(() => {
        sameCaregiversSourceIdsRef.current = [];
        setSelectedCaregiverIdsByShift({});
    }, []);
    const handleStartSelectedCaregivers = useCallback(async () => {
        if (isSelectedCaregiverActionDisabled) {
            return;
        }

        setIsStartingSelectedCaregivers(true);
        try {
            await (isAgentPickingCaregivers
                ? handleConfirmSuggestionsAsOutreach(
                      selectedCaregiverShiftIds,
                      selectedCaregiverNameById,
                  )
                : handleStartOutreach(
                      selectedCaregiverShiftIds,
                      selectedCaregiverNameById,
                  ));
        } catch {
            // submitStartOutreach surfaces errors via toast/report.
        } finally {
            setIsStartingSelectedCaregivers(false);
        }
    }, [
        handleConfirmSuggestionsAsOutreach,
        handleStartOutreach,
        isAgentPickingCaregivers,
        isSelectedCaregiverActionDisabled,
        selectedCaregiverNameById,
        selectedCaregiverShiftIds,
    ]);
    const renderSelectedCaregiverStartButton = (className?: string) => {
        const button = (
            <Button
                size="sm"
                className={className}
                disabled={isSelectedCaregiverActionDisabled}
                onClick={() => {
                    void handleStartSelectedCaregivers();
                }}
            >
                {isStartingSelectedCaregivers
                    ? 'Contacting…'
                    : outreachButtonText}
            </Button>
        );

        if (
            selectedCaregiverActionTooltip &&
            isSelectedCaregiverActionDisabled
        ) {
            return (
                <Tooltip>
                    <TooltipTrigger asChild={true}>
                        <span className="inline-flex">{button}</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                        {selectedCaregiverActionTooltip}
                    </TooltipContent>
                </Tooltip>
            );
        }

        return button;
    };
    const showSelectionFloatingBar =
        startReport !== null ||
        (outreach.status === 'pending' && selectedCaregiverCount > 0);

    useEffect(() => {
        if (hasAgentInstructions) {
            autoGeneratedInstructionsKeyRef.current = null;
        }
    }, [hasAgentInstructions]);

    useEffect(() => {
        if (!organizationId || !organization || hasAgentInstructions) {
            return;
        }

        if (shiftIdsForInstructions.length === 0) {
            return;
        }

        if (isLoadingAgentInstructionsTemplate) {
            return;
        }

        if (
            autoGeneratedInstructionsKeyRef.current ===
            instructionsGenerationKey
        ) {
            return;
        }

        autoGeneratedInstructionsKeyRef.current = instructionsGenerationKey;

        let isActive = true;
        const generateAndSaveInstructions = async () => {
            setIsGeneratingInstructions(true);
            try {
                const response = await getApiClient().post({
                    url: '/organizations/{organization_id}/shifts/agent-instructions',
                    pathParams: {
                        organization_id: organizationId,
                    },
                    body: {
                        shift_ids: shiftIdsForInstructions,
                    },
                    reason: 'callout-detail',
                });

                const finalInstructions =
                    mergeGeneratedAndTemplateAgentInstructions({
                        generatedInstructions: response.instructions,
                        template: agentInstructionsTemplate,
                        shifts: outreach.shifts,
                        organization,
                    });

                if (!finalInstructions) {
                    return;
                }

                if (
                    !isActive ||
                    autoGeneratedInstructionsKeyRef.current !==
                        instructionsGenerationKey
                ) {
                    return;
                }

                await getApiClient().patch({
                    url: '/organizations/{organization_id}/callouts/{callout_id}',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                    },
                    body: {
                        instructions: finalInstructions,
                    },
                    reason: 'callout-detail',
                    idempotent: true,
                });

                await mutateOutreach();
            } catch (error) {
                if (
                    isActive &&
                    autoGeneratedInstructionsKeyRef.current ===
                        instructionsGenerationKey
                ) {
                    console.error(
                        'Failed to auto-generate agent instructions:',
                        error,
                    );
                    if (!isTransparentRateLimitError(error)) {
                        toast.error('Failed to generate agent instructions', {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        });
                    }
                }
            } finally {
                if (
                    isActive &&
                    autoGeneratedInstructionsKeyRef.current ===
                        instructionsGenerationKey
                ) {
                    setIsGeneratingInstructions(false);
                }
            }
        };

        void generateAndSaveInstructions();

        return () => {
            isActive = false;
            if (
                autoGeneratedInstructionsKeyRef.current ===
                instructionsGenerationKey
            ) {
                autoGeneratedInstructionsKeyRef.current = null;
            }
            setIsGeneratingInstructions(false);
        };
    }, [
        agentInstructionsTemplate,
        hasAgentInstructions,
        instructionsGenerationKey,
        isLoadingAgentInstructionsTemplate,
        mutateOutreach,
        organization,
        organizationId,
        outreach.id,
        outreach.shifts,
        shiftIdsForInstructions,
    ]);

    // Auto-generate care plan summary post-creation (and for newly added shifts)
    const existingShiftCarePlans = useMemo(
        () =>
            (outreach.outreach_metadata?.['shift_care_plans'] ?? {}) as Record<
                string,
                {
                    summary?: string | null;
                    bullets?: string | null;
                }
            >,
        [outreach.outreach_metadata],
    );

    const shiftIdsNeedingCarePlan = useMemo(
        () =>
            outreach.shifts
                .filter(
                    (s) =>
                        (!!s.patient_care_plan?.trim() ||
                            s.client_has_care_plan_summary === true) &&
                        !existingShiftCarePlans[s.id],
                )
                .map((s) => s.id),
        [existingShiftCarePlans, outreach.shifts],
    );
    const shiftsMissingCarePlanData = useMemo(
        () =>
            outreach.shifts.filter(
                (s) =>
                    !s.patient_care_plan?.trim() &&
                    s.client_has_care_plan_summary !== true &&
                    !existingShiftCarePlans[s.id],
            ),
        [existingShiftCarePlans, outreach.shifts],
    );

    const requireCarePlanAcknowledgement = useFeatureFlag(
        'require_care_plan_acknowledgement',
        organization?.features,
    );
    const needsCarePlan =
        requireCarePlanAcknowledgement && shiftIdsNeedingCarePlan.length > 0;
    const carePlanGenerationKey = `${outreach.id}:${shiftIdsNeedingCarePlan.join(',')}`;

    useEffect(() => {
        if (shiftIdsNeedingCarePlan.length === 0) {
            autoGeneratedCarePlanKeyRef.current = null;
        }
    }, [shiftIdsNeedingCarePlan]);

    useEffect(() => {
        if (
            !organizationId ||
            !needsCarePlan ||
            shiftIdsNeedingCarePlan.length === 0
        ) {
            return;
        }

        if (autoGeneratedCarePlanKeyRef.current === carePlanGenerationKey) {
            return;
        }

        autoGeneratedCarePlanKeyRef.current = carePlanGenerationKey;

        let isActive = true;
        const generateAndSaveCarePlan = async () => {
            setIsGeneratingCarePlan(true);
            try {
                const response = await getApiClient().post({
                    url: '/organizations/{organization_id}/shifts/summarize-care-plan',
                    pathParams: {
                        organization_id: organizationId,
                    },
                    body: {
                        shift_ids: shiftIdsNeedingCarePlan,
                    },
                    reason: 'callout-detail',
                });

                const newShiftCarePlans: Record<
                    string,
                    {
                        summary: string | null;
                        bullets: string | null;
                    }
                > = {};
                for (const item of response.per_shift ?? []) {
                    newShiftCarePlans[item.shift_id] = {
                        summary: item.summary || null,
                        bullets: item.care_plan_bullets || null,
                    };
                }

                if (
                    !isActive ||
                    autoGeneratedCarePlanKeyRef.current !==
                        carePlanGenerationKey
                ) {
                    return;
                }

                const mergedCarePlans = {
                    ...existingShiftCarePlans,
                    ...newShiftCarePlans,
                };

                await getApiClient().patch({
                    url: '/organizations/{organization_id}/callouts/{callout_id}',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                    },
                    body: {
                        shift_care_plans: mergedCarePlans,
                    },
                    reason: 'callout-detail',
                    idempotent: true,
                });

                await mutateOutreach();
            } catch (error) {
                if (
                    isActive &&
                    autoGeneratedCarePlanKeyRef.current ===
                        carePlanGenerationKey
                ) {
                    console.error('Failed to auto-generate care plan:', error);
                    if (!isTransparentRateLimitError(error)) {
                        toast.error('Failed to generate care plan summary', {
                            persistNotification: true,
                            notificationType: 'outreach_action',
                        });
                    }
                }
            } finally {
                if (
                    isActive &&
                    autoGeneratedCarePlanKeyRef.current ===
                        carePlanGenerationKey
                ) {
                    setIsGeneratingCarePlan(false);
                }
            }
        };

        void generateAndSaveCarePlan();

        return () => {
            isActive = false;
            if (autoGeneratedCarePlanKeyRef.current === carePlanGenerationKey) {
                autoGeneratedCarePlanKeyRef.current = null;
            }
            setIsGeneratingCarePlan(false);
        };
    }, [
        carePlanGenerationKey,
        existingShiftCarePlans,
        mutateOutreach,
        needsCarePlan,
        organizationId,
        outreach.id,
        shiftIdsNeedingCarePlan,
    ]);

    const handleStartEditInstructions = () => {
        if (isAgentInstructionsLoading) {
            return;
        }

        setEditedInstructions(instructions);
        setIsEditingInstructions(true);
    };

    const handleCancelEditInstructions = () => {
        setIsEditingInstructions(false);
        setEditedInstructions('');
    };

    const handleSaveInstructions = async () => {
        if (!organizationId) {
            return;
        }

        setIsSavingInstructions(true);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/callouts/{callout_id}',
                pathParams: {
                    organization_id: organizationId,
                    callout_id: outreach.id,
                },
                body: {
                    instructions: editedInstructions,
                },
            });
            toast.success('Instructions updated', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            await mutateOutreach();
            setIsEditingInstructions(false);
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error('Failed to update instructions', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            }
            console.error('Error updating instructions:', error);
        } finally {
            setIsSavingInstructions(false);
        }
    };

    const handleCloseInstructionsModal = (open: boolean) => {
        if (!open) {
            setIsEditingInstructions(false);
            setEditedInstructions('');
        }
        setShowInstructionsModal(open);
    };

    const handleToggleOutreach = async () => {
        if (!organizationId) {
            return;
        }

        setIsTogglingOutreach(true);
        try {
            const updatedOutreach = await getApiClient().post({
                url: '/organizations/{organization_id}/outreaches/toggle/{outreach_id}',
                pathParams: {
                    organization_id: organizationId,
                    outreach_id: outreach.id,
                },
            });
            toast.success(
                updatedOutreach.status === 'paused'
                    ? 'Outreach paused'
                    : 'Outreach resumed',
                {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                },
            );
            mutateOutreach(
                (current) =>
                    current ? { ...current, ...updatedOutreach } : current,
                false,
            );
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error(
                    getApiErrorDetail(error) ?? 'Failed to toggle outreach',
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
            }
            console.error('Error toggling outreach:', error);
        } finally {
            setIsTogglingOutreach(false);
        }
    };

    const { data: stagedMessages } = useOrganizationPendingMessagesRealtime(
        'callout-detail',
        false,
        {
            organizationId,
            domain: 'callout',
            domainId: outreach.id,
            conversationIdFilter:
                contactAttempts
                    ?.filter(
                        (attempt) =>
                            attempt.delivery_mode === 'sms' &&
                            attempt.conversation_id !== null,
                    )
                    .map((attempt) => attempt.conversation_id)
                    .filter((id): id is string => Boolean(id)) ?? undefined,
        },
    );

    const hasPendingMessageForCaregiver = useCallback(
        (caregiverId: string) => {
            if (!stagedMessages || !contactAttempts) {
                return false;
            }

            // Get conversation IDs for this specific caregiver's SMS attempts
            const caregiverConversationIds = contactAttempts
                .filter(
                    (attempt) =>
                        attempt.caregiver_id === caregiverId &&
                        attempt.delivery_mode === 'sms' &&
                        attempt.conversation_id !== null,
                )
                .map((attempt) => attempt.conversation_id)
                .filter((id): id is string => id !== null);

            if (caregiverConversationIds.length === 0) {
                return false;
            }

            // Check if there are pending messages for any of this caregiver's conversations
            return stagedMessages.some(
                (msg) =>
                    msg.status === 'pending' &&
                    caregiverConversationIds.includes(msg.conversation_id),
            );
        },
        [stagedMessages, contactAttempts],
    );

    const clientReferenceTerm =
        organization?.general_settings?.client_reference_term ?? 'client';
    const clientReferenceTermTitle = `${clientReferenceTerm
        .charAt(0)
        .toUpperCase()}${clientReferenceTerm.slice(1)}`;

    const formatCaregiverDisplayName = useCallback(
        (name: string) =>
            formatCaregiverName(name, displayCaregiverNamesLastFirst),
        [displayCaregiverNamesLastFirst],
    );

    // Get caregiver IDs that have been contacted
    const caregiverIdsForBlockedCheck = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return null;
        }
        const uniqueIds = new Set<string>();
        for (const attempt of contactAttempts) {
            uniqueIds.add(attempt.caregiver_id);
        }
        return Array.from(uniqueIds);
    }, [contactAttempts]);

    const { data: blockedCaregivers, mutate: mutateBlockedCaregivers } =
        useBlockedCaregivers(organizationId, caregiverIdsForBlockedCheck);

    const blockedCaregiverMap = useMemo(() => {
        const map = new Map<string, Array<BlockedCaregiverInfo>>();
        for (const blocked of blockedCaregivers ?? []) {
            const existing = map.get(blocked.caregiver_id);
            if (existing) {
                existing.push(blocked);
            } else {
                map.set(blocked.caregiver_id, [blocked]);
            }
        }
        return map;
    }, [blockedCaregivers]);

    const contactAttemptsColumns = useMemo(
        () =>
            createContactAttemptsColumns(
                timezone,
                handleCallAgain,
                isCallingCaregiver,
                handleOpenAssignAndFillModal,
                hasPendingMessageForCaregiver,
                formatCaregiverDisplayName,
                {
                    isDevelopment,
                    onDeleteContactAttempt: handleDeleteContactAttempt,
                    deletingContactAttemptId,
                    outreachSteps: isActiveOutreach(outreach?.status)
                        ? outreach?.steps
                        : undefined,
                    pausedTimeSeconds:
                        (outreach?.outreach_metadata?.[
                            'paused_time_seconds'
                        ] as number | undefined) ?? 0,
                    isPaused: outreach?.status === 'paused',
                    blockedCaregiverMap,
                    onCaregiverUnblocked: () => mutateBlockedCaregivers(),
                    onCaregiverClick: setSelectedCaregiverDetailId,
                    recordingUrls,
                    isMultiShiftOutreach,
                    isMultiClientOutreach,
                    sortByLastName,
                    shifts: outreach.shifts,
                    timezone,
                    onShiftResponseChange: handleShiftResponseChange,
                    quietHoursResumesAt:
                        outreach?.quiet_hours_hold?.resumes_at ?? null,
                    skipBusyCaregivers:
                        organization?.callout_settings?.skip_busy_caregivers ??
                        false,
                    filledShiftIds,
                    cancelledShiftIds,
                    dataSourceAccounts,
                    matchSignalSortKey,
                    onMatchSignalSortKeyChange: handleMatchSignalSortKeyChange,
                    matchSignalSortOptions,
                    isAgentAddingPeople,
                    showEhrCaregiverStatus,
                    showHHAExchangeCompliance,
                },
            ),
        [
            timezone,
            handleCallAgain,
            isCallingCaregiver,
            handleOpenAssignAndFillModal,
            hasPendingMessageForCaregiver,
            formatCaregiverDisplayName,
            isDevelopment,
            handleDeleteContactAttempt,
            deletingContactAttemptId,
            outreach?.steps,
            outreach?.outreach_metadata,
            blockedCaregiverMap,
            mutateBlockedCaregivers,
            recordingUrls,
            outreach?.status,
            isMultiShiftOutreach,
            isMultiClientOutreach,
            sortByLastName,
            outreach.shifts,
            handleShiftResponseChange,
            outreach?.quiet_hours_hold?.resumes_at,
            organization?.callout_settings?.skip_busy_caregivers,
            filledShiftIds,
            cancelledShiftIds,
            dataSourceAccounts,
            matchSignalSortKey,
            handleMatchSignalSortKeyChange,
            matchSignalSortOptions,
            isAgentAddingPeople,
            showEhrCaregiverStatus,
            showHHAExchangeCompliance,
        ],
    );

    const renderContactAttemptMobileCard = useCallback(
        (group: ContactAttemptGroup) => {
            const latestAttempt = group.timelineItems[0] ?? group.attempts[0];
            const resolvedShiftIds = new Set<string>([
                ...filledShiftIds,
                ...cancelledShiftIds,
            ]);
            const assignAndFillCandidate = getAssignAndFillCandidate(
                group,
                resolvedShiftIds,
            );

            return (
                <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-base font-semibold">
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setSelectedCaregiverDetailId(
                                            group.caregiver.id,
                                        );
                                    }}
                                    className="block truncate text-left text-base font-semibold underline underline-offset-2"
                                >
                                    {formatCaregiverDisplayName(
                                        group.caregiver.name,
                                    )}
                                </button>
                            </div>
                            <div className="text-muted-foreground text-sm">
                                {group.attempts.length}{' '}
                                {group.attempts.length === 1
                                    ? 'attempt'
                                    : 'attempts'}
                                {group.caregiver.city
                                    ? ` · ${group.caregiver.city}`
                                    : ''}
                            </div>
                        </div>
                        {assignAndFillCandidate ? (
                            <Button
                                size="sm"
                                className="shrink-0 bg-[rgb(49,124,255)] text-white hover:bg-[rgb(29,104,235)]"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenAssignAndFillModal(
                                        group.caregiver,
                                        assignAndFillCandidate.interestedShiftIds,
                                        group.wellskyOverrideReview ?? null,
                                        group.hhaexchangeOvertimeReview ?? null,
                                        group.scheduleOverlapMinutes,
                                        group.scheduleOverlapRanges,
                                        group.scheduleConflictingShifts,
                                        'inline_button',
                                    );
                                }}
                            >
                                {group.wellskyOverrideReview ||
                                group.hhaexchangeOvertimeReview
                                    ? 'Review'
                                    : 'Assign'}
                            </Button>
                        ) : null}
                    </div>
                    {showEhrCaregiverStatus || showHHAExchangeCompliance ? (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            {showEhrCaregiverStatus ? (
                                <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground text-[10px] font-medium uppercase">
                                        Status
                                    </span>
                                    {group.caregiver.ehr_caregiver_status ? (
                                        <EhrCaregiverStatusBadge
                                            status={
                                                group.caregiver
                                                    .ehr_caregiver_status
                                            }
                                        />
                                    ) : (
                                        <span>—</span>
                                    )}
                                </div>
                            ) : null}
                            {showHHAExchangeCompliance ? (
                                <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground text-[10px] font-medium uppercase">
                                        Compliance
                                    </span>
                                    {group.caregiver.caregiver_compliance ? (
                                        <CaregiverComplianceBadge
                                            compliance={
                                                group.caregiver
                                                    .caregiver_compliance
                                            }
                                        />
                                    ) : (
                                        <span>—</span>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-2">
                        {isMultiShiftOutreach && outreach.shifts.length > 0 ? (
                            outreach.shifts.map((shift) => (
                                <Badge
                                    key={shift.id}
                                    variant="outline"
                                    className="max-w-full justify-start"
                                >
                                    <span className="truncate">
                                        {formatName(shift.patient_name)}:{' '}
                                        {getCaregiverResponseLabel(
                                            group.shiftResponses?.[shift.id] ??
                                                null,
                                        )}
                                    </span>
                                </Badge>
                            ))
                        ) : (
                            <Badge variant="outline">
                                {getCaregiverResponseLabel(
                                    group.overallResponse,
                                )}
                            </Badge>
                        )}
                        <div className="text-muted-foreground text-xs">
                            <ContactAttemptDistances
                                clientDistances={group.clientDistances}
                                distance={group.distance}
                            />
                        </div>
                        {group.wellskyOverrideReview ? (
                            <Badge className="border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50">
                                Override approval required
                            </Badge>
                        ) : null}
                    </div>
                    {latestAttempt ? (
                        <div className="bg-background rounded-md border p-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                    {getContactAttemptDeliveryModeLabel(
                                        latestAttempt,
                                    )}
                                </Badge>
                                <ContactAttemptStatusBadge
                                    status={latestAttempt.outreach_status}
                                    deliveryMode={latestAttempt.delivery_mode}
                                    isInboundCall={isRelatedInboundTimelineItem(
                                        latestAttempt,
                                    )}
                                    communicationDetails={
                                        latestAttempt.communication_details
                                    }
                                    currentShiftEndTime={
                                        latestAttempt.current_shift_end
                                    }
                                    showBusyQueuedState={
                                        organization?.callout_settings
                                            ?.skip_busy_caregivers ?? false
                                    }
                                    voiceCallStartedAt={
                                        latestAttempt.voice_call?.started_at
                                    }
                                    timezone={timezone}
                                    heldByQuietHours={
                                        latestAttempt.held_by_quiet_hours
                                    }
                                    quietHoursResumesAt={
                                        outreach.quiet_hours_hold?.resumes_at ??
                                        null
                                    }
                                />
                                <span className="text-muted-foreground text-xs">
                                    {formatDateAndTime(
                                        latestAttempt.created_at,
                                        timezone,
                                    )}
                                </span>
                            </div>
                            <div className="text-muted-foreground mt-2 text-xs break-words">
                                {shouldShowSummaryPending(latestAttempt)
                                    ? 'Generating summary...'
                                    : getContactAttemptDetailsText(
                                          latestAttempt,
                                      )}
                            </div>
                            {outreach.status === 'paused' && outreach.steps ? (
                                <div className="mt-2 text-xs">
                                    <NextAttemptCountdown
                                        attempt={latestAttempt}
                                        outreachSteps={outreach.steps}
                                        pausedTimeSeconds={
                                            (outreach.outreach_metadata?.[
                                                'paused_time_seconds'
                                            ] as number | undefined) ?? 0
                                        }
                                        isPaused={true}
                                        currentShiftEndTime={
                                            group.currentShiftEndTime ?? null
                                        }
                                        skipBusyCaregivers={
                                            organization?.callout_settings
                                                ?.skip_busy_caregivers ?? false
                                        }
                                        timezone={timezone}
                                    />
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="text-muted-foreground text-xs">
                        Tap to view full attempt history.
                    </div>
                </div>
            );
        },
        [
            cancelledShiftIds,
            filledShiftIds,
            formatCaregiverDisplayName,
            formatName,
            handleOpenAssignAndFillModal,
            showEhrCaregiverStatus,
            showHHAExchangeCompliance,
            isMultiShiftOutreach,
            organization?.callout_settings?.skip_busy_caregivers,
            outreach.outreach_metadata,
            outreach.quiet_hours_hold?.resumes_at,
            outreach.shifts,
            outreach.status,
            outreach.steps,
            timezone,
        ],
    );

    const mobileShiftDetails = mobileShiftDetailsId
        ? outreach.shifts.find((shift) => shift.id === mobileShiftDetailsId)
        : undefined;
    const mobileShiftDetailsClient =
        mobileShiftDetails?.client_id !== undefined &&
        mobileShiftDetails.client_id !== null
            ? clientById.get(mobileShiftDetails.client_id)
            : undefined;
    const mobileShiftDetailsClientNotificationTarget = mobileShiftDetails
        ? resolveClientNotificationTarget(mobileShiftDetailsClient)
        : null;
    const mobileShiftDetailsOriginalCaregiver = mobileShiftDetails
        ? outreach.original_caregivers?.[mobileShiftDetails.id]
        : undefined;
    const mobileShiftDetailsFilledInfo = mobileShiftDetails
        ? (
              outreach.outreach_metadata as {
                  filled_shifts?: Record<string, FilledShiftInfo>;
              } | null
          )?.filled_shifts?.[mobileShiftDetails.id]
        : undefined;
    const mobileShiftDetailsFilledCaregiverName =
        mobileShiftDetailsFilledInfo !== undefined
            ? formatName(
                  contactAttempts?.find(
                      (contactAttempt) =>
                          contactAttempt.caregiver_id ===
                          mobileShiftDetailsFilledInfo.caregiver_id,
                  )?.org_caregiver_record?.name ??
                      mobileShiftDetailsFilledInfo.caregiver_name,
              )
            : undefined;
    const mobileShiftDetailsIsCancelled =
        mobileShiftDetails !== undefined &&
        (cancelledShiftIds.has(mobileShiftDetails.id) ||
            mobileShiftDetails.status === 'cancelled');
    const mobileShiftDetailsIsUnfilled =
        mobileShiftDetails !== undefined &&
        mobileShiftDetailsFilledInfo === undefined &&
        !mobileShiftDetailsIsCancelled &&
        new Date(mobileShiftDetails.shift_end_time) < new Date();
    const mobileShiftDetailsStatusLabel =
        mobileShiftDetailsFilledInfo !== undefined
            ? `Filled by ${mobileShiftDetailsFilledCaregiverName ?? 'Unknown'}`
            : mobileShiftDetailsIsCancelled
              ? 'Archived'
              : mobileShiftDetailsIsUnfilled
                ? 'Unfilled'
                : 'Pending';
    const mobileShiftDetailsCanCancel =
        mobileShiftDetails !== undefined &&
        CANCELLABLE_SHIFT_OUTREACH_STATUSES.has(outreach.status) &&
        mobileShiftDetailsFilledInfo === undefined &&
        !mobileShiftDetailsIsCancelled;
    const mobileShiftDetailsCanEdit =
        mobileShiftDetails !== undefined &&
        Boolean(organization?.features?.['edit_shift_enabled']) &&
        outreach.shifts.length === 1;
    const mobileShiftDetailsClientAttempt =
        mobileShiftDetails?.client_id !== undefined &&
        mobileShiftDetails.client_id !== null
            ? clientAttemptByClientId.get(mobileShiftDetails.client_id)
            : undefined;
    const mobileShiftDetailsClientNotificationWillQueue =
        mobileShiftDetails !== undefined &&
        willQueueClientNotification({
            shiftId: mobileShiftDetails.id,
            assignedCaregiverId: mobileShiftDetails.assigned_caregiver_id,
            filledInfo: mobileShiftDetailsFilledInfo,
            isCancelled: mobileShiftDetailsIsCancelled,
        });
    const mobileShiftDetailsClientNotificationCanRetry =
        mobileShiftDetailsClientAttempt !== undefined &&
        CLIENT_NOTIFICATION_RETRYABLE_STATUSES.has(
            mobileShiftDetailsClientAttempt.outreach_status,
        ) &&
        mobileShiftDetailsClientAttempt.round < 1;
    const mobileShiftDetailsClientNotificationIsRetrying =
        mobileShiftDetailsClientAttempt !== undefined &&
        retryingClientContactAttemptId === mobileShiftDetailsClientAttempt.id;

    const canEditMobileShifts =
        outreach.status === 'pending' && !isOutreachShiftAddBlocked(outreach);
    const mobileOutreachShiftsSection =
        isMobile && outreach.shifts.length > 0 ? (
            <MobileDetailCard
                testId="mobile-outreach-shifts-card"
                label="Shifts"
                compact={true}
                action={
                    canEditMobileShifts ? (
                        <div className="flex items-center gap-1.5">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 gap-1 px-2 text-xs [&_svg]:size-3"
                                onClick={() => setShowAddShiftsDialog(true)}
                            >
                                <Plus />
                                Add shifts
                            </Button>
                        </div>
                    ) : null
                }
            >
                {showAutoAssignColumn && (
                    <AutoAssignClientPolicyOverrides
                        overrides={autoAssignClientPolicyOverrides}
                        className="mb-1 px-1.5"
                    />
                )}
                <div className="space-y-0.5">
                    {outreach.shifts.map((shift) => (
                        <div key={shift.id} className="flex items-center gap-2">
                            <button
                                type="button"
                                data-testid="mobile-shift-summary-card"
                                className="hover:bg-muted/60 flex min-w-0 flex-1 items-start justify-between gap-2 rounded-md px-1.5 py-1 text-left transition-colors"
                                onClick={() =>
                                    setMobileShiftDetailsId(shift.id)
                                }
                            >
                                <span className="min-w-0 flex-1 truncate text-[13px] leading-4 font-medium">
                                    {formatName(shift.patient_name)}
                                </span>
                                <span className="text-muted-foreground shrink-0 text-right text-[10px] leading-3">
                                    {formatDateAndTimeWithWeekday(
                                        shift.shift_start_time,
                                        timezone,
                                    )}{' '}
                                    -{' '}
                                    {formatTime(shift.shift_end_time, timezone)}
                                </span>
                            </button>
                            {showAutoAssignColumn ? (
                                <AutoAssignShiftCell
                                    shiftId={shift.id}
                                    patientName={formatName(shift.patient_name)}
                                    checked={autoAssignEdits.isShiftChecked(
                                        shift.id,
                                    )}
                                    disabled={autoAssignEdits.isUpdating}
                                    onCheckedChange={(checked) =>
                                        autoAssignEdits.requestToggleShift(
                                            shift.id,
                                            checked,
                                        )
                                    }
                                />
                            ) : null}
                        </div>
                    ))}
                </div>
            </MobileDetailCard>
        ) : undefined;
    const outreachDetailsPanel = (
        <OutreachDetails
            outreach={outreach}
            onEditOutreachClick={
                outreach.status === 'pending' &&
                (outreach.outreach_type === 'callout' ||
                    outreach.outreach_type === 'recurring_shift')
                    ? () => setShowEditOutreachModal(true)
                    : undefined
            }
            timezone={timezone}
            mobileShiftsSection={mobileOutreachShiftsSection}
            displayStatus={displayStatus}
            organizationId={organizationId}
            autoAssignPolicy={
                organization?.callout_settings?.auto_assign_caregiver_policy
            }
            mutateOutreach={mutateOutreach}
            chrome={chrome}
            showAutoAssignFirstResponderToggle={
                supportsAutoAssignFirstResponder && !isCompactChrome
            }
            onAutoAssignSave={handleAutoAssignSave}
            isUpdatingAutoAssign={isUpdatingAutoAssignFirstResponder}
            autoAssignClientPolicyOverrides={autoAssignClientPolicyOverrides}
            showAddShiftsDialog={showAddShiftsDialog}
            onAddShiftsDialogOpenChange={setShowAddShiftsDialog}
            clientNotificationContent={
                isMultiShiftOutreach ? undefined : clientNotificationContent
            }
            onClientNotificationClick={
                isMultiShiftOutreach
                    ? undefined
                    : () => setShowClientNotificationModal(true)
            }
            actionButtons={
                <React.Fragment>
                    <ShiftNotesModalButton
                        outreach={outreach}
                        buttonVariant={outreachDetailReferenceButtonVariant(
                            chrome,
                        )}
                        className={outreachDetailReferenceButtonClassName(
                            chrome,
                        )}
                    />
                    <CarePlanModalButton
                        outreach={outreach}
                        chrome={chrome}
                        buttonVariant={outreachDetailReferenceButtonVariant(
                            chrome,
                        )}
                        className={outreachDetailReferenceButtonClassName(
                            chrome,
                        )}
                        isLoading={isGeneratingCarePlan}
                        forceVisible={isGeneratingCarePlan}
                        onSaved={async () => {
                            await mutateOutreach();
                        }}
                    />
                    <Button
                        variant={outreachDetailReferenceButtonVariant(chrome)}
                        size="sm"
                        className={outreachDetailReferenceButtonClassName(
                            chrome,
                        )}
                        onClick={() => setShowStepsPreviewModal(true)}
                    >
                        <ListChecks
                            className={outreachDetailReferenceIconClassName(
                                chrome,
                            )}
                        />
                        Steps
                    </Button>
                    {isMobile && isChatBackedOutreach && onOpenOutreachChat ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onOpenOutreachChat}
                            title="Open outreach chat"
                        >
                            {isOutreachChatLoading ? (
                                <Loader2
                                    className="mr-2 h-4 w-4 animate-spin"
                                    data-testid="outreach-chat-loading-icon"
                                />
                            ) : (
                                <MessageSquareText className="mr-2 h-4 w-4" />
                            )}
                            <span>Chat</span>
                        </Button>
                    ) : null}
                    {isMobile ? (
                        <React.Fragment>
                            <Button
                                variant={outreachDetailReferenceButtonVariant(
                                    chrome,
                                )}
                                size="sm"
                                className={outreachDetailReferenceButtonClassName(
                                    chrome,
                                )}
                                onClick={() => setShowInstructionsModal(true)}
                            >
                                {isAgentInstructionsLoading ? (
                                    <Loader2
                                        className={outreachDetailReferenceIconClassName(
                                            chrome,
                                            true,
                                        )}
                                    />
                                ) : (
                                    <FileText
                                        className={outreachDetailReferenceIconClassName(
                                            chrome,
                                        )}
                                    />
                                )}
                                <span>Agent Instructions</span>
                            </Button>
                            {organizationId ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setShowSubscribersModal(true)
                                    }
                                >
                                    <Bell className="mr-2 h-4 w-4" />
                                    Manage subscribers
                                </Button>
                            ) : null}
                        </React.Fragment>
                    ) : (
                        <OutreachDetailsOverflowMenu
                            onAgentInstructions={() =>
                                setShowInstructionsModal(true)
                            }
                            onManageSubscribers={
                                organizationId
                                    ? () => setShowSubscribersModal(true)
                                    : undefined
                            }
                            triggerVariant={outreachDetailReferenceButtonVariant(
                                chrome,
                            )}
                            triggerClassName={outreachDetailReferenceButtonClassName(
                                chrome,
                            )}
                        />
                    )}
                    {showContactAttemptHistory &&
                        isActiveOutreach(outreach.status) && (
                            <React.Fragment>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleToggleOutreach}
                                    disabled={isTogglingOutreach}
                                >
                                    {isTogglingOutreach ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : outreach.status === 'paused' ? (
                                        <Play className="mr-2 h-4 w-4" />
                                    ) : (
                                        <Pause className="mr-2 h-4 w-4" />
                                    )}
                                    <span>
                                        {outreach.status === 'paused'
                                            ? 'Resume'
                                            : 'Pause'}
                                    </span>
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={isAgentAddingPeople}
                                    title={
                                        isAgentAddingPeople
                                            ? 'Phoebe is adding people…'
                                            : undefined
                                    }
                                    onClick={() => {
                                        if (!isMobile && !isAgentAddingPeople) {
                                            setShowAddCaregiversModal(true);
                                        }
                                    }}
                                >
                                    <Plus className="mr-2 h-4 w-3" />
                                    Add People
                                </Button>
                            </React.Fragment>
                        )}
                </React.Fragment>
            }
        />
    );
    const mobileRecommendationsHeaderAction: React.ReactNode = undefined;

    return (
        <div
            data-testid="outreach-detail-query-container"
            className={cn(
                // The detail view can render beside the chat panel, so
                // size children against the panel width (@container
                // variants), not the viewport.
                '@container',
                isMobile && 'flex h-full min-h-0 flex-col overflow-hidden',
            )}
        >
            {/* h-12 matches the chat panel header, so the two border lines
                meet at the same height across the panel divider. */}
            <div className="hidden h-12 shrink-0 items-center border-b px-3 md:flex">
                <div className="flex w-full items-center justify-between">
                    <Link
                        to="/outreaches"
                        search={{ tab: 'callouts' }}
                        className="text-muted-foreground hover:text-foreground inline-flex items-center text-sm"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back to
                        Outreaches
                    </Link>
                    <TrainingModePageTourReplayButton />
                </div>
            </div>
            {outreach.status === 'pending' && !isGeneratingCarePlan && (
                <MissingCarePlanBanner
                    outreach={outreach}
                    chrome={chrome}
                    missingShifts={shiftsMissingCarePlanData}
                    organizationId={organizationId ?? null}
                    onSaved={async (_plans) => {
                        await mutateOutreach();
                    }}
                    className="mx-3 mt-3"
                />
            )}
            <QuietHoursBanner
                outreach={outreach}
                organizationId={organizationId ?? null}
                timezone={timezone}
                onChanged={() => mutateOutreach()}
                className="mx-3 mt-3"
            />
            {outreachDetailsPanel}
            <div
                className={cn(
                    'mx-auto',
                    isMobile && 'min-h-0 w-full flex-1 overflow-hidden',
                )}
            >
                <div
                    className={cn(
                        'flex h-full min-h-0 flex-col gap-0 px-2 pt-1 pb-0 md:gap-2 md:px-6 md:py-6',
                        isMobile && 'flex-1 overflow-hidden',
                    )}
                >
                    {outreachConflictError && (
                        <div className="mb-4">
                            <OutreachConflictAlert
                                conflict={outreachConflictError}
                                timezone={timezone}
                            />
                        </div>
                    )}
                    {!isMobile ? (
                        <Tabs defaultValue="shifts" className="gap-2">
                            <TabsList>
                                <TabsTrigger value="shifts">Shifts</TabsTrigger>
                                <TabsTrigger
                                    value="activity"
                                    data-testid="outreach-activity-tab"
                                >
                                    Activity
                                </TabsTrigger>
                            </TabsList>
                            <TabsContent value="shifts" className="min-h-0">
                                <React.Fragment>
                                    <div
                                        className="relative rounded-lg border"
                                        data-testid="outreach-shifts-table"
                                    >
                                        {selectedArchivableShiftIds.length >
                                            0 && (
                                            <div
                                                className={cn(
                                                    'bg-background pointer-events-none absolute top-0 right-0 z-10 flex items-center justify-between gap-2 border-b px-2',
                                                    showShiftArchiveSelection
                                                        ? 'left-8'
                                                        : 'left-0',
                                                    isCompactChrome
                                                        ? 'h-8'
                                                        : 'h-10',
                                                )}
                                            >
                                                <span
                                                    className={
                                                        isCompactChrome
                                                            ? 'text-xs font-medium'
                                                            : 'text-sm font-medium'
                                                    }
                                                >
                                                    {
                                                        selectedArchivableShiftIds.length
                                                    }{' '}
                                                    selected
                                                </span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className={cn(
                                                        'text-muted-foreground hover:text-destructive pointer-events-auto gap-1',
                                                        isCompactChrome
                                                            ? 'h-6 px-2 text-xs'
                                                            : 'h-7 px-2',
                                                    )}
                                                    aria-label="Remove selected shifts"
                                                    onClick={() =>
                                                        setShiftToCancel({
                                                            ids: selectedArchivableShiftIds,
                                                            targetLabel:
                                                                selectedShiftArchivePatientName,
                                                        })
                                                    }
                                                >
                                                    <Trash2
                                                        className={
                                                            isCompactChrome
                                                                ? 'size-3.5'
                                                                : 'h-4 w-4'
                                                        }
                                                    />
                                                    Remove
                                                </Button>
                                            </div>
                                        )}
                                        {/* The compact layout has no Auto-Assign
                                        section, so the client-setting note sits
                                        above the switch column instead. */}
                                        {showAutoAssignColumn && (
                                            <AutoAssignClientPolicyOverrides
                                                overrides={
                                                    autoAssignClientPolicyOverrides
                                                }
                                                className="mb-2 px-1"
                                            />
                                        )}
                                        {/* Content-sized columns keep Auto-assign,
                                        Location, and Status compact. Client and
                                        Date/Time wrap if the panel is narrow. */}
                                        <Table
                                            className={outreachShiftsTableClassName(
                                                chrome,
                                            )}
                                        >
                                            <TableHeader>
                                                <TableRow>
                                                    {showShiftArchiveSelection && (
                                                        <TableHead className="w-8">
                                                            <Checkbox
                                                                checked={
                                                                    allArchivableShiftsSelected
                                                                        ? true
                                                                        : selectedArchivableShiftIds.length >
                                                                            0
                                                                          ? 'indeterminate'
                                                                          : false
                                                                }
                                                                onCheckedChange={() =>
                                                                    setSelectedShiftIdsToArchive(
                                                                        allArchivableShiftsSelected
                                                                            ? new Set()
                                                                            : new Set(
                                                                                  archivableShiftIds,
                                                                              ),
                                                                    )
                                                                }
                                                                aria-label="Select all archivable shifts"
                                                            />
                                                        </TableHead>
                                                    )}
                                                    <TableHead
                                                        className={cn(
                                                            'whitespace-normal',
                                                            selectedArchivableShiftIds.length >
                                                                0 &&
                                                                'invisible',
                                                        )}
                                                    >
                                                        {
                                                            clientReferenceTermTitle
                                                        }
                                                    </TableHead>
                                                    <TableHead
                                                        className={cn(
                                                            'min-w-28 whitespace-normal',
                                                            selectedArchivableShiftIds.length >
                                                                0 &&
                                                                'invisible',
                                                        )}
                                                    >
                                                        Date / Time
                                                    </TableHead>
                                                    {showAutoAssignColumn && (
                                                        <TableHead
                                                            className={cn(
                                                                'w-px text-center whitespace-nowrap',
                                                                selectedArchivableShiftIds.length >
                                                                    0 &&
                                                                    'invisible',
                                                            )}
                                                        >
                                                            <AutoAssignShiftHeader
                                                                policy={
                                                                    organization
                                                                        ?.callout_settings
                                                                        ?.auto_assign_caregiver_policy
                                                                }
                                                            />
                                                        </TableHead>
                                                    )}
                                                    <TableHead
                                                        className={cn(
                                                            'w-px',
                                                            selectedArchivableShiftIds.length >
                                                                0 &&
                                                                'invisible',
                                                        )}
                                                    >
                                                        Location
                                                    </TableHead>
                                                    <TableHead
                                                        className={cn(
                                                            'w-px',
                                                            selectedArchivableShiftIds.length >
                                                                0 &&
                                                                'invisible',
                                                        )}
                                                    >
                                                        Status
                                                    </TableHead>
                                                    {showNotifyClientColumn && (
                                                        <TableHead
                                                            className={cn(
                                                                'w-px',
                                                                selectedArchivableShiftIds.length >
                                                                    0 &&
                                                                    'invisible',
                                                            )}
                                                        >
                                                            Notify Client
                                                        </TableHead>
                                                    )}
                                                    {showClientNotificationStatusColumn && (
                                                        <TableHead
                                                            className={
                                                                selectedArchivableShiftIds.length >
                                                                0
                                                                    ? 'invisible'
                                                                    : undefined
                                                            }
                                                        >
                                                            Client Notification
                                                        </TableHead>
                                                    )}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {outreach.shifts.map(
                                                    (shift) => {
                                                        const originalCaregiver =
                                                            outreach
                                                                .original_caregivers?.[
                                                                shift.id
                                                            ];
                                                        const metadata =
                                                            outreach.outreach_metadata as {
                                                                filled_shifts?: Record<
                                                                    string,
                                                                    FilledShiftInfo
                                                                >;
                                                            } | null;
                                                        const filledInfo =
                                                            metadata
                                                                ?.filled_shifts?.[
                                                                shift.id
                                                            ];
                                                        const isCancelled =
                                                            displayCancelledShiftIds.has(
                                                                shift.id,
                                                            );
                                                        // A shift is unfilled if it's not filled and end time has passed
                                                        const isUnfilled =
                                                            !filledInfo &&
                                                            !isCancelled &&
                                                            new Date(
                                                                shift.shift_end_time,
                                                            ) < new Date();

                                                        // Find caregiver name for filled shifts
                                                        let filledCaregiverName:
                                                            | string
                                                            | undefined;
                                                        if (filledInfo) {
                                                            const caregiver =
                                                                contactAttempts?.find(
                                                                    (ca) =>
                                                                        ca.caregiver_id ===
                                                                        filledInfo.caregiver_id,
                                                                )?.org_caregiver_record;
                                                            filledCaregiverName =
                                                                formatName(
                                                                    caregiver?.name ??
                                                                        filledInfo.caregiver_name,
                                                                );
                                                        }

                                                        const canCancelShift =
                                                            CANCELLABLE_SHIFT_OUTREACH_STATUSES.has(
                                                                outreach.status,
                                                            ) &&
                                                            !filledInfo &&
                                                            !isCancelled;
                                                        const clientId =
                                                            shift.client_id;
                                                        const client = clientId
                                                            ? clientById.get(
                                                                  clientId,
                                                              )
                                                            : undefined;
                                                        const clientNotificationTarget =
                                                            resolveClientNotificationTarget(
                                                                client,
                                                            );
                                                        return (
                                                            <TableRow
                                                                key={shift.id}
                                                            >
                                                                {showShiftArchiveSelection && (
                                                                    <TableCell>
                                                                        <Checkbox
                                                                            checked={selectedShiftIdsToArchive.has(
                                                                                shift.id,
                                                                            )}
                                                                            disabled={
                                                                                !canCancelShift
                                                                            }
                                                                            onCheckedChange={(
                                                                                checked,
                                                                            ) => {
                                                                                setSelectedShiftIdsToArchive(
                                                                                    (
                                                                                        previousIds,
                                                                                    ) => {
                                                                                        const nextIds =
                                                                                            new Set(
                                                                                                previousIds,
                                                                                            );
                                                                                        if (
                                                                                            checked ===
                                                                                            true
                                                                                        ) {
                                                                                            nextIds.add(
                                                                                                shift.id,
                                                                                            );
                                                                                        } else {
                                                                                            nextIds.delete(
                                                                                                shift.id,
                                                                                            );
                                                                                        }
                                                                                        return nextIds;
                                                                                    },
                                                                                );
                                                                            }}
                                                                            aria-label={`Select shift ${shift.id} for archive`}
                                                                        />
                                                                    </TableCell>
                                                                )}
                                                                <TableCell className="font-medium whitespace-normal">
                                                                    <div className="flex min-w-0 items-center gap-2">
                                                                        <div className="min-w-0">
                                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                                {clientId ? (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={(
                                                                                            event,
                                                                                        ) => {
                                                                                            event.stopPropagation();
                                                                                            setClientDetailClientId(
                                                                                                clientId,
                                                                                            );
                                                                                        }}
                                                                                        className={cn(
                                                                                            'cursor-pointer text-left',
                                                                                            uiV3 && [
                                                                                                REFERENCE_PILL_CLASS,
                                                                                                REFERENCE_TYPE_TINT_CLASSES.client,
                                                                                                REFERENCE_TYPE_HOVER_CLASSES.client,
                                                                                            ],
                                                                                            isCancelled &&
                                                                                                'text-muted-foreground line-through',
                                                                                        )}
                                                                                    >
                                                                                        {uiV3 ? (
                                                                                            <ReferencePillContent
                                                                                                Icon={
                                                                                                    REFERENCE_TYPE_ICONS.client
                                                                                                }
                                                                                                label={formatName(
                                                                                                    shift.patient_name,
                                                                                                )}
                                                                                                wrap={
                                                                                                    false
                                                                                                }
                                                                                            />
                                                                                        ) : (
                                                                                            formatName(
                                                                                                shift.patient_name,
                                                                                            )
                                                                                        )}
                                                                                    </button>
                                                                                ) : (
                                                                                    <span
                                                                                        className={cn(
                                                                                            isCancelled &&
                                                                                                'text-muted-foreground line-through',
                                                                                        )}
                                                                                    >
                                                                                        {formatName(
                                                                                            shift.patient_name,
                                                                                        )}
                                                                                    </span>
                                                                                )}
                                                                                {shift.status ===
                                                                                    'missing' &&
                                                                                    isActiveOutreach(
                                                                                        outreach.status,
                                                                                    ) && (
                                                                                        <OutreachStatusBadge status="shift_missing" />
                                                                                    )}
                                                                                {shiftGroupByShiftId.has(
                                                                                    shift.id,
                                                                                ) && (
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger
                                                                                            asChild={
                                                                                                true
                                                                                            }
                                                                                        >
                                                                                            {uiV3 ? (
                                                                                                <span className="text-muted-foreground text-xs font-normal">
                                                                                                    Group{' '}
                                                                                                    {shiftGroupByShiftId.get(
                                                                                                        shift.id,
                                                                                                    )! +
                                                                                                        1}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <Badge
                                                                                                    variant="outline"
                                                                                                    className={cn(
                                                                                                        'px-1.5 py-0 text-[10px]',
                                                                                                        GROUP_BADGE_COLORS[
                                                                                                            shiftGroupByShiftId.get(
                                                                                                                shift.id,
                                                                                                            )! %
                                                                                                                GROUP_BADGE_COLORS.length
                                                                                                        ],
                                                                                                    )}
                                                                                                >
                                                                                                    Group{' '}
                                                                                                    {shiftGroupByShiftId.get(
                                                                                                        shift.id,
                                                                                                    )! +
                                                                                                        1}
                                                                                                </Badge>
                                                                                            )}
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent>
                                                                                            All
                                                                                            shifts
                                                                                            in
                                                                                            this
                                                                                            group
                                                                                            must
                                                                                            be
                                                                                            accepted
                                                                                            together
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                )}
                                                                            </div>
                                                                            {originalCaregiver?.name ? (
                                                                                <div className="text-muted-foreground truncate text-xs leading-4">
                                                                                    {formatName(
                                                                                        originalCaregiver.name,
                                                                                    )}
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                        {!uiV3 && (
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                asChild={
                                                                                    true
                                                                                }
                                                                                className="h-7 w-7"
                                                                                onClick={(
                                                                                    event,
                                                                                ) =>
                                                                                    event.stopPropagation()
                                                                                }
                                                                            >
                                                                                <Link
                                                                                    to="/shifts"
                                                                                    search={{
                                                                                        date: getDateStringInTimezone(
                                                                                            new Date(
                                                                                                shift.shift_start_time,
                                                                                            ),
                                                                                            timezone,
                                                                                        ),
                                                                                        shiftId:
                                                                                            shift.id,
                                                                                    }}
                                                                                    title="View shift"
                                                                                >
                                                                                    <ExternalLink className="h-4 w-4" />
                                                                                    <span className="sr-only">
                                                                                        View
                                                                                        shift
                                                                                    </span>
                                                                                </Link>
                                                                            </Button>
                                                                        )}
                                                                        {canCancelShift &&
                                                                            !showShiftArchiveSelection && (
                                                                                <CancelShiftButton
                                                                                    onCancel={() =>
                                                                                        setShiftToCancel(
                                                                                            {
                                                                                                ids: [
                                                                                                    shift.id,
                                                                                                ],
                                                                                                targetLabel:
                                                                                                    formatName(
                                                                                                        shift.patient_name,
                                                                                                    ),
                                                                                            },
                                                                                        )
                                                                                    }
                                                                                />
                                                                            )}
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="whitespace-normal">
                                                                    {formatDateAndTimeWithWeekday(
                                                                        shift.shift_start_time,
                                                                        timezone,
                                                                    )}{' '}
                                                                    -{' '}
                                                                    {formatTime(
                                                                        shift.shift_end_time,
                                                                        timezone,
                                                                    )}
                                                                </TableCell>
                                                                {showAutoAssignColumn && (
                                                                    <TableCell
                                                                        className="text-center"
                                                                        onClick={(
                                                                            event,
                                                                        ) =>
                                                                            event.stopPropagation()
                                                                        }
                                                                    >
                                                                        <AutoAssignShiftCell
                                                                            shiftId={
                                                                                shift.id
                                                                            }
                                                                            patientName={formatName(
                                                                                shift.patient_name,
                                                                            )}
                                                                            checked={autoAssignEdits.isShiftChecked(
                                                                                shift.id,
                                                                            )}
                                                                            disabled={
                                                                                autoAssignEdits.isUpdating
                                                                            }
                                                                            onCheckedChange={(
                                                                                checked,
                                                                            ) =>
                                                                                autoAssignEdits.requestToggleShift(
                                                                                    shift.id,
                                                                                    checked,
                                                                                )
                                                                            }
                                                                        />
                                                                    </TableCell>
                                                                )}
                                                                <TableCell>
                                                                    {shift.patient_city ||
                                                                        '—'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {filledInfo ? (
                                                                        <Badge
                                                                            variant="outline"
                                                                            className={cn(
                                                                                statusIntentBadgeClassName(
                                                                                    'positive',
                                                                                ),
                                                                                'max-w-[16rem] truncate',
                                                                            )}
                                                                            title={`Filled by ${filledCaregiverName ?? 'Unknown'}`}
                                                                        >
                                                                            Filled
                                                                            by{' '}
                                                                            {filledCaregiverName ??
                                                                                'Unknown'}
                                                                        </Badge>
                                                                    ) : isCancelled ? (
                                                                        <Badge
                                                                            variant="outline"
                                                                            className={statusIntentBadgeClassName(
                                                                                'neutral',
                                                                            )}
                                                                        >
                                                                            Archived
                                                                        </Badge>
                                                                    ) : isUnfilled ? (
                                                                        <Badge
                                                                            variant="outline"
                                                                            className={statusIntentBadgeClassName(
                                                                                'negative',
                                                                            )}
                                                                        >
                                                                            Unfilled
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge
                                                                            variant="outline"
                                                                            className={statusIntentBadgeClassName(
                                                                                'neutral',
                                                                            )}
                                                                        >
                                                                            Pending
                                                                        </Badge>
                                                                    )}
                                                                </TableCell>
                                                                {showNotifyClientColumn && (
                                                                    <TableCell
                                                                        onClick={(
                                                                            e,
                                                                        ) =>
                                                                            e.stopPropagation()
                                                                        }
                                                                    >
                                                                        {isCancelled ? null : clientNotificationTarget ? (
                                                                            <div className="flex flex-col gap-1">
                                                                                <Switch
                                                                                    checked={isNotifyClientEnabledForShift(
                                                                                        shift.id,
                                                                                    )}
                                                                                    disabled={
                                                                                        !!filledInfo
                                                                                    }
                                                                                    onCheckedChange={(
                                                                                        checked,
                                                                                    ) =>
                                                                                        handleToggleNotifyClient(
                                                                                            shift.id,
                                                                                            checked,
                                                                                        )
                                                                                    }
                                                                                />
                                                                                {client?.default_notify_on_fill ===
                                                                                    false &&
                                                                                    !isNotifyClientEnabledForShift(
                                                                                        shift.id,
                                                                                    ) && (
                                                                                        <span className="text-muted-foreground text-xs">
                                                                                            Client
                                                                                            notifications
                                                                                            disabled
                                                                                        </span>
                                                                                    )}
                                                                                {clientNotificationTarget.secondaryContactName && (
                                                                                    <span className="text-muted-foreground text-xs">
                                                                                        Secondary:{' '}
                                                                                        {
                                                                                            clientNotificationTarget.secondaryContactName
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-muted-foreground text-xs">
                                                                                No
                                                                                client
                                                                                phone
                                                                                number
                                                                            </span>
                                                                        )}
                                                                    </TableCell>
                                                                )}
                                                                {showClientNotificationStatusColumn && (
                                                                    <TableCell>
                                                                        {(() => {
                                                                            if (
                                                                                !shift.client_id
                                                                            ) {
                                                                                return null;
                                                                            }
                                                                            const attempt =
                                                                                clientAttemptByClientId.get(
                                                                                    shift.client_id,
                                                                                );
                                                                            if (
                                                                                !attempt
                                                                            ) {
                                                                                return willQueueClientNotification(
                                                                                    {
                                                                                        shiftId:
                                                                                            shift.id,
                                                                                        assignedCaregiverId:
                                                                                            shift.assigned_caregiver_id,
                                                                                        filledInfo,
                                                                                        isCancelled,
                                                                                    },
                                                                                ) ? (
                                                                                    <ContactAttemptStatusBadge status="queued" />
                                                                                ) : (
                                                                                    <span className="text-muted-foreground text-xs">
                                                                                        —
                                                                                    </span>
                                                                                );
                                                                            }
                                                                            const maxRounds = 2;
                                                                            const canRetry =
                                                                                CLIENT_NOTIFICATION_RETRYABLE_STATUSES.has(
                                                                                    attempt.outreach_status,
                                                                                ) &&
                                                                                attempt.round <
                                                                                    maxRounds -
                                                                                        1;
                                                                            const isRetrying =
                                                                                retryingClientContactAttemptId ===
                                                                                attempt.id;
                                                                            return (
                                                                                <div className="flex items-center gap-2">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="cursor-pointer"
                                                                                        onClick={(
                                                                                            e,
                                                                                        ) => {
                                                                                            e.stopPropagation();
                                                                                            setShowClientNotificationModal(
                                                                                                true,
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <ContactAttemptStatusBadge
                                                                                            status={
                                                                                                attempt.outreach_status
                                                                                            }
                                                                                            deliveryMode={
                                                                                                attempt.delivery_mode
                                                                                            }
                                                                                            recipientType="client"
                                                                                            communicationDetails={
                                                                                                attempt.communication_details
                                                                                            }
                                                                                        />
                                                                                    </button>
                                                                                    {canRetry && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs transition-colors"
                                                                                            disabled={
                                                                                                isRetrying
                                                                                            }
                                                                                            onClick={(
                                                                                                e,
                                                                                            ) => {
                                                                                                e.stopPropagation();
                                                                                                handleRetryClientContactAttempt(
                                                                                                    attempt.id,
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            {isRetrying ? (
                                                                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                                                            ) : (
                                                                                                <Phone className="h-3 w-3" />
                                                                                            )}
                                                                                            <span>
                                                                                                Retry
                                                                                            </span>
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        );
                                                    },
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                    {(showEditShiftAction ||
                                        outreach.status === 'pending' ||
                                        isOutreachShiftAddBlocked(
                                            outreach,
                                        )) && (
                                        <div className="mt-2 flex justify-end gap-2">
                                            {showEditShiftAction && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        setShowEditShiftModal(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    <Pencil className="mr-2 h-4 w-4" />
                                                    Edit Shift
                                                </Button>
                                            )}
                                            {outreach.status === 'pending' ||
                                            isOutreachShiftAddBlocked(
                                                outreach,
                                            ) ? (
                                                isOutreachShiftAddBlocked(
                                                    outreach,
                                                ) ? null : (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className={cn(
                                                            'text-muted-foreground hover:text-foreground hover:bg-transparent h-auto gap-1 px-0 font-normal shadow-none',
                                                            isCompactChrome
                                                                ? 'text-xs [&_svg]:size-3'
                                                                : 'text-sm',
                                                        )}
                                                        onClick={() =>
                                                            setShowAddShiftsDialog(
                                                                true,
                                                            )
                                                        }
                                                    >
                                                        <Plus
                                                            className={
                                                                isCompactChrome
                                                                    ? undefined
                                                                    : 'h-4 w-4'
                                                            }
                                                        />
                                                        Add shifts
                                                    </Button>
                                                )
                                            ) : null}
                                        </div>
                                    )}
                                </React.Fragment>
                            </TabsContent>
                            {organizationId ? (
                                <TabsContent value="activity">
                                    <EntityEventsTimeline
                                        organizationId={organizationId}
                                        entityType="outreach"
                                        entityId={outreach.id}
                                    />
                                </TabsContent>
                            ) : null}
                        </Tabs>
                    ) : null}
                    {outreach.status === 'pending' &&
                        outreach.shifts.length > 0 && (
                            <div
                                ref={recommendationsSectionRef}
                                data-testid="outreach-caregivers-section"
                                className={cn(
                                    'mt-6 flex min-h-0 flex-col gap-1 md:mt-8 md:gap-4',
                                    // Reserve space so the fixed selection
                                    // pill doesn't overlap the table's
                                    // pagination controls when scrolled to
                                    // the bottom.
                                    (selectedCaregiverCount > 0 ||
                                        startReport !== null) &&
                                        'pb-16',
                                )}
                            >
                                {(() => {
                                    const renderRecsForUnit = (
                                        unit: OutreachDisplayUnit<OutreachShift>,
                                    ) => {
                                        const [singleShift] = unit.shifts;
                                        const shiftGroupId =
                                            unit.kind === 'group'
                                                ? unit.id
                                                : undefined;
                                        const unitShiftIds = unit.shiftIds;
                                        const unitGroupMode =
                                            unit.kind === 'group'
                                                ? unit.groupMode
                                                : undefined;
                                        if (
                                            unit.kind === 'shift' &&
                                            !singleShift
                                        ) {
                                            return null;
                                        }
                                        const unitShiftIdSet = new Set(
                                            unitShiftIds,
                                        );
                                        const hasStoredSelectionForUnit =
                                            unitShiftIds.some(
                                                (shiftId) =>
                                                    shiftId in
                                                    selectedCaregiverIdsByShift,
                                            );
                                        const storedIdsForUnit =
                                            hasStoredSelectionForUnit
                                                ? normalizeSelectedCaregiverIds(
                                                      unitShiftIds.flatMap(
                                                          (shiftId) =>
                                                              selectedCaregiverIdsByShift[
                                                                  shiftId
                                                              ] ?? [],
                                                      ),
                                                  )
                                                : undefined;
                                        const syncedPreSelectedIds =
                                            useSameCaregiversAcrossShifts
                                                ? sameCaregiversSourceIdsRef.current
                                                : undefined;
                                        const initialIdsForShift =
                                            syncedPreSelectedIds ??
                                            storedIdsForUnit;
                                        // In-house tables own their
                                        // selection (localStorage +
                                        // server dismissals); echoing
                                        // parent state would clobber
                                        // restore-after-reload. Only
                                        // once cross-shift sync has
                                        // been engaged does parent
                                        // state become the display
                                        // source for every table.
                                        const inhouseControlledSelectedIds =
                                            hasEverSyncedAcrossShifts
                                                ? initialIdsForShift
                                                : undefined;
                                        const suggestedIdsForUnit =
                                            unitAgentRecommendationsById.get(
                                                unit.id,
                                            )?.caregiverIds ??
                                            EMPTY_UNIT_CAREGIVER_IDS;
                                        const inhousePreSelectedIds =
                                            suggestedIdsForUnit.length > 0
                                                ? suggestedIdsForUnit
                                                : undefined;
                                        const patientTags = Array.isArray(
                                            singleShift?.patient_tags,
                                        )
                                            ? singleShift.patient_tags.filter(
                                                  (tag) =>
                                                      tag.trim().length > 0,
                                              )
                                            : [];
                                        const defaultTagFilters =
                                            isAxisCare && patientTags.length > 0
                                                ? patientTags
                                                : undefined;
                                        const selectedCaregiverIdsByUnit =
                                            Object.fromEntries(
                                                displayUnits.map(
                                                    (displayUnit) => [
                                                        displayUnit.id,
                                                        normalizeSelectedCaregiverIds(
                                                            displayUnit.shiftIds.flatMap(
                                                                (shiftId) =>
                                                                    selectedCaregiverIdsByShift[
                                                                        shiftId
                                                                    ] ?? [],
                                                            ),
                                                        ),
                                                    ],
                                                ),
                                            );
                                        const handleStartOutreachForUnit = (
                                            caregiverIds: Array<string>,
                                            caregiverNamesById: CaregiverNamesById,
                                        ): Promise<boolean> => {
                                            const caregiverShiftIds =
                                                buildCaregiverShiftIdsFromUnitSelections(
                                                    {
                                                        selectedCaregiverIdsByUnit,
                                                        displayUnits,
                                                        currentUnitId: unit.id,
                                                        currentCaregiverIds:
                                                            caregiverIds,
                                                    },
                                                );
                                            return handleStartOutreach(
                                                caregiverShiftIds,
                                                caregiverNamesById,
                                            );
                                        };

                                        const handleTableSelectionChangeForUnit =
                                            (
                                                caregiverIds: Array<string>,
                                                caregiverNamesById: CaregiverNamesById,
                                            ) => {
                                                const normalizedCaregiverIds =
                                                    normalizeSelectedCaregiverIds(
                                                        caregiverIds,
                                                    );
                                                const selectedIdsByShiftForUnit =
                                                    Object.fromEntries(
                                                        unitShiftIds.map(
                                                            (shiftId) => [
                                                                shiftId,
                                                                normalizedCaregiverIds,
                                                            ],
                                                        ),
                                                    );
                                                if (
                                                    Object.keys(
                                                        caregiverNamesById,
                                                    ).length > 0
                                                ) {
                                                    setSelectedCaregiverNameById(
                                                        (prev) =>
                                                            Object.entries(
                                                                caregiverNamesById,
                                                            ).some(
                                                                ([id, name]) =>
                                                                    prev[id] !==
                                                                    name,
                                                            )
                                                                ? {
                                                                      ...prev,
                                                                      ...caregiverNamesById,
                                                                  }
                                                                : prev,
                                                    );
                                                }
                                                const matchesNormalized = (
                                                    existing:
                                                        | Array<string>
                                                        | undefined,
                                                ): boolean =>
                                                    existing !== undefined &&
                                                    existing.length ===
                                                        normalizedCaregiverIds.length &&
                                                    existing.every(
                                                        (id, index) =>
                                                            id ===
                                                            normalizedCaregiverIds[
                                                                index
                                                            ],
                                                    );
                                                if (
                                                    useSameCaregiversAcrossShifts
                                                ) {
                                                    sameCaregiversSourceIdsRef.current =
                                                        normalizedCaregiverIds;
                                                    // Bail when every shift already holds these exact ids; the
                                                    // table's selection-sync effect re-fires onSelectionChange on
                                                    // each render and would otherwise loop the parent's state.
                                                    setSelectedCaregiverIdsByShift(
                                                        (prev) =>
                                                            outreach.shifts.every(
                                                                (shift) =>
                                                                    matchesNormalized(
                                                                        prev[
                                                                            shift
                                                                                .id
                                                                        ],
                                                                    ),
                                                            )
                                                                ? prev
                                                                : Object.fromEntries(
                                                                      outreach.shifts.map(
                                                                          (
                                                                              shift,
                                                                          ) => [
                                                                              shift.id,
                                                                              [
                                                                                  ...normalizedCaregiverIds,
                                                                              ],
                                                                          ],
                                                                      ),
                                                                  ),
                                                    );
                                                    return;
                                                }
                                                setSelectedCaregiverIdsByShift(
                                                    (prev) =>
                                                        unitShiftIds.every(
                                                            (id) =>
                                                                matchesNormalized(
                                                                    prev[id],
                                                                ),
                                                        )
                                                            ? prev
                                                            : {
                                                                  ...prev,
                                                                  ...selectedIdsByShiftForUnit,
                                                              },
                                                );
                                            };

                                        if (
                                            suggestedIdsForUnit.length === 0 &&
                                            (coordinatorPicksCaregivers ||
                                                !isAgentBackedOutreach)
                                        ) {
                                            return (
                                                <InhouseRecommendationsTable
                                                    outreachAgentGeneration={
                                                        outreach.agent_generation
                                                    }
                                                    key={unit.id}
                                                    shiftId={
                                                        shiftGroupId
                                                            ? undefined
                                                            : singleShift?.id
                                                    }
                                                    shiftGroupId={shiftGroupId}
                                                    groupShiftIds={unitShiftIds}
                                                    groupMode={unitGroupMode}
                                                    title="Eligible Caregivers"
                                                    onStartOutreach={
                                                        handleStartOutreachForUnit
                                                    }
                                                    onSelectionChange={
                                                        handleTableSelectionChangeForUnit
                                                    }
                                                    showStartButton={false}
                                                    buttonText={
                                                        outreachButtonText
                                                    }
                                                    preSelectedCaregiverIds={
                                                        inhousePreSelectedIds
                                                    }
                                                    controlledSelectedCaregiverIds={
                                                        inhouseControlledSelectedIds
                                                    }
                                                    isStartOutreachDisabled={
                                                        isStartOutreachDisabled ||
                                                        !allShiftsHaveSelections
                                                    }
                                                    startOutreachDisabledTooltip={
                                                        startOutreachDisabledTooltip
                                                    }
                                                    shiftReadinessLabel={
                                                        shiftReadinessLabel
                                                    }
                                                    outreachId={outreach.id}
                                                    persistedFeedbackByCaregiver={
                                                        outreach.suggested_caregivers_feedback ??
                                                        undefined
                                                    }
                                                    defaultTagFilters={
                                                        defaultTagFilters
                                                    }
                                                    mobileHeaderAction={
                                                        mobileRecommendationsHeaderAction
                                                    }
                                                />
                                            );
                                        }

                                        const isAgentAnalyzing =
                                            outreach.agent_status ===
                                            'analyzing';
                                        const didAgentFail =
                                            outreach.agent_status === 'failed';
                                        const isManualFallbackOpen =
                                            manualFallbackShiftIds.has(unit.id);
                                        const unitSuggestions =
                                            suggestedCaregivers.filter((s) =>
                                                s.covered_shift_ids.some(
                                                    (shiftId) =>
                                                        unitShiftIdSet.has(
                                                            shiftId,
                                                        ),
                                                ),
                                            );
                                        const unitKey = shiftGroupId
                                            ? `shift_group:${shiftGroupId}`
                                            : `shift:${singleShift?.id}`;
                                        const hasUnitRecommendations =
                                            unitSuggestions.length > 0 ||
                                            unitKeysWithDismissals.has(unitKey);
                                        const unitAgentRecommendations =
                                            unitAgentRecommendationsById.get(
                                                unit.id,
                                            );
                                        const unitCaregiverIds =
                                            unitAgentRecommendations?.caregiverIds ??
                                            EMPTY_UNIT_CAREGIVER_IDS;
                                        const unitReasoningMap =
                                            unitAgentRecommendations?.reasoningMap ??
                                            EMPTY_UNIT_REASONING_MAP;
                                        const buildSelectedIdsByShiftForUnit = (
                                            caregiverIds: Array<string>,
                                        ): Record<string, Array<string>> => {
                                            const normalizedIds =
                                                normalizeSelectedCaregiverIds(
                                                    caregiverIds,
                                                );
                                            const selectedIds = new Set(
                                                normalizedIds,
                                            );
                                            const idsByShift =
                                                Object.fromEntries(
                                                    unitShiftIds.map(
                                                        (shiftId) => [
                                                            shiftId,
                                                            [],
                                                        ],
                                                    ),
                                                ) as Record<
                                                    string,
                                                    Array<string>
                                                >;
                                            const idsWithSuggestionCoverage =
                                                new Set<string>();

                                            for (const suggestion of unitSuggestions) {
                                                if (
                                                    !selectedIds.has(
                                                        suggestion.caregiver_id,
                                                    )
                                                ) {
                                                    continue;
                                                }
                                                const coveredUnitShiftIds =
                                                    suggestion.covered_shift_ids.filter(
                                                        (shiftId) =>
                                                            unitShiftIdSet.has(
                                                                shiftId,
                                                            ),
                                                    );
                                                if (
                                                    coveredUnitShiftIds.length ===
                                                    0
                                                ) {
                                                    continue;
                                                }
                                                idsWithSuggestionCoverage.add(
                                                    suggestion.caregiver_id,
                                                );
                                                for (const shiftId of coveredUnitShiftIds) {
                                                    idsByShift[shiftId]?.push(
                                                        suggestion.caregiver_id,
                                                    );
                                                }
                                            }

                                            const manuallySelectedIds =
                                                normalizedIds.filter(
                                                    (caregiverId) =>
                                                        !idsWithSuggestionCoverage.has(
                                                            caregiverId,
                                                        ),
                                                );
                                            for (const shiftId of unitShiftIds) {
                                                idsByShift[shiftId] =
                                                    normalizeSelectedCaregiverIds(
                                                        [
                                                            ...(idsByShift[
                                                                shiftId
                                                            ] ?? []),
                                                            ...manuallySelectedIds,
                                                        ],
                                                    );
                                            }

                                            return idsByShift;
                                        };
                                        const handleTableSelectionChangeForAgentUnit =
                                            (
                                                caregiverIds: Array<string>,
                                                caregiverNamesById: CaregiverNamesById,
                                            ) => {
                                                if (
                                                    !shiftGroupId ||
                                                    useSameCaregiversAcrossShifts
                                                ) {
                                                    handleTableSelectionChangeForUnit(
                                                        caregiverIds,
                                                        caregiverNamesById,
                                                    );
                                                    return;
                                                }

                                                if (
                                                    Object.keys(
                                                        caregiverNamesById,
                                                    ).length > 0
                                                ) {
                                                    setSelectedCaregiverNameById(
                                                        (prev) =>
                                                            Object.entries(
                                                                caregiverNamesById,
                                                            ).some(
                                                                ([id, name]) =>
                                                                    prev[id] !==
                                                                    name,
                                                            )
                                                                ? {
                                                                      ...prev,
                                                                      ...caregiverNamesById,
                                                                  }
                                                                : prev,
                                                    );
                                                }
                                                const selectedIdsByShiftForUnit =
                                                    buildSelectedIdsByShiftForUnit(
                                                        caregiverIds,
                                                    );
                                                setSelectedCaregiverIdsByShift(
                                                    (prev) => {
                                                        const alreadyMatches =
                                                            Object.entries(
                                                                selectedIdsByShiftForUnit,
                                                            ).every(
                                                                ([
                                                                    sid,
                                                                    ids,
                                                                ]) => {
                                                                    const existing =
                                                                        prev[
                                                                            sid
                                                                        ];
                                                                    return (
                                                                        existing !==
                                                                            undefined &&
                                                                        existing.length ===
                                                                            ids.length &&
                                                                        existing.every(
                                                                            (
                                                                                cid,
                                                                                i,
                                                                            ) =>
                                                                                cid ===
                                                                                ids[
                                                                                    i
                                                                                ],
                                                                        )
                                                                    );
                                                                },
                                                            );
                                                        return alreadyMatches
                                                            ? prev
                                                            : {
                                                                  ...prev,
                                                                  ...selectedIdsByShiftForUnit,
                                                              };
                                                    },
                                                );
                                            };
                                        const handleConfirmSuggestionsForUnit =
                                            (
                                                caregiverIds: Array<string>,
                                                caregiverNamesById: CaregiverNamesById,
                                            ): Promise<boolean> => {
                                                const selectedIdsByShiftForUnit =
                                                    buildSelectedIdsByShiftForUnit(
                                                        caregiverIds,
                                                    );
                                                const mergedSelectedIdsByShiftForUnit =
                                                    Object.fromEntries(
                                                        unitShiftIds.map(
                                                            (shiftId) => [
                                                                shiftId,
                                                                normalizeSelectedCaregiverIds(
                                                                    [
                                                                        ...(selectedCaregiverIdsByShift[
                                                                            shiftId
                                                                        ] ??
                                                                            []),
                                                                        ...(selectedIdsByShiftForUnit[
                                                                            shiftId
                                                                        ] ??
                                                                            []),
                                                                    ],
                                                                ),
                                                            ],
                                                        ),
                                                    ) as Record<
                                                        string,
                                                        Array<string>
                                                    >;
                                                const selectedIdsByShiftForConfirm =
                                                    {
                                                        ...selectedCaregiverIdsByShift,
                                                        ...mergedSelectedIdsByShiftForUnit,
                                                    };
                                                const caregiverShiftIds: CaregiverShiftIds =
                                                    {};
                                                for (const shift of outreach.shifts) {
                                                    for (const caregiverId of selectedIdsByShiftForConfirm[
                                                        shift.id
                                                    ] ?? []) {
                                                        caregiverShiftIds[
                                                            caregiverId
                                                        ] = [
                                                            ...(caregiverShiftIds[
                                                                caregiverId
                                                            ] ?? []),
                                                            shift.id,
                                                        ];
                                                    }
                                                }

                                                return handleConfirmSuggestionsAsOutreach(
                                                    caregiverShiftIds,
                                                    caregiverNamesById,
                                                );
                                            };
                                        const shouldShowAgentAnalyzingCard =
                                            isAgentBackedOutreach &&
                                            isAgentAnalyzing &&
                                            !isManualFallbackOpen &&
                                            !hasUnitRecommendations;
                                        const enableManualFallbackForUnit =
                                            () => {
                                                setManualFallbackShiftIds(
                                                    (prev) => {
                                                        const next = new Set(
                                                            prev,
                                                        );
                                                        next.add(unit.id);
                                                        return next;
                                                    },
                                                );
                                            };

                                        return (
                                            <div
                                                key={unit.id}
                                                className="flex min-h-0 flex-1 flex-col overflow-hidden"
                                            >
                                                {shouldShowAgentAnalyzingCard && (
                                                    <OutreachRecommendationEmptyState
                                                        title="Phoebe is analyzing caregivers…"
                                                        description="Suggestions will appear below as they are added."
                                                        actionLabel="Select Caregivers Manually"
                                                        onAction={
                                                            enableManualFallbackForUnit
                                                        }
                                                        headerAction={
                                                            mobileRecommendationsHeaderAction
                                                        }
                                                    />
                                                )}
                                                {hasUnitRecommendations && (
                                                    <InhouseRecommendationsTable
                                                        outreachAgentGeneration={
                                                            outreach.agent_generation
                                                        }
                                                        shiftId={
                                                            shiftGroupId
                                                                ? undefined
                                                                : singleShift?.id
                                                        }
                                                        shiftGroupId={
                                                            shiftGroupId
                                                        }
                                                        groupShiftIds={
                                                            unitShiftIds
                                                        }
                                                        groupMode={
                                                            unitGroupMode
                                                        }
                                                        onStartOutreach={
                                                            handleConfirmSuggestionsForUnit
                                                        }
                                                        onSelectionChange={
                                                            handleTableSelectionChangeForAgentUnit
                                                        }
                                                        showStartButton={false}
                                                        buttonText={
                                                            outreachButtonText
                                                        }
                                                        preSelectedCaregiverIds={
                                                            unitCaregiverIds
                                                        }
                                                        controlledSelectedCaregiverIds={
                                                            inhouseControlledSelectedIds
                                                        }
                                                        defaultShowSelectedOnly={
                                                            true
                                                        }
                                                        agentReasoningMap={
                                                            unitReasoningMap
                                                        }
                                                        isStartOutreachDisabled={
                                                            isStartOutreachDisabled ||
                                                            !allShiftsHaveSelections
                                                        }
                                                        startOutreachDisabledTooltip={
                                                            startOutreachDisabledTooltip
                                                        }
                                                        shiftReadinessLabel={
                                                            shiftReadinessLabel
                                                        }
                                                        outreachId={outreach.id}
                                                        persistedFeedbackByCaregiver={
                                                            outreach.suggested_caregivers_feedback ??
                                                            undefined
                                                        }
                                                        isAgentRunning={
                                                            isAgentAddingPeople
                                                        }
                                                        defaultTagFilters={
                                                            defaultTagFilters
                                                        }
                                                        mobileHeaderAction={
                                                            mobileRecommendationsHeaderAction
                                                        }
                                                    />
                                                )}
                                                {didAgentFail &&
                                                    !isManualFallbackOpen && (
                                                        <OutreachRecommendationEmptyState
                                                            title="Phoebe could not generate suggestions."
                                                            actionLabel="Select Caregivers Manually"
                                                            onAction={
                                                                enableManualFallbackForUnit
                                                            }
                                                            headerAction={
                                                                mobileRecommendationsHeaderAction
                                                            }
                                                        />
                                                    )}
                                                {!isAgentAnalyzing &&
                                                    !didAgentFail &&
                                                    !isManualFallbackOpen &&
                                                    !hasUnitRecommendations &&
                                                    hasLoadedOutreachDismissedCaregivers && (
                                                        <OutreachRecommendationEmptyState
                                                            title="No suggestions available"
                                                            description="Phoebe did not find eligible caregivers for this shift. You can continue manually."
                                                            actionLabel="Select Caregivers Manually"
                                                            onAction={
                                                                enableManualFallbackForUnit
                                                            }
                                                            headerAction={
                                                                mobileRecommendationsHeaderAction
                                                            }
                                                        />
                                                    )}
                                                {isManualFallbackOpen &&
                                                    (didAgentFail ||
                                                        !hasUnitRecommendations) && (
                                                        <div
                                                            data-testid="manual-fallback-table-scroll"
                                                            className={
                                                                MOBILE_MANUAL_FALLBACK_TABLE_SCROLL_CLASSNAME
                                                            }
                                                        >
                                                            <InhouseRecommendationsTable
                                                                outreachAgentGeneration={
                                                                    outreach.agent_generation
                                                                }
                                                                shiftId={
                                                                    shiftGroupId
                                                                        ? undefined
                                                                        : singleShift?.id
                                                                }
                                                                shiftGroupId={
                                                                    shiftGroupId
                                                                }
                                                                groupShiftIds={
                                                                    unitShiftIds
                                                                }
                                                                groupMode={
                                                                    unitGroupMode
                                                                }
                                                                title="Eligible Caregivers"
                                                                onStartOutreach={
                                                                    handleStartOutreachForUnit
                                                                }
                                                                onSelectionChange={
                                                                    handleTableSelectionChangeForUnit
                                                                }
                                                                showStartButton={
                                                                    false
                                                                }
                                                                buttonText={
                                                                    outreachButtonText
                                                                }
                                                                preSelectedCaregiverIds={
                                                                    inhousePreSelectedIds
                                                                }
                                                                controlledSelectedCaregiverIds={
                                                                    inhouseControlledSelectedIds
                                                                }
                                                                isStartOutreachDisabled={
                                                                    isStartOutreachDisabled ||
                                                                    !allShiftsHaveSelections
                                                                }
                                                                startOutreachDisabledTooltip={
                                                                    startOutreachDisabledTooltip
                                                                }
                                                                shiftReadinessLabel={
                                                                    shiftReadinessLabel
                                                                }
                                                                outreachId={
                                                                    outreach.id
                                                                }
                                                                persistedFeedbackByCaregiver={
                                                                    outreach.suggested_caregivers_feedback ??
                                                                    undefined
                                                                }
                                                                isAgentRunning={
                                                                    isAgentAddingPeople
                                                                }
                                                                defaultTagFilters={
                                                                    defaultTagFilters
                                                                }
                                                                mobileHeaderAction={
                                                                    mobileRecommendationsHeaderAction
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                            </div>
                                        );
                                    };

                                    if (displayUnits.length === 0) {
                                        return null;
                                    }

                                    if (!isMultiShiftOutreach) {
                                        const [activeUnit] = displayUnits;
                                        if (!activeUnit) {
                                            return null;
                                        }
                                        return (
                                            <React.Fragment>
                                                <div className="flex justify-end">
                                                    {renderSelectedCaregiverStartButton()}
                                                </div>
                                                {renderRecsForUnit(activeUnit)}
                                            </React.Fragment>
                                        );
                                    }

                                    const activeDisplayUnit =
                                        displayUnits.find(
                                            (unit) =>
                                                unit.id ===
                                                activeRecommendationUnitId,
                                        ) ?? displayUnits[0];

                                    if (!activeDisplayUnit) {
                                        return null;
                                    }

                                    const activeDisplayUnitId =
                                        activeDisplayUnit.id;

                                    const displayUnitAccordionItems =
                                        displayUnits.flatMap((unit) => {
                                            const [firstShiftForLabel] =
                                                unit.shifts;
                                            if (!firstShiftForLabel) {
                                                return [];
                                            }
                                            const groupNumber =
                                                unit.kind === 'group'
                                                    ? (shiftGroupByShiftId.get(
                                                          firstShiftForLabel.id,
                                                      ) ?? 0) + 1
                                                    : null;
                                            const labelPrefix =
                                                formatDateAndTimeWithWeekday(
                                                    firstShiftForLabel.shift_start_time,
                                                    timezone,
                                                );
                                            const fullLabel =
                                                groupNumber !== null
                                                    ? `Group ${groupNumber}: ${labelPrefix} - ${formatName(firstShiftForLabel.patient_name)}`
                                                    : `${labelPrefix} - ${formatName(firstShiftForLabel.patient_name)}`;
                                            const headerGroups = Array.from(
                                                unit.shifts.reduce(
                                                    (groups, shift) => {
                                                        const timeRows =
                                                            groups.get(
                                                                shift.patient_name,
                                                            ) ?? [];
                                                        timeRows.push({
                                                            shiftId: shift.id,
                                                            label: `${formatDateAndTimeWithWeekday(
                                                                shift.shift_start_time,
                                                                timezone,
                                                            )} - ${formatTime(shift.shift_end_time, timezone)}`,
                                                            selectedCount:
                                                                new Set(
                                                                    selectedCaregiverIdsByShift[
                                                                        shift.id
                                                                    ] ?? [],
                                                                ).size,
                                                        });
                                                        groups.set(
                                                            shift.patient_name,
                                                            timeRows,
                                                        );
                                                        return groups;
                                                    },
                                                    new Map<
                                                        string,
                                                        Array<RecommendationAccordionTimeRow>
                                                    >(),
                                                ),
                                            ).map(([clientName, timeRows]) => ({
                                                clientName,
                                                timeRows,
                                            }));
                                            return [
                                                {
                                                    fullLabel,
                                                    groupNumber,
                                                    headerGroups,
                                                    unit,
                                                    unitId: unit.id,
                                                },
                                            ];
                                        });

                                    return (
                                        <React.Fragment>
                                            <div className="mt-0 flex shrink-0 flex-col gap-2 text-sm md:mt-4">
                                                <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2">
                                                    <span className="text-muted-foreground whitespace-nowrap">
                                                        {isAgentPickingCaregivers
                                                            ? 'Showing recommended caregivers for'
                                                            : 'Showing caregivers for'}
                                                    </span>
                                                    <label className="text-muted-foreground flex items-center gap-1.5 whitespace-nowrap">
                                                        <Checkbox
                                                            checked={
                                                                useSameCaregiversAcrossShifts
                                                            }
                                                            onCheckedChange={(
                                                                checked,
                                                            ) =>
                                                                handleSameCaregiversAcrossShiftsToggle(
                                                                    checked ===
                                                                        true,
                                                                    activeDisplayUnit,
                                                                )
                                                            }
                                                        />
                                                        Same caregivers for all
                                                        shifts
                                                    </label>
                                                </div>
                                            </div>
                                            <div
                                                role="group"
                                                aria-label="Recommended caregiver groups and shifts"
                                                className="space-y-2 pr-1"
                                            >
                                                {displayUnitAccordionItems.map(
                                                    (item, index) => {
                                                        const isOpen =
                                                            openRecommendationUnitIds.has(
                                                                item.unitId,
                                                            );
                                                        const panelId = `${recommendationAccordionId}-recommendation-unit-${index}`;
                                                        const groupModeBadge =
                                                            item.unit.kind ===
                                                            'group' ? (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="border-agent-action/25 bg-agent-action/5 text-agent-action h-5 shrink-0 px-1.5 text-[11px] leading-none font-medium dark:border-violet-400/30 dark:bg-violet-400/10 dark:text-violet-300"
                                                                >
                                                                    {groupModeLabel(
                                                                        item
                                                                            .unit
                                                                            .groupMode,
                                                                    )}
                                                                </Badge>
                                                            ) : null;
                                                        return (
                                                            <div
                                                                key={
                                                                    item.unitId
                                                                }
                                                                className="border-border/70 bg-background overflow-hidden rounded-md border"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    aria-expanded={
                                                                        isOpen
                                                                    }
                                                                    aria-controls={
                                                                        panelId
                                                                    }
                                                                    title={
                                                                        item.fullLabel
                                                                    }
                                                                    onClick={() =>
                                                                        handleRecommendationUnitToggle(
                                                                            item.unitId,
                                                                        )
                                                                    }
                                                                    className={cn(
                                                                        'hover:bg-muted/60 flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left transition-colors',
                                                                        !uiV3 &&
                                                                            item.unitId ===
                                                                                activeDisplayUnitId
                                                                            ? 'bg-muted/40'
                                                                            : '',
                                                                    )}
                                                                >
                                                                    <span className="min-w-0 space-y-1">
                                                                        {item.headerGroups.map(
                                                                            (
                                                                                {
                                                                                    clientName,
                                                                                    timeRows,
                                                                                },
                                                                                headerGroupIndex,
                                                                            ) => (
                                                                                <span
                                                                                    key={
                                                                                        clientName
                                                                                    }
                                                                                    className="block space-y-0.5"
                                                                                >
                                                                                    <span className="flex min-w-0 items-center gap-2">
                                                                                        <span
                                                                                            className={cn(
                                                                                                'text-foreground truncate leading-snug font-medium',
                                                                                                uiV3
                                                                                                    ? 'text-base'
                                                                                                    : 'text-sm',
                                                                                            )}
                                                                                        >
                                                                                            {
                                                                                                clientName
                                                                                            }
                                                                                        </span>
                                                                                        {headerGroupIndex ===
                                                                                            0 &&
                                                                                        !uiV3
                                                                                            ? groupModeBadge
                                                                                            : null}
                                                                                    </span>
                                                                                    {timeRows.map(
                                                                                        (
                                                                                            timeRow,
                                                                                        ) => (
                                                                                            <span
                                                                                                key={`${clientName}-${timeRow.shiftId}`}
                                                                                                className="text-muted-foreground block text-sm leading-snug"
                                                                                            >
                                                                                                {
                                                                                                    timeRow.label
                                                                                                }
                                                                                                <span className="text-agent-action ml-1 font-medium dark:text-violet-300">
                                                                                                    ·{' '}
                                                                                                    {
                                                                                                        timeRow.selectedCount
                                                                                                    }{' '}
                                                                                                    selected
                                                                                                </span>
                                                                                            </span>
                                                                                        ),
                                                                                    )}
                                                                                </span>
                                                                            ),
                                                                        )}
                                                                    </span>
                                                                    <span
                                                                        className={cn(
                                                                            'flex shrink-0',
                                                                            // v3 mirrors the chat's outreach card:
                                                                            // group label top right, chevron bottom right.
                                                                            uiV3
                                                                                ? 'flex-col items-end gap-1 self-stretch'
                                                                                : 'mt-0.5 items-center',
                                                                        )}
                                                                    >
                                                                        {uiV3 &&
                                                                        item
                                                                            .unit
                                                                            .kind ===
                                                                            'group' ? (
                                                                            <span className="text-muted-foreground text-xs">
                                                                                {`Group ${item.groupNumber} - ${groupModeLabel(item.unit.groupMode)}`}
                                                                            </span>
                                                                        ) : null}
                                                                        <ChevronDown
                                                                            className={cn(
                                                                                'text-muted-foreground size-4 transition-transform',
                                                                                uiV3 &&
                                                                                    'mt-auto',
                                                                                isOpen
                                                                                    ? 'rotate-180'
                                                                                    : '',
                                                                            )}
                                                                            aria-hidden="true"
                                                                        />
                                                                    </span>
                                                                </button>
                                                                <div
                                                                    id={panelId}
                                                                    hidden={
                                                                        !isOpen
                                                                    }
                                                                    className="border-t p-2"
                                                                >
                                                                    <div className="min-h-0">
                                                                        {renderRecsForUnit(
                                                                            item.unit,
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    },
                                                )}
                                            </div>
                                        </React.Fragment>
                                    );
                                })()}
                            </div>
                        )}
                    {showContactAttemptHistory && (
                        <div
                            className="space-y-4"
                            data-testid="outreach-caregivers-section"
                        >
                            <div className="flex items-center justify-end gap-2 md:hidden">
                                {!isLoadingPendingMessagesCount &&
                                    (pendingMessagesCount ?? 0) > 0 && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-2 text-xs"
                                            onClick={() =>
                                                setShowPendingMessagesDialog(
                                                    true,
                                                )
                                            }
                                        >
                                            <MessageSquareText className="mr-1.5 h-3.5 w-3.5" />
                                            Approve ({pendingMessagesCount})
                                        </Button>
                                    )}
                                {mobileRecommendationsHeaderAction}
                            </div>
                            {!isLoadingPendingMessagesCount &&
                                (pendingMessagesCount ?? 0) > 0 && (
                                    <div className="my-6 hidden items-center justify-end md:flex">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                                setShowPendingMessagesDialog(
                                                    true,
                                                )
                                            }
                                        >
                                            <MessageSquareText className="mr-2 h-4 w-4" />
                                            Approve Messages (
                                            {pendingMessagesCount})
                                        </Button>
                                    </div>
                                )}
                            {responseSummary && responseSummary.total > 0 && (
                                <div className="my-2 grid grid-cols-3 gap-1.5 md:my-6 md:gap-3 @3xl:grid-cols-6">
                                    <div
                                        className="bg-card rounded-md border p-1.5 md:rounded-lg md:p-3"
                                        data-testid="response-summary-total"
                                    >
                                        <div className="text-muted-foreground text-[10px] leading-tight font-medium md:text-sm">
                                            Total Contacted
                                        </div>
                                        <div className="mt-0.5 text-lg leading-none font-semibold md:mt-1 md:text-2xl">
                                            {responseSummary.total}
                                        </div>
                                    </div>
                                    <div
                                        className="bg-card rounded-md border p-1.5 md:rounded-lg md:p-3"
                                        data-testid="response-summary-available"
                                    >
                                        <div className="text-muted-foreground text-[10px] leading-tight font-medium md:text-sm">
                                            Available
                                        </div>
                                        <div className="mt-0.5 flex items-center gap-1 md:mt-1 md:gap-2">
                                            <span className="text-lg leading-none font-semibold md:text-2xl">
                                                {
                                                    responseSummary.available_interested
                                                }
                                            </span>
                                            {responseSummary.available_interested >
                                                0 && (
                                                <Badge
                                                    className={cn(
                                                        RESPONSE_COUNT_CHIP_CLASS,
                                                        statusIntentBadgeClassName(
                                                            'positive',
                                                        ),
                                                    )}
                                                >
                                                    ✓
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-card rounded-md border p-1.5 md:rounded-lg md:p-3">
                                        <div className="text-muted-foreground text-[10px] leading-tight font-medium md:text-sm">
                                            Not a fit
                                        </div>
                                        <div className="mt-0.5 flex items-center gap-1 md:mt-1 md:gap-2">
                                            <span className="text-lg leading-none font-semibold md:text-2xl">
                                                {
                                                    responseSummary.available_not_interested
                                                }
                                            </span>
                                            {responseSummary.available_not_interested >
                                                0 && (
                                                <Badge
                                                    className={cn(
                                                        RESPONSE_COUNT_CHIP_CLASS,
                                                        statusIntentBadgeClassName(
                                                            'warning',
                                                        ),
                                                    )}
                                                >
                                                    -
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-card rounded-md border p-1.5 md:rounded-lg md:p-3">
                                        <div className="text-muted-foreground text-[10px] leading-tight font-medium md:text-sm">
                                            Unavailable
                                        </div>
                                        <div className="mt-0.5 flex items-center gap-1 md:mt-1 md:gap-2">
                                            <span className="text-lg leading-none font-semibold md:text-2xl">
                                                {responseSummary.unavailable}
                                            </span>
                                            {responseSummary.unavailable >
                                                0 && (
                                                <Badge
                                                    className={cn(
                                                        RESPONSE_COUNT_CHIP_CLASS,
                                                        statusIntentBadgeClassName(
                                                            'negative',
                                                        ),
                                                    )}
                                                >
                                                    ✗
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className="bg-card rounded-md border p-1.5 md:rounded-lg md:p-3"
                                        data-testid="response-summary-pending"
                                    >
                                        <div className="text-muted-foreground text-[10px] leading-tight font-medium md:text-sm">
                                            Pending
                                        </div>
                                        <div className="mt-0.5 flex items-center gap-1 md:mt-1 md:gap-2">
                                            <span className="text-lg leading-none font-semibold md:text-2xl">
                                                {responseSummary.pending}
                                            </span>
                                            {responseSummary.pending > 0 && (
                                                <Badge
                                                    className={cn(
                                                        RESPONSE_COUNT_CHIP_CLASS,
                                                        statusIntentBadgeClassName(
                                                            'neutral',
                                                        ),
                                                    )}
                                                >
                                                    …
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-card rounded-md border p-1.5 md:rounded-lg md:p-3">
                                        <div className="text-muted-foreground text-[10px] leading-tight font-medium md:text-sm">
                                            No Response
                                        </div>
                                        <div className="mt-0.5 flex items-center gap-1 md:mt-1 md:gap-2">
                                            <span className="text-lg leading-none font-semibold md:text-2xl">
                                                {responseSummary.no_response}
                                            </span>
                                            {responseSummary.no_response >
                                                0 && (
                                                <Badge
                                                    className={cn(
                                                        RESPONSE_COUNT_CHIP_CLASS,
                                                        statusIntentBadgeClassName(
                                                            'neutral',
                                                        ),
                                                    )}
                                                >
                                                    ?
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {contactAttemptsError ? (
                                <div className="text-destructive flex justify-center p-6">
                                    Error loading contact attempts:{' '}
                                    {contactAttemptsError.message}
                                </div>
                            ) : isLoadingContactAttempts ? (
                                <div className="flex justify-center p-6">
                                    Loading contact attempts…
                                </div>
                            ) : contactAttempts?.length === 0 ? (
                                <div className="bg-muted/50 flex justify-center rounded-lg p-6">
                                    {outreach.status === 'in_progress' ? (
                                        <div className="flex items-center gap-2">
                                            <Spinner className="text-muted-foreground" />
                                            <p className="text-muted-foreground text-sm">
                                                Starting outreach…
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="text-muted-foreground text-sm">
                                            {isActiveOutreach(outreach.status)
                                                ? 'Please add caregivers to contact'
                                                : 'No contact attempts have been made yet.'}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="[&>div]:rounded-none [&>div]:border-x-0">
                                    <DataTable
                                        columns={contactAttemptsColumns}
                                        data={contactAttemptGroups}
                                        mobileRowsClassName="max-h-[calc(100dvh-14rem)] overflow-y-auto overscroll-contain scroll-pb-24 pb-24 [-webkit-overflow-scrolling:touch]"
                                        renderMobileCard={
                                            renderContactAttemptMobileCard
                                        }
                                        stickyRightColumnId="actions"
                                        stickyRightFade={true}
                                        onSortingChange={(sorting) => {
                                            if (
                                                sorting[0]?.id !==
                                                'matchSignals'
                                            ) {
                                                setMatchSignalSortKey(null);
                                            }
                                        }}
                                        onRowClick={(group) => {
                                            const attempts = group.attempts;

                                            if (attempts.length === 0) {
                                                return;
                                            }

                                            const [firstAttempt] = attempts;

                                            if (!firstAttempt) {
                                                return;
                                            }

                                            const matchingSelectedAttempt =
                                                selectedContactAttemptId
                                                    ? attempts.find(
                                                          (attempt) =>
                                                              attempt.id ===
                                                              selectedContactAttemptId,
                                                      )
                                                    : null;

                                            const attemptToOpen =
                                                matchingSelectedAttempt ??
                                                attempts.reduce<ContactAttempt>(
                                                    (latest, attempt) =>
                                                        new Date(
                                                            attempt.created_at,
                                                        ).getTime() >
                                                        new Date(
                                                            latest.created_at,
                                                        ).getTime()
                                                            ? attempt
                                                            : latest,
                                                    firstAttempt,
                                                );

                                            openContactAttemptModal(
                                                attemptToOpen.id,
                                            );
                                        }}
                                        initialSorting={[
                                            {
                                                id: 'overallResponse',
                                                desc: false,
                                            },
                                            {
                                                id: 'caregiver.name',
                                                desc: false,
                                            },
                                        ]}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {showSelectionFloatingBar ? (
                        <OutreachSelectionFloatingBar
                            startReport={startReport}
                            selectedCaregiverCount={selectedCaregiverCount}
                            selectedCaregiverShiftCount={
                                selectedCaregiverShiftCount
                            }
                            isOverCaregiverLimit={isOverCaregiverLimit}
                            maxOutreachCaregivers={maxOutreachCaregivers}
                            shiftReadinessLabel={shiftReadinessLabel}
                            isAgentAddingPeople={isAgentAddingPeople}
                            isStartingSelectedCaregivers={
                                isStartingSelectedCaregivers
                            }
                            clearSelectedCaregivers={clearSelectedCaregivers}
                            renderStartButton={
                                renderSelectedCaregiverStartButton
                            }
                        />
                    ) : null}
                </div>
            </div>
            {chatTypingUsers && chatTypingUsers.length > 0 ? (
                <OutreachChatTypingPill
                    typingUsers={chatTypingUsers}
                    liftedAboveSelectionBar={showSelectionFloatingBar}
                />
            ) : null}
            {selectedCaregiverGroup && (
                <CaregiverOutreachDetailModal
                    isOpen={true}
                    onClose={closeContactAttemptModal}
                    caregiver={selectedCaregiverGroup.caregiver}
                    attempts={selectedCaregiverGroup.attempts}
                    timelineItems={selectedCaregiverGroup.timelineItems}
                    isAdmin={user?.admin}
                    outreachId={outreach.id}
                    recordingUrls={recordingUrls}
                    shifts={outreach.shifts}
                    wellskyOverrideReview={
                        selectedCaregiverGroup.wellskyOverrideReview ?? null
                    }
                    onReviewOverride={
                        selectedCaregiverGroup.wellskyOverrideReview
                            ? handleReviewOverrideFromDetailModal
                            : undefined
                    }
                    skipBusyCaregivers={
                        organization?.callout_settings?.skip_busy_caregivers ??
                        false
                    }
                />
            )}
            {organizationId ? (
                <CaregiverDetailModal
                    isOpen={selectedCaregiverDetailId !== null}
                    onClose={() => setSelectedCaregiverDetailId(null)}
                    organizationId={organizationId}
                    caregiverId={selectedCaregiverDetailId ?? undefined}
                />
            ) : null}
            <ClientNotificationDetailModal
                isOpen={showClientNotificationModal}
                onClose={() => setShowClientNotificationModal(false)}
                attempts={clientContactAttempts ?? []}
                timezone={timezone}
                recordingUrls={recordingUrls}
                onRetryAttempt={handleRetryClientContactAttempt}
                onMarkNotifiedAttempt={handleMarkClientContactAttemptNotified}
                retryingContactAttemptId={retryingClientContactAttemptId}
                markingContactAttemptId={markingClientContactAttemptId}
                onClientClick={(clientId) => setClientDetailClientId(clientId)}
            />
            {organizationId && (
                <ClientDetailModal
                    organizationId={organizationId}
                    client={
                        clientDetailClientId
                            ? (clientById.get(clientDetailClientId) ?? null)
                            : null
                    }
                    open={clientDetailClientId !== null}
                    onOpenChange={(open) => {
                        if (!open) {
                            setClientDetailClientId(null);
                        }
                    }}
                    onClientUpdated={() => void mutateClients()}
                />
            )}
            <AddCaregiversModal
                isOpen={showAddCaregiversModal}
                onClose={() => setShowAddCaregiversModal(false)}
                outreach={outreach}
                contactedCaregiverIds={contactedCaregiverIds}
                onSuccess={() => {
                    mutateContactAttempts();
                    mutateOutreach();
                }}
            />
            {organizationId && (
                <CancelShiftDialog
                    open={!!shiftToCancel}
                    onOpenChange={(open) => !open && setShiftToCancel(null)}
                    shiftIds={shiftToCancel?.ids ?? []}
                    targetLabel={shiftToCancel?.targetLabel ?? ''}
                    organizationId={organizationId}
                    outreachId={outreach.id}
                    onSuccess={() => {
                        setSelectedShiftIdsToArchive(new Set());
                        mutateOutreach();
                        setShiftToCancel(null);
                    }}
                    onPartialSuccess={(failedShiftIds) => {
                        setSelectedShiftIdsToArchive(new Set(failedShiftIds));
                        mutateOutreach();
                        setShiftToCancel(null);
                    }}
                />
            )}
            <PendingMessagesDialog
                organizationId={organizationId!}
                open={showPendingMessagesDialog}
                onOpenChange={setShowPendingMessagesDialog}
                domain="callout"
                domainId={outreach.id}
                showCareCoordinatorFilter={false}
            />
            {assignAndFillModalState.caregiver &&
                outreach.shifts[0] &&
                organization && (
                    <AssignAndFillConfirmationModal
                        isOpen={assignAndFillModalState.isOpen}
                        onClose={() => {
                            setAssignAndFillModalState((prev) => ({
                                ...prev,
                                isOpen: false,
                            }));
                        }}
                        caregiver={assignAndFillModalState.caregiver}
                        isLoading={false}
                        outreachId={outreach.id}
                        onSubmit={() => {
                            setAssignAndFillModalState((prev) => ({
                                ...prev,
                                isOpen: false,
                            }));
                            mutateOutreach();
                            mutateContactAttempts();
                        }}
                        outreachType={outreach.outreach_type}
                        shift={outreach.shifts[0]}
                        allShifts={outreach.shifts}
                        shiftCarePlans={shiftCarePlans}
                        interestedShiftIds={
                            assignAndFillModalState.interestedShiftIds
                        }
                        analyticsEntryPoint={assignAndFillModalState.entryPoint}
                        wellskyOverrideReview={
                            assignAndFillModalState.wellskyOverrideReview
                        }
                        hhaexchangeOvertimeReview={
                            assignAndFillModalState.hhaexchangeOvertimeReview
                        }
                        scheduleOverlapMinutes={
                            assignAndFillModalState.scheduleOverlapMinutes
                        }
                        scheduleOverlapRanges={
                            assignAndFillModalState.scheduleOverlapRanges
                        }
                        scheduleConflictingShifts={
                            assignAndFillModalState.scheduleConflictingShifts
                        }
                        filledShiftIds={filledShiftIds}
                        cancelledShiftIds={cancelledShiftIds}
                        timezone={timezone}
                        formatCaregiverName={formatCaregiverDisplayName}
                        isAdmin={user?.admin}
                        defaultNotifyClient={
                            assignAndFillModalState.interestedShiftIds &&
                            assignAndFillModalState.interestedShiftIds.length >
                                0
                                ? assignAndFillModalState.interestedShiftIds.every(
                                      (shiftId) =>
                                          isNotifyClientEnabledForShift(
                                              shiftId,
                                          ),
                                  )
                                : outreach.shifts[0]
                                  ? isNotifyClientEnabledForShift(
                                        outreach.shifts[0].id,
                                    )
                                  : false
                        }
                    />
                )}
            {outreach.shifts[0] && (
                <EditShiftModal
                    isOpen={showEditShiftModal}
                    onClose={() => {
                        setShowEditShiftModal(false);
                    }}
                    shift={outreach.shifts[0]}
                    organizationId={organizationId ?? ''}
                    onSuccess={() => {
                        mutateOutreach();
                        setShowEditShiftModal(false);
                    }}
                    outreachId={outreach.id}
                />
            )}
            <ManualContactConflictDialog
                conflict={manualContactConflictOverride}
                shifts={outreach.shifts}
                timezone={timezone}
                isCalling={isCallingCaregiver !== null}
                onCancel={() => setManualContactConflictOverride(null)}
                onConfirm={(caregiverId, shiftIds) => {
                    void handleCallAgain(caregiverId, {
                        [caregiverId]: [...shiftIds],
                    });
                }}
            />
            <OutreachScheduleConflictDialog
                conflict={scheduleConflict}
                shifts={outreach.shifts}
                timezone={timezone}
                isSubmitting={isConfirmingScheduleConflict}
                onCancel={() => {
                    setScheduleConflict(null);
                    setConflictingStart(null);
                }}
                onConfirm={(acknowledgedConflicts) => {
                    if (!conflictingStart) {
                        return;
                    }
                    setIsConfirmingScheduleConflict(true);
                    const submit =
                        conflictingStart.kind === 'start'
                            ? submitStartOutreach
                            : submitConfirmSuggestionsAsOutreach;
                    void submit(
                        conflictingStart.caregiverShiftIds,
                        conflictingStart.caregiverNamesById,
                        acknowledgedConflicts,
                    ).finally(() => {
                        setIsConfirmingScheduleConflict(false);
                    });
                }}
            />
            {skipListDialog}
            <Dialog
                open={mobileShiftDetails !== undefined}
                onOpenChange={(open) => {
                    if (!open) {
                        setMobileShiftDetailsId(null);
                    }
                }}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Shift details</DialogTitle>
                    </DialogHeader>
                    {mobileShiftDetails ? (
                        <div className="space-y-3 text-sm">
                            <div className="space-y-1">
                                <div className="font-semibold">
                                    {formatName(
                                        mobileShiftDetails.patient_name,
                                    )}
                                </div>
                                <div className="text-muted-foreground text-xs">
                                    {formatDateAndTimeWithWeekday(
                                        mobileShiftDetails.shift_start_time,
                                        timezone,
                                    )}{' '}
                                    -{' '}
                                    {formatTime(
                                        mobileShiftDetails.shift_end_time,
                                        timezone,
                                    )}
                                </div>
                            </div>
                            <Badge
                                variant="outline"
                                className={cn(
                                    'h-5 rounded-sm px-1.5 py-0 text-[11px]',
                                    mobileShiftDetailsFilledInfo !== undefined
                                        ? statusIntentBadgeClassName('positive')
                                        : mobileShiftDetailsIsCancelled
                                          ? statusIntentBadgeClassName(
                                                'neutral',
                                            )
                                          : mobileShiftDetailsIsUnfilled
                                            ? statusIntentBadgeClassName(
                                                  'negative',
                                              )
                                            : statusIntentBadgeClassName(
                                                  'neutral',
                                              ),
                                )}
                            >
                                {mobileShiftDetailsStatusLabel}
                            </Badge>
                            <div className="grid gap-2 text-xs">
                                <div>
                                    <span className="text-muted-foreground">
                                        Location:{' '}
                                    </span>
                                    {mobileShiftDetails.patient_city || '—'}
                                </div>
                                <div>
                                    <span className="text-muted-foreground">
                                        Original caregiver:{' '}
                                    </span>
                                    {formatName(
                                        mobileShiftDetailsOriginalCaregiver?.name,
                                    ) || 'No original caregiver'}
                                </div>
                            </div>
                            {(mobileShiftDetailsCanEdit ||
                                mobileShiftDetailsCanCancel) && (
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {mobileShiftDetailsCanEdit && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="w-full"
                                            onClick={() => {
                                                setMobileShiftDetailsId(null);
                                                setShowEditShiftModal(true);
                                            }}
                                        >
                                            <Pencil className="mr-2 h-4 w-4" />
                                            Edit shift
                                        </Button>
                                    )}
                                    {mobileShiftDetailsCanCancel && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="text-destructive hover:bg-destructive/10 hover:text-destructive w-full border-red-200"
                                            onClick={() => {
                                                setShiftToCancel({
                                                    ids: [
                                                        mobileShiftDetails.id,
                                                    ],
                                                    targetLabel:
                                                        mobileShiftDetails.patient_name,
                                                });
                                                setMobileShiftDetailsId(null);
                                            }}
                                        >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Archive shift
                                        </Button>
                                    )}
                                </div>
                            )}
                            {showNotifyClientColumn && (
                                <div className="rounded-md border p-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 space-y-1">
                                            <div className="text-xs font-medium">
                                                Notify client
                                            </div>
                                            {mobileShiftDetailsIsCancelled ? (
                                                <div className="text-muted-foreground text-xs">
                                                    Archived shifts do not send
                                                    notifications.
                                                </div>
                                            ) : mobileShiftDetailsClientNotificationTarget ? (
                                                <React.Fragment>
                                                    {mobileShiftDetailsClient?.default_notify_on_fill ===
                                                        false &&
                                                        !isNotifyClientEnabledForShift(
                                                            mobileShiftDetails.id,
                                                        ) && (
                                                            <div className="text-muted-foreground text-xs">
                                                                Client
                                                                notifications
                                                                disabled
                                                            </div>
                                                        )}
                                                    {mobileShiftDetailsClientNotificationTarget.secondaryContactName && (
                                                        <div className="text-muted-foreground text-xs">
                                                            Secondary:{' '}
                                                            {
                                                                mobileShiftDetailsClientNotificationTarget.secondaryContactName
                                                            }
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            ) : (
                                                <div className="text-muted-foreground text-xs">
                                                    No client phone number
                                                </div>
                                            )}
                                        </div>
                                        {!mobileShiftDetailsIsCancelled &&
                                            mobileShiftDetailsClientNotificationTarget && (
                                                <Switch
                                                    aria-label="Notify client when this shift is filled"
                                                    checked={isNotifyClientEnabledForShift(
                                                        mobileShiftDetails.id,
                                                    )}
                                                    disabled={
                                                        mobileShiftDetailsFilledInfo !==
                                                        undefined
                                                    }
                                                    onCheckedChange={(
                                                        checked,
                                                    ) =>
                                                        handleToggleNotifyClient(
                                                            mobileShiftDetails.id,
                                                            checked,
                                                        )
                                                    }
                                                />
                                            )}
                                    </div>
                                </div>
                            )}
                            {showClientNotificationStatusColumn &&
                                mobileShiftDetails.client_id && (
                                    <div className="rounded-md border p-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="text-xs font-medium">
                                                Client notification
                                            </div>
                                            {mobileShiftDetailsClientAttempt ? (
                                                <button
                                                    type="button"
                                                    className="cursor-pointer"
                                                    aria-label="View client notification details"
                                                    onClick={() => {
                                                        setMobileShiftDetailsId(
                                                            null,
                                                        );
                                                        setShowClientNotificationModal(
                                                            true,
                                                        );
                                                    }}
                                                >
                                                    <ContactAttemptStatusBadge
                                                        status={
                                                            mobileShiftDetailsClientAttempt.outreach_status
                                                        }
                                                        deliveryMode={
                                                            mobileShiftDetailsClientAttempt.delivery_mode
                                                        }
                                                        recipientType="client"
                                                        communicationDetails={
                                                            mobileShiftDetailsClientAttempt.communication_details
                                                        }
                                                    />
                                                </button>
                                            ) : mobileShiftDetailsClientNotificationWillQueue ? (
                                                <ContactAttemptStatusBadge status="queued" />
                                            ) : (
                                                <span className="text-muted-foreground text-xs">
                                                    —
                                                </span>
                                            )}
                                        </div>
                                        {mobileShiftDetailsClientNotificationCanRetry && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="mt-2 h-7 px-2 text-xs"
                                                disabled={
                                                    mobileShiftDetailsClientNotificationIsRetrying
                                                }
                                                onClick={() =>
                                                    handleRetryClientContactAttempt(
                                                        mobileShiftDetailsClientAttempt.id,
                                                    )
                                                }
                                            >
                                                {mobileShiftDetailsClientNotificationIsRetrying ? (
                                                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                                                ) : (
                                                    <Phone className="mr-1.5 h-3 w-3" />
                                                )}
                                                <span>Retry</span>
                                            </Button>
                                        )}
                                    </div>
                                )}
                            <Button
                                variant="outline"
                                size="sm"
                                asChild={true}
                                className="w-full"
                            >
                                <Link
                                    to="/shifts"
                                    search={{
                                        date: getDateStringInTimezone(
                                            new Date(
                                                mobileShiftDetails.shift_start_time,
                                            ),
                                            timezone,
                                        ),
                                        shiftId: mobileShiftDetails.id,
                                    }}
                                >
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    Open shift
                                </Link>
                            </Button>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
            {isMobile ? null : (
                <AddShiftsDialog
                    open={showAddShiftsDialog}
                    onOpenChange={setShowAddShiftsDialog}
                    outreach={outreach}
                    organizationId={organizationId ?? ''}
                    onSuccess={() => {
                        mutateOutreach();
                    }}
                />
            )}
            <OutreachStepsModal
                isOpen={showStepsPreviewModal}
                onClose={() => setShowStepsPreviewModal(false)}
                outreach={outreach}
                onSuccess={() => {
                    mutateOutreach();
                }}
            />
            <Dialog
                open={showSubscribersModal}
                onOpenChange={setShowSubscribersModal}
            >
                <DialogContent className="overflow-hidden sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Subscribers</DialogTitle>
                        <DialogDescription>
                            Choose who should receive updates for this outreach.{' '}
                            <a className="underline" href="/settings?tab=user">
                                Manage your subscription preferences here
                            </a>
                            .
                        </DialogDescription>
                    </DialogHeader>
                    {organizationId ? (
                        <OutreachSubscribersPanel
                            displayMode="dialog"
                            organizationId={organizationId}
                            outreachId={outreach.id}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>
            <CreateOutreachModal
                isOpen={showEditOutreachModal}
                onClose={() => setShowEditOutreachModal(false)}
                outreachToEdit={outreach}
                onOutreachSaved={() => {
                    mutateOutreach();
                }}
            />
            <GroupSelectionWarningDialog {...warningDialogProps} />
            <AutoAssignGroupChangeDialog
                pending={autoAssignEdits.pendingGroupChange}
                currentShiftIds={autoAssignEdits.draftShiftIds}
                shifts={outreach.shifts}
                timezone={timezone}
                onConfirm={autoAssignEdits.confirmPendingGroupChange}
                onDismiss={autoAssignEdits.dismissPendingGroupChange}
            />
            <Dialog
                open={showInstructionsModal}
                onOpenChange={handleCloseInstructionsModal}
            >
                <DialogContent
                    className="max-w-2xl"
                    onOpenAutoFocus={(event) => {
                        if (!isAgentInstructionsLoading) {
                            return;
                        }

                        event.preventDefault();
                        instructionsLoadingStatusRef.current?.focus({
                            preventScroll: true,
                        });
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>Agent Instructions</DialogTitle>
                    </DialogHeader>
                    {isAgentInstructionsLoading ? (
                        <div
                            ref={instructionsLoadingStatusRef}
                            tabIndex={-1}
                            className="text-muted-foreground flex items-center gap-2 text-sm outline-none"
                        >
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Generating agent instructions…
                        </div>
                    ) : isEditingInstructions ? (
                        <Textarea
                            value={editedInstructions}
                            onChange={(e) =>
                                setEditedInstructions(e.target.value)
                            }
                            className="h-[600px] text-sm"
                            placeholder="Enter agent instructions..."
                        />
                    ) : (
                        <div className="text-muted-foreground max-h-[60vh] overflow-y-auto text-sm whitespace-pre-wrap">
                            {instructions || 'No instructions configured.'}
                        </div>
                    )}
                    <DialogFooter>
                        {isAgentInstructionsLoading ? null : isEditingInstructions ? (
                            <React.Fragment>
                                <Button
                                    variant="outline"
                                    onClick={handleCancelEditInstructions}
                                    disabled={isSavingInstructions}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSaveInstructions}
                                    disabled={isSavingInstructions}
                                >
                                    {isSavingInstructions ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : null}
                                    <span>Save</span>
                                </Button>
                            </React.Fragment>
                        ) : (
                            <Button
                                variant="outline"
                                onClick={handleStartEditInstructions}
                            >
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
